/**
 * Site Layout editor (`/siteLayout`).
 *
 * Admin tool that turns a top-down bin-layout image (e.g. a whiteboard photo of a customer site)
 * into a CustomerSiteLayout draft and commits it through the saveCustomerSiteLayout mutation.
 *
 * Phase 1 scope (round silos):
 *   - Upload an image, click bin centers, map each click to a real Bin (id + name + dimensions).
 *   - Graph grid = 10 ft/square; default ft/px maps the default bin circle to Site 3D catalog size (~30 ft).
 *   - Auto-cluster clicks into CustomerSiteLayoutGroups by proximity (DBSCAN-like, no group sides).
 *   - Preview 3D: in-editor modal from the current draft (no layout-table write).
 *   - Preview 3D: previewSite3d.js → layout-table proxy → Site3d (layoutPreviewMode).
 *   - Load: layout tables / Site 3D grid → 2D editor (manual only; not on site open).
 *   - Open site: EditorDraftJson (DB), else browser session; never auto-import layout tables.
 *   - Save: EditorDraftJson only. Save layout tables: groups/bins/sides (+ editor snapshot).
 *
 * Flat areas: walls/arcs between bins; closed regions auto-detect as assignable enclosed areas.
 * Emit CustomerSiteLayoutSide rows via `siteLayoutStice.js` + `siteLayoutEnclosure.js`.
 *
 * Axes: editor image (x, y) with y down → draft feet (x, z) with z = image y.
 * Preview Site / Preview 3D use editor axes (x, y↑, +z) via previewSite3d.js + layoutPreviewView. Live Site 3D uses `[x, y, -z]`.
 */

import React, {
    startTransition,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import GridOnIcon from "@mui/icons-material/GridOn";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CropFreeIcon from "@mui/icons-material/CropFree";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import ViewStreamIcon from "@mui/icons-material/ViewStream";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { MuiMaterials } from "../@index.js";
import * as global from "../data/global";
import { createApiEndpoint } from "../index.js";
import { Spinner, SiteWindowHeaderLeading, SiteWindowToolNav, ChromeHeaderBar, ChromeActionBar, useNarrowChrome, SITE_WINDOW_IDS, ConfirmDialog, confirmDialogButtonSx, withScrollHintMenuProps, WindowChrome, SiteSelectPhase } from "./windowChrome/index.js";
import {
    augmentDraftWithRoundBinPerimeterSides,
    buildSticeBinDraft,
    arcIsInnerFromBulge,
    createSegmentBetweenSnaps,
    createSegmentBetweenLayoutPoints,
    layoutPointsAllowWall,
    closedRectIdForFace,
    createFlatRectangleSegments,
    flatRectCornersFromCenter,
    drawSticeSegments,
    findFlatRectCornerAtImage,
    findFlatRectCornerHit,
    findFlatRectMoveCenterHit,
    flatRectMoveHandleImagePoint,
    findFlatRectAtPoint,
    FLAT_RECT_MOVE_HANDLE_HIT_CANVAS,
    FLAT_RECT_CORNER_HIT_TOL_IMAGE,
    FLAT_RECT_CORNER_HIT_TOL_CANVAS,
    getFlatRectBounds,
    layoutPlacementBlockedForFlatRect,
    layoutPlacementBlockedForRoundBin,
    getFlatRectOrientedGeometry,
    orientedHalfAxesFromBinFt,
    pointInFlatRectBounds,
    replaceFlatRectSegments,
    replaceFlatRectSegmentsOriented,
    resizeFlatBoundsByCorner,
    findSegmentAtCanvas,
    findSegmentSnapAtCanvas,
    getMarkerRadiusImagePx,
    DEFAULT_MARKER_RADIUS_PX,
    defaultLayoutFeetPerPixel,
    PIXEL_TO_FEET_DEFAULT,
    isFloatingSegment,
    resolveSegmentGeometry,
    segmentIsFullySnapped,
    snapBinCenterForCanvas,
    snapImageToLayoutPoint,
    snapsAllowWall,
} from "./siteLayoutStice.js";
import {
    buildOrderedLayoutAreas,
    computeEnclosedFaces,
    computeLayoutSiteCenterPx,
    enclosedFaceSummaryForState,
    LAYOUT_CANVAS_TAG_FACE_LIMIT,
    faceEligibleForLayoutDraft,
    faceShouldPersistAsLayoutSticeBin,
    faceHasSynthOutlineForDraft,
    drawAreaNameTag,
    drawEnclosedFaces,
    findFaceAtPoint,
    findFaceTagAtCanvasPoint,
    flattenLayoutSegments,
    getFaceDisplayLabel,
    faceTopologyKey,
    unassignedAreaLetter,
} from "./siteLayoutEnclosure.js";
import SiteLayoutPreview3d from "./SiteLayoutPreview3d.js";
import {
    finalizeEditorLayoutForSite3dView,
    snapshotSiteLayout,
} from "./layoutDraftToSite.js";
import {
    layoutTablesToEditorGeometry,
    faceAssignmentsFromLayoutTables,
    mergeSavedEditorSegmentsIntoGeometry,
    fitBlankLayoutCanvasSize,
} from "./layoutTablesToEditor.js";
import { buildEditorGeometryFromSite3d } from "./siteLayoutFromSite3d.js";
import {
    applyLayoutTableProxyToSite,
    buildLayoutTableProxyFromEditorDraft,
    buildSavePayloadFromLayoutProxy,
    clearSite3dSavedViewsForSite,
    extractLayoutProxyFromEditorDraft,
    mergeLayoutProxyIntoEditorPayload,
    parseSiteLayoutEditorDraftFromApi,
    prepareDraftForLayoutTableSave,
    resolveLayoutBinIdForSave,
    saveLayoutDraftForSite3d,
    saveLayoutProxyForSite3d,
} from "./layoutTableProxy.js";

/** React DevTools may clone performance.measure detail; huge face polygons can OOM. */
const installSafePerformanceMeasure = () => {
    if (typeof performance === "undefined" || performance.__gwSafeMeasure) return;
    const native = performance.measure?.bind(performance);
    if (!native) return;
    performance.measure = function gwSafeMeasure(name, startOrOpts, endMark) {
        try {
            return native(name, startOrOpts, endMark);
        } catch (err) {
            if (err?.name === "DataCloneError") return undefined;
            throw err;
        }
    };
    performance.__gwSafeMeasure = true;
};
installSafePerformanceMeasure();

/** Graph overlay: each square = 10 ft (spacing in image px follows ft/px). */
const GRAPH_GRID_FEET = 10;
const MIN_VIEW_ZOOM = 0.25;
const MAX_VIEW_ZOOM = 16;
const DEFAULT_VIEW_ZOOM = Math.round(((MIN_VIEW_ZOOM + MAX_VIEW_ZOOM) / 2) * 100) / 100;
/** Padding when fitting the layout canvas inside the viewport (0–1). */
const VIEW_FIT_PADDING = 0.92;

const clampViewZoom = (z) =>
    Math.min(MAX_VIEW_ZOOM, Math.max(MIN_VIEW_ZOOM, Math.round(z * 100) / 100));
/** Canvas bitmap size at a zoom level (width/height attributes — crisp, not CSS-scaled). */
const scaledCanvasPixelSize = (base, zoom) => ({
    width: Math.max(1, Math.floor(base.width * zoom)),
    height: Math.max(1, Math.floor(base.height * zoom)),
});
/** Resize canvas backing store; scale existing pixels to avoid blank frames. */
const resizeLayoutCanvasPreserve = (canvas, nextW, nextH) => {
    if (!canvas) return;
    const prevW = canvas.width;
    const prevH = canvas.height;
    if (prevW === nextW && prevH === nextH) return;
    let snap = null;
    if (prevW > 0 && prevH > 0) {
        snap = document.createElement("canvas");
        snap.width = prevW;
        snap.height = prevH;
        snap.getContext("2d")?.drawImage(canvas, 0, 0);
    }
    canvas.width = nextW;
    canvas.height = nextH;
    if (snap) {
        const ctx = canvas.getContext("2d");
        if (ctx) {
            ctx.drawImage(snap, 0, 0, prevW, prevH, 0, 0, nextW, nextH);
        }
    }
};
const ARROW_NUDGE_PX = 1;
const ARROW_NUDGE_FAST_PX = 8;
/** Per mouse-wheel notch (zoom in / out on layout viewport). */
const VIEW_ZOOM_STEP = 0.32;
/** After last wheel/button zoom preview, resize bitmap once (ms). */
const VIEW_ZOOM_COMMIT_MS = 320;
/** Defer syncing React viewPan after pan so the first wheel does not hitch. */
const VIEW_PAN_SYNC_DELAY_MS = 48;
/** Logical image size when building a layout without an uploaded photo. */
const BLANK_LAYOUT_SIZE = { width: 2400, height: 1600 };
/** Matches assignment sidebar width + gap to canvas row below. */
const LAYOUT_SIDEBAR_WIDTH_PX = 360;
const LAYOUT_CANVAS_SIDEBAR_GAP_PX = 16;

/** Graph-paper overlay: 10 ft cells in image space (scaled to canvas). */
/** Full-viewport horizontal/vertical guides through a point (image → canvas). */
const drawAlignmentCrosshairs = (ctx, imageX, imageY, scaleX, scaleY, canvasW, canvasH) => {
    const cx = imageX * scaleX;
    const cy = imageY * scaleY;
    const ext = Math.max(canvasW, canvasH) * 3;
    ctx.save();
    ctx.strokeStyle = getThemeColor("rgba(255,235,59,0.55)", "rgba(255,235,59,0.4)");
    ctx.lineWidth = 1;
    ctx.setLineDash([8, 6]);
    ctx.beginPath();
    ctx.moveTo(cx - ext, cy);
    ctx.lineTo(cx + ext, cy);
    ctx.moveTo(cx, cy - ext);
    ctx.lineTo(cx, cy + ext);
    ctx.stroke();
    ctx.restore();
};

const drawFeetGraphGrid = (ctx, canvasW, canvasH, scaleX, scaleY, feetPerPixel, theme, imageSize) => {
    const fpp = feetPerPixel > 0 ? feetPerPixel : PIXEL_TO_FEET_DEFAULT;
    const stepImg = GRAPH_GRID_FEET / fpp;
    const stepX = stepImg * scaleX;
    const stepY = stepImg * scaleY;
    if (stepX < 3 || stepY < 3) return;

    const imgW = Math.max(imageSize?.width || 1, 1);
    const imgH = Math.max(imageSize?.height || 1, 1);
    const axisX = (imgW / 2) * scaleX;
    const axisY = (imgH / 2) * scaleY;

    ctx.save();
    for (let x = 0, col = 0; x <= canvasW + 0.5; x += stepX, col++) {
        const major = col % 5 === 0;
        ctx.strokeStyle = major ? theme.major : theme.minor;
        ctx.lineWidth = major ? theme.majorWidth : theme.minorWidth;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvasH);
        ctx.stroke();
    }
    for (let y = 0, row = 0; y <= canvasH + 0.5; y += stepY, row++) {
        const major = row % 5 === 0;
        ctx.strokeStyle = major ? theme.major : theme.minor;
        ctx.lineWidth = major ? theme.majorWidth : theme.minorWidth;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvasW, y);
        ctx.stroke();
    }
    ctx.strokeStyle = theme.axis;
    ctx.lineWidth = theme.axisWidth;
    ctx.beginPath();
    ctx.moveTo(axisX, 0);
    ctx.lineTo(axisX, canvasH);
    ctx.moveTo(0, axisY);
    ctx.lineTo(canvasW, axisY);
    ctx.stroke();
    ctx.restore();
};

/** Canvas px tolerance for invisible snap (bins, walls, arcs, scale). */
const SNAP_TOL_CANVAS_PX = 30;
/** Defer planar enclosure after bin-only adds (ms). */
const MARKER_ADD_ENCLOSURE_DEFER_MS = 400;

/** Bin mode: snap center so a perimeter snap point meets another bin's snap point. */
const snapBinModeCenter = (movingId, x, y, markers, scaleMin) =>
    snapBinCenterForCanvas(movingId, x, y, markers, scaleMin, SNAP_TOL_CANVAS_PX);

// Soft fallback diameter used only when the chosen bin record has no dimensions yet.
// Round bins typically have an explicit `diameter` from BTS; flat houses pull from `width`/`length`.
const FALLBACK_BIN_SIZE = 30;
/** Default flat footprint when placing (feet) — sized via sidebar W / L after place. */
const DEFAULT_FLAT_WIDTH_FT = FALLBACK_BIN_SIZE;
const DEFAULT_FLAT_LENGTH_FT = FALLBACK_BIN_SIZE;

const MODES = {
    PLACE: "place",
    WALL: "wall",
    ARC: "arc",
    FLAT: "flat",
};

const isMultiSelectKey = (e) => e.ctrlKey || e.metaKey;

/** Circle bounds in image px for a round-bin marker. */
const getMarkerBounds = (m) => {
    const r = getMarkerRadiusImagePx(m);
    return {
        left: m.x - r,
        right: m.x + r,
        top: m.y - r,
        bottom: m.y + r,
        cx: m.x,
        cy: m.y,
        r,
    };
};

/** Bounding box (image px) around selected bins and walls for group drag hit-test. */
const getGroupSelectionBounds = (markers, layoutSegments, markerIds, segmentIds, pad = 20) => {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    const add = (x, y, r = 0) => {
        minX = Math.min(minX, x - r);
        maxX = Math.max(maxX, x + r);
        minY = Math.min(minY, y - r);
        maxY = Math.max(maxY, y + r);
    };
    markerIds.forEach((id) => {
        const m = markers.find((mk) => mk.id === id);
        if (!m) return;
        const b = getMarkerBounds(m);
        add(b.cx, b.cy, b.r);
    });
    segmentIds.forEach((id) => {
        const raw = layoutSegments.find((s) => s.id === id);
        const g = raw ? resolveSegmentGeometry(raw, markers, layoutSegments) : null;
        if (!g) return;
        add(g.x1, g.y1, 8);
        add(g.x2, g.y2, 8);
        if (g.curveX != null && g.curveY != null) add(g.curveX, g.curveY, 8);
    });
    if (!Number.isFinite(minX)) return null;
    const span = Math.max(maxX - minX, maxY - minY, 1);
    const autoPad = Math.max(pad, Math.min(80, span * 0.06));
    return {
        minX: minX - autoPad,
        maxX: maxX + autoPad,
        minY: minY - autoPad,
        maxY: maxY + autoPad,
    };
};

/** Centroid of all bin markers in image space. */
const computeBinCentroid = (markers) => {
    if (!markers?.length) return null;
    let sumX = 0;
    let sumY = 0;
    markers.forEach((m) => {
        sumX += m.x;
        sumY += m.y;
    });
    const n = markers.length;
    return { cx: sumX / n, cy: sumY / n };
};

const segmentTouchesMarkerIds = (seg, markerIdSet, markers, segments) => {
    if (seg.markerId1 != null && markerIdSet.has(seg.markerId1)) return true;
    if (seg.markerId2 != null && markerIdSet.has(seg.markerId2)) return true;
    const g = resolveSegmentGeometry(seg, markers, segments);
    if (!g) return false;
    for (const id of markerIdSet) {
        const m = markers.find((mk) => mk.id === id);
        if (!m) continue;
        const tol = getMarkerRadiusImagePx(m) + 12;
        if (Math.hypot(g.x1 - m.x, g.y1 - m.y) <= tol) return true;
        if (Math.hypot(g.x2 - m.x, g.y2 - m.y) <= tol) return true;
    }
    return false;
};

const normalizeAngle = (a) => {
    const t = 2 * Math.PI;
    return ((a % t) + t) % t;
};

/** Clockwise per click (image y increases downward). */
const GROUP_ROTATE_STEP_DEG = 12.5;
const GROUP_ROTATE_STEP_RAD = (GROUP_ROTATE_STEP_DEG * Math.PI) / 180;
const GROUP_ROTATE_FINE_DEG = 0.5;
const GROUP_ROTATE_FINE_RAD = (GROUP_ROTATE_FINE_DEG * Math.PI) / 180;

const rotatePointAround = (x, y, cx, cy, angleRad) => {
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);
    const dx = x - cx;
    const dy = y - cy;
    return {
        x: cx + dx * cos - dy * sin,
        y: cy + dx * sin + dy * cos,
    };
};

const rotateSegmentAround = (seg, cx, cy, angleRad) => {
    const next = { ...seg };
    const rot = (x, y) => rotatePointAround(x, y, cx, cy, angleRad);
    if (next.x1 != null && next.y1 != null && next.x2 != null && next.y2 != null) {
        const p1 = rot(next.x1, next.y1);
        const p2 = rot(next.x2, next.y2);
        next.x1 = p1.x;
        next.y1 = p1.y;
        next.x2 = p2.x;
        next.y2 = p2.y;
    }
    if (next.bulgeX != null && next.bulgeY != null) {
        const b = rot(next.bulgeX, next.bulgeY);
        next.bulgeX = b.x;
        next.bulgeY = b.y;
    }
    if (next.attach1?.x != null && next.attach1?.y != null) {
        const a = rot(next.attach1.x, next.attach1.y);
        next.attach1 = { ...next.attach1, x: a.x, y: a.y };
    }
    if (next.attach2?.x != null && next.attach2?.y != null) {
        const a = rot(next.attach2.x, next.attach2.y);
        next.attach2 = { ...next.attach2, x: a.x, y: a.y };
    }
    if (next.angle1 != null) next.angle1 = normalizeAngle(next.angle1 + angleRad);
    if (next.angle2 != null) next.angle2 = normalizeAngle(next.angle2 + angleRad);
    if (next.bulgeAngle != null) {
        next.bulgeAngle = normalizeAngle(next.bulgeAngle + angleRad);
    }
    return next;
};

/** Rotate selected bins, walls, and flats around the selection bounding-box center. */
const rotateGroupSelection = (
    markers,
    segments,
    markerIds,
    segmentIds,
    angleRad = GROUP_ROTATE_STEP_RAD
) => {
    const segIds = expandSegmentIdsToFullFlatRects(segments, segmentIds);
    const bounds = getGroupSelectionBounds(markers, segments, markerIds, segIds, 0);
    if (!bounds) return { markers, segments };
    const cx = (bounds.minX + bounds.maxX) / 2;
    const cy = (bounds.minY + bounds.maxY) / 2;
    const markerSet = new Set(markerIds);
    const segmentSet = new Set(segIds);
    const shouldRotateSegment = (s) => {
        if (segIds.length && segmentSet.has(s.id)) return true;
        if (markerIds.length && segmentTouchesMarkerIds(s, markerSet, markers, segments)) {
            return true;
        }
        return false;
    };
    const nextMarkers = markers.map((m) => {
        if (!markerSet.has(m.id)) return m;
        const p = rotatePointAround(m.x, m.y, cx, cy, angleRad);
        return { ...m, x: p.x, y: p.y };
    });
    const nextSegments = segments.map((s) =>
        shouldRotateSegment(s) ? rotateSegmentAround(s, cx, cy, angleRad) : s
    );
    return { markers: nextMarkers, segments: nextSegments };
};

const applyDeltaToSegment = (seg, dx, dy) => {
    const next = { ...seg };
    if (next.x1 != null) {
        next.x1 += dx;
        next.y1 += dy;
    }
    if (next.x2 != null) {
        next.x2 += dx;
        next.y2 += dy;
    }
    if (next.bulgeX != null && next.bulgeY != null) {
        next.bulgeX += dx;
        next.bulgeY += dy;
    }
    if (next.attach1?.x != null) {
        next.attach1 = { ...next.attach1, x: next.attach1.x + dx, y: next.attach1.y + dy };
    }
    if (next.attach2?.x != null) {
        next.attach2 = { ...next.attach2, x: next.attach2.x + dx, y: next.attach2.y + dy };
    }
    return next;
};

const resolveGroupMarkerIds = (selectedMarkerId, selectedMarkerIds) =>
    selectedMarkerIds?.length > 0
        ? selectedMarkerIds
        : selectedMarkerId
          ? [selectedMarkerId]
          : [];

/** Two or more selected bins — flat tools must not steal group move. */
const hasMultiBinGroupSelection = (selectedMarkerId, selectedMarkerIds) =>
    resolveGroupMarkerIds(selectedMarkerId, selectedMarkerIds).length >= 2;

/** @deprecated Use hasMultiBinGroupSelection */
const hasBinGroupSelection = hasMultiBinGroupSelection;

const resolveGroupSegmentIds = (selectedSegmentId, selectedSegmentIds) =>
    selectedSegmentIds?.length > 0
        ? selectedSegmentIds
        : selectedSegmentId
          ? [selectedSegmentId]
          : [];

const shouldStartGroupDrag = ({
    x,
    y,
    markerHit,
    segHit,
    flatRectHit,
    selectedMarkerIds,
    selectedSegmentIds,
    markers,
    layoutSegments,
}) => {
    if (selectedMarkerIds.length === 0 && selectedSegmentIds.length === 0) {
        return false;
    }
    if (markerHit && selectedMarkerIds.includes(markerHit.id)) return true;
    if (segHit && selectedSegmentIds.includes(segHit.id)) return true;
    if (flatRectHit) {
        const bounds = getFlatRectBounds(layoutSegments, flatRectHit);
        const wallIds = bounds?.segmentIds ?? [];
        if (
            wallIds.length > 0 &&
            wallIds.every((id) => selectedSegmentIds.includes(id))
        ) {
            return true;
        }
    }
    const bounds = getGroupSelectionBounds(
        markers,
        layoutSegments,
        selectedMarkerIds,
        selectedSegmentIds
    );
    if (!bounds) return false;
    const inBounds =
        x >= bounds.minX &&
        x <= bounds.maxX &&
        y >= bounds.minY &&
        y <= bounds.maxY;
    if (!inBounds) return false;
    /** Bin-only marquee: bbox covers the whole site — require a direct bin/wall hit. */
    if (selectedSegmentIds.length === 0 && selectedMarkerIds.length > 0) {
        return false;
    }
    return true;
};

/** Unique flat-house rect ids from a segment id list. */
const flatRectIdsFromSegmentIds = (segments, segmentIds) => {
    const ids = new Set();
    (segmentIds || []).forEach((sid) => {
        const s = segments.find((seg) => seg.id === sid);
        if (s?.closedRectId) ids.add(s.closedRectId);
    });
    return [...ids];
};

/** Marquee / multi-select: include all four walls when any wall of a flat is selected. */
const expandSegmentIdsToFullFlatRects = (segments, segmentIds) => {
    const rectIds = flatRectIdsFromSegmentIds(segments, segmentIds);
    if (!rectIds.length) return segmentIds;
    const extra = new Set(segmentIds);
    rectIds.forEach((rectId) => {
        segments
            .filter((s) => s.closedRectId === rectId)
            .forEach((s) => extra.add(s.id));
    });
    return [...extra];
};

/** Map layout from one image size to another (blank canvas → uploaded photo). */
const scaleLayoutGeometryForImageResize = (markers, segments, sx, sy) => ({
    markers: markers.map((m) => ({ ...m, x: m.x * sx, y: m.y * sy })),
    segments: segments.map((seg) => {
        const next = { ...seg };
        if (next.x1 != null && next.y1 != null && next.x2 != null && next.y2 != null) {
            next.x1 *= sx;
            next.y1 *= sy;
            next.x2 *= sx;
            next.y2 *= sy;
        }
        if (next.bulgeX != null && next.bulgeY != null) {
            next.bulgeX *= sx;
            next.bulgeY *= sy;
        }
        if (next.attach1?.x != null && next.attach1?.y != null) {
            next.attach1 = {
                ...next.attach1,
                x: next.attach1.x * sx,
                y: next.attach1.y * sy,
            };
        }
        if (next.attach2?.x != null && next.attach2?.y != null) {
            next.attach2 = {
                ...next.attach2,
                x: next.attach2.x * sx,
                y: next.attach2.y * sy,
            };
        }
        return next;
    }),
});

/** Align / resize selected flat houses to the anchor flat (first selected). */
const alignFlatsInGroup = (segments, rectIds, anchorRectId, op) => {
    const anchorBounds = getFlatRectBounds(segments, anchorRectId);
    if (!anchorBounds) return segments;
    const anchorCx = (anchorBounds.minX + anchorBounds.maxX) / 2;
    const anchorCy = (anchorBounds.minY + anchorBounds.maxY) / 2;
    const anchorW = anchorBounds.maxX - anchorBounds.minX;
    const anchorH = anchorBounds.maxY - anchorBounds.minY;
    let next = segments;
    rectIds.forEach((rectId) => {
        if (rectId === anchorRectId) return;
        const b = getFlatRectBounds(next, rectId);
        if (!b) return;
        let { minX, minY, maxX, maxY } = b;
        const cx = (minX + maxX) / 2;
        const cy = (minY + maxY) / 2;
        if (op === "alignX") {
            const dx = anchorCx - cx;
            minX += dx;
            maxX += dx;
        } else if (op === "alignY") {
            const dy = anchorCy - cy;
            minY += dy;
            maxY += dy;
        } else if (op === "sameSize") {
            minX = cx - anchorW / 2;
            maxX = cx + anchorW / 2;
            minY = cy - anchorH / 2;
            maxY = cy + anchorH / 2;
        } else {
            return;
        }
        next = replaceFlatRectSegments(next, rectId, minX, minY, maxX, maxY);
    });
    return next;
};

/** Align / resize selected bins to the anchor bin (first selected). */
const alignMarkersInGroup = (markers, ids, anchorId, op) => {
    const anchor = markers.find((m) => m.id === anchorId);
    if (!anchor || ids.length === 0) return markers;
    if (op === "sameSize") {
        const r = getMarkerRadiusImagePx(anchor);
        return markers.map((m) =>
            ids.includes(m.id) && m.id !== anchorId ? { ...m, radiusPx: r } : m
        );
    }
    return markers.map((m) => {
        if (!ids.includes(m.id) || m.id === anchorId) return m;
        if (op === "alignX") return { ...m, x: anchor.x };
        if (op === "alignY") return { ...m, y: anchor.y };
        return m;
    });
};

const MAX_LAYOUT_UNDO = 15;

const cloneMarkerForHistory = (m) => ({
    id: m.id,
    x: m.x,
    y: m.y,
    radiusPx: m.radiusPx,
    binDataId: m.binDataId ?? null,
});

const cloneSegmentForHistory = (s) => {
    const o = {
        id: s.id,
        kind: s.kind,
        isInner: !!s.isInner,
        markerId1: s.markerId1 ?? null,
        angle1: s.angle1 ?? null,
        markerId2: s.markerId2 ?? null,
        angle2: s.angle2 ?? null,
    };
    if (s.attach1) o.attach1 = { ...s.attach1 };
    if (s.attach2) o.attach2 = { ...s.attach2 };
    if (s.bulgeX != null) {
        o.bulgeX = s.bulgeX;
        o.bulgeY = s.bulgeY;
    }
    if (s.x1 != null) {
        o.x1 = s.x1;
        o.y1 = s.y1;
        o.x2 = s.x2;
        o.y2 = s.y2;
    }
    if (s.closedRectId) o.closedRectId = s.closedRectId;
    return o;
};

/** Shallow copy for group-drag origin (avoid JSON deep-clone per segment). */
const cloneSegmentForDrag = (s) => {
    const o = { ...s };
    if (s.attach1) o.attach1 = { ...s.attach1 };
    if (s.attach2) o.attach2 = { ...s.attach2 };
    return o;
};

/** Tag on canvas / preview: custom name, assigned bin name, or area letter. */
const getMarkerDisplayLabel = (
    marker,
    slot,
    markerLabels,
    siteBins,
    markerAreaNames = {}
) => {
    const custom = markerLabels?.[marker.id];
    if (typeof custom === "string" && custom.trim()) return custom.trim();
    if (marker.binDataId != null) {
        const bin = siteBins.find((b) =>
            sameBinDataId(b.binDataId, marker.binDataId)
        );
        if (bin?.binName) return String(bin.binName);
    }
    const stable = markerAreaNames[marker.id];
    if (stable) return stable;
    const letter = unassignedAreaLetter(slot?.areaIndex ?? 0);
    return slot?.areaLabel ?? `Area ${letter}`;
};

const normalizeLayoutHistorySiteGuid = (guid) =>
    String(global.normalizeGuidForCompare?.(guid) ?? guid ?? "");

const cloneLayoutSnapshot = (snap) => ({
    siteGuid: normalizeLayoutHistorySiteGuid(snap.siteGuid),
    markers: (snap.markers ?? []).map(cloneMarkerForHistory),
    layoutSegments: (snap.layoutSegments ?? []).map(cloneSegmentForHistory),
    faceAssignments: { ...(snap.faceAssignments ?? {}) },
    faceLabels: { ...(snap.faceLabels ?? {}) },
    markerLabels: { ...(snap.markerLabels ?? {}) },
    markerAreaNames: { ...(snap.markerAreaNames ?? {}) },
    viewPan: { x: snap.viewPan?.x ?? 0, y: snap.viewPan?.y ?? 0 },
    viewZoom: snap.viewZoom ?? 1,
});

/** Remove only the chosen segment(s); clear broken attach refs on survivors (do not cascade-delete). */
const filterSegmentsAfterDelete = (segments, deletedIds) => {
    const gone = new Set(deletedIds);
    segments.forEach((s) => {
        if (!gone.has(s.id) || !s.closedRectId) return;
        segments.forEach((t) => {
            if (t.closedRectId === s.closedRectId) gone.add(t.id);
        });
    });
    return segments
        .filter((s) => !gone.has(s.id))
        .map((s) => {
            const next = { ...s };
            [1, 2].forEach((end) => {
                const attach = next[`attach${end}`];
                if (attach?.segmentId && gone.has(attach.segmentId)) {
                    delete next[`attach${end}`];
                }
            });
            return next;
        });
};

/** Drop walls/arcs that are not snapped at both ends (orange dashed). */
const pruneUnsnappedSegments = (segments, markers) => {
    const ids = segments
        .filter((s) => isFloatingSegment(s, markers, segments))
        .map((s) => s.id);
    if (!ids.length) return segments;
    return filterSegmentsAfterDelete(segments, ids);
};

/** Static help page served from `public/site-layout-help.html`. */
const siteLayoutHelpUrl = () => {
    const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
    return `${base}/site-layout-help.html`;
};

const siteLayoutSessionKey = (siteGuid) =>
    `siteLayout_draft_${String(global.normalizeGuidForCompare?.(siteGuid) ?? siteGuid)}`;

const siteLayoutSessionGeomKey = (siteGuid) => `${siteLayoutSessionKey(siteGuid)}_geom`;

const siteLayoutSessionImageKey = (siteGuid) => `${siteLayoutSessionKey(siteGuid)}_img`;

/** Geometry-only draft (walls/arcs survive even when the photo exceeds sessionStorage quota). */
const siteLayoutSessionGeomPayload = (payload) => ({
    blankCanvas: payload.blankCanvas,
    blankImageSize: payload.blankImageSize,
    markers: payload.markers,
    layoutSegments: payload.layoutSegments,
    faceAssignments: payload.faceAssignments,
    faceLabels: payload.faceLabels,
    markerLabels: payload.markerLabels,
    markerAreaNames: payload.markerAreaNames,
    feetPerPixel: payload.feetPerPixel,
    clusterEpsFt: payload.clusterEpsFt,
    mode: payload.mode,
});

const saveSiteLayoutSession = (siteGuid, payload) => {
    const geomKey = siteLayoutSessionGeomKey(siteGuid);
    const imgKey = siteLayoutSessionImageKey(siteGuid);
    const fullKey = siteLayoutSessionKey(siteGuid);
    try {
        sessionStorage.setItem(geomKey, JSON.stringify(siteLayoutSessionGeomPayload(payload)));
    } catch {
        // ignore
    }
    const { imageDataUrl, ...rest } = payload;
    try {
        if (imageDataUrl) {
            sessionStorage.setItem(imgKey, imageDataUrl);
        } else {
            sessionStorage.removeItem(imgKey);
        }
        sessionStorage.setItem(fullKey, JSON.stringify({ ...rest, imageDataUrl: imageDataUrl || null }));
    } catch {
        // image data URLs can exceed sessionStorage quota on very large photos; geometry key still saved
    }
};

const loadSiteLayoutSession = (siteGuid) => {
    try {
        const geomRaw = sessionStorage.getItem(siteLayoutSessionGeomKey(siteGuid));
        const geom = geomRaw ? JSON.parse(geomRaw) : null;
        const fullRaw = sessionStorage.getItem(siteLayoutSessionKey(siteGuid));
        const full = fullRaw ? JSON.parse(fullRaw) : null;
        const imageOnly = sessionStorage.getItem(siteLayoutSessionImageKey(siteGuid));
        if (!geom && !full && !imageOnly) return null;
        const merged = { ...(full || {}), ...(geom || {}) };
        if (!merged.imageDataUrl && imageOnly) merged.imageDataUrl = imageOnly;
        return merged;
    } catch {
        return null;
    }
};

const clearSiteLayoutSession = (siteGuid) => {
    try {
        sessionStorage.removeItem(siteLayoutSessionKey(siteGuid));
        sessionStorage.removeItem(siteLayoutSessionGeomKey(siteGuid));
        sessionStorage.removeItem(siteLayoutSessionImageKey(siteGuid));
    } catch { /* ignore */ }
};

/** DB / session may store editor fields flat or under `editor`. */
const normalizeEditorDraftPayload = (raw) => {
    if (!raw || typeof raw !== "object") return null;
    const inner =
        raw.editor && typeof raw.editor === "object" && !Array.isArray(raw.editor)
            ? raw.editor
            : raw;
    if (
        !inner.imageDataUrl &&
        !inner.blankCanvas &&
        !(inner.markers?.length > 0) &&
        !(inner.layoutSegments?.length > 0) &&
        !(inner.stices?.length > 0)
    ) {
        return null;
    }
    return inner;
};

/** One row in the assignment dropdown (full init or site-list fallback). */
const mapBinRowForLayoutAssignment = (b) => {
    if (!b) return null;
    const binDataId = b.binDataId ?? b.binId;
    if (binDataId == null || binDataId === "") return null;
    const binName = String(b.binName ?? b.name ?? binDataId).trim();
    return {
        binDataId,
        binName: binName || String(binDataId),
        type: b.type,
        diameter: b.diameter,
        width: b.width,
        length: b.length,
        height: b.height,
        degreesToNorth: b.degreesToNorth,
    };
};

const layoutBinsFromSiteInfos = (siteGuid) => {
    const want = global.normalizeGuidForCompare?.(siteGuid) ?? siteGuid;
    const info = (global.gw?.siteInfos ?? []).find(
        (s) => global.normalizeGuidForCompare?.(s.siteGuid) === want
    );
    return (info?.bins ?? [])
        .map(mapBinRowForLayoutAssignment)
        .filter(Boolean);
};

/** When fetchUserSite fails, still fill assignment list from fetchUserAttribute site list. */
const hydrateSelectedSiteBinsFromSiteInfo = (siteGuid) => {
    const mapped = layoutBinsFromSiteInfos(siteGuid);
    if (!mapped.length || !global.gw?.selectedSite) return 0;
    global.gw.selectedSite.bins = mapped.map((row, i) => ({
        ...row,
        binIndex: i,
        binId: row.binDataId,
        name: row.binName,
        cableList: [],
    }));
    return mapped.length;
};

/** Minimal layout bounds when only the editor snapshot is persisted. */
const EMPTY_LAYOUT_BOUNDS = {
    width: 0,
    length: 0,
    height: 0,
    rotation: 0,
    x: 0,
    y: 0,
    z: 0,
    cx: null,
    cy: null,
    cz: null,
    lx: null,
    ly: null,
    lz: null,
};

const getThemeColor = (lightColor, darkColor) =>
    global.gw.theme === "light" ? lightColor : darkColor;

/** Site Layout dark-mode text (orange accent — not white). */
const LAYOUT_DARK_TEXT = "#f4cc8e";
const LAYOUT_DARK_TEXT_MUTED = "#d4a86a";

/** Footprint on the ground from the circle drawn on the image (not BTS diameters). */
const getMarkerFootprintFt = (marker, feetPerPixel) => {
    if (feetPerPixel <= 0) return 0;
    const rPx = marker?.radiusPx > 4 ? marker.radiusPx : DEFAULT_MARKER_RADIUS_PX;
    return round1(2 * rPx * feetPerPixel);
};

const diameterFtToRadiusPx = (diameterFt, feetPerPixel) => {
    const d = Number(diameterFt);
    if (!(d > 0) || feetPerPixel <= 0) return null;
    return Math.max(6, (d / 2) / feetPerPixel);
};

/** BTS diameter (or max flat width/length) for hinting size when a bin is linked. */
const getBinRecordDiameterFt = (bin) => {
    if (!bin) return 0;
    const d = Number(bin.diameter);
    if (d > 0) return d;
    const w = Number(bin.width);
    const l = Number(bin.length);
    if (w > 0 && l > 0) return Math.max(w, l);
    return 0;
};

const markerDiameterFtString = (marker, feetPerPixel, siteBins) => {
    let ft = getMarkerFootprintFt(marker, feetPerPixel);
    if (ft <= 0 && marker?.binDataId != null) {
        const bin = siteBins.find((b) =>
            sameBinDataId(b.binDataId, marker.binDataId)
        );
        ft = round1(getBinRecordDiameterFt(bin));
    }
    if (ft <= 0) ft = FALLBACK_BIN_SIZE;
    return String(ft);
};

const MIN_FLAT_RECT_IMAGE_PX = 6;

/** Image-space bounds for a flat area (rectangle tool or face polygon). */
const getFaceBoundsImage = (face, layoutSegments) => {
    const rectId = closedRectIdForFace(face, layoutSegments);
    if (rectId) {
        const b = getFlatRectBounds(layoutSegments, rectId);
        if (b) return { ...b, rectId };
    }
    if (face?.bbox) {
        const { minX, minY, maxX, maxY } = face.bbox;
        if (Number.isFinite(minX)) {
            return { minX, minY, maxX, maxY, rectId: null };
        }
    }
    const poly = face?.polygon;
    if (!poly?.length) return null;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    poly.forEach((p) => {
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x);
        maxY = Math.max(maxY, p.y);
    });
    if (!Number.isFinite(minX)) return null;
    return { minX, minY, maxX, maxY, rectId: null };
};

const faceWidthLengthFtStrings = (
    face,
    layoutSegments,
    feetPerPixel,
    siteBins,
    assignedBinDataId
) => {
    const bounds = getFaceBoundsImage(face, layoutSegments);
    const rectId =
        bounds?.rectId ?? closedRectIdForFace(face, layoutSegments);
    const oriented =
        rectId != null
            ? getFlatRectOrientedGeometry(layoutSegments, rectId)
            : null;
    let widthFt =
        oriented && feetPerPixel > 0
            ? round1(oriented.halfW * 2 * feetPerPixel)
            : bounds && feetPerPixel > 0
              ? round1((bounds.maxX - bounds.minX) * feetPerPixel)
              : 0;
    let lengthFt =
        oriented && feetPerPixel > 0
            ? round1(oriented.halfL * 2 * feetPerPixel)
            : bounds && feetPerPixel > 0
              ? round1((bounds.maxY - bounds.minY) * feetPerPixel)
              : 0;
    if ((widthFt <= 0 || lengthFt <= 0) && assignedBinDataId != null) {
        const bin = siteBins.find((b) =>
            sameBinDataId(b.binDataId, assignedBinDataId)
        );
        const w = Number(bin?.width);
        const l = Number(bin?.length);
        if (w > 0) widthFt = round1(w);
        if (l > 0) lengthFt = round1(l);
    }
    if (widthFt <= 0) widthFt = FALLBACK_BIN_SIZE;
    if (lengthFt <= 0) lengthFt = FALLBACK_BIN_SIZE;
    return {
        w: String(widthFt),
        l: String(lengthFt),
        rectId: bounds?.rectId ?? null,
    };
};

/** Marker → CustomerSiteLayoutBin draft (relative to its group center). */
const buildBinDraft = (
    marker,
    group,
    feetPerPixel,
    bin,
    forPreview = false,
    matchSite3dDimensions = false,
    useEditorFootprint = false
) => {
    if (!bin) return null;
    const isRound = !(bin.type || "").toLowerCase().includes("flat");
    const footprintFt = getMarkerFootprintFt(marker, feetPerPixel);
    const catalogRoundD =
        bin.diameter > 0 ? bin.diameter : bin.width > 0 ? bin.width : 0;
    const useCatalogRound =
        matchSite3dDimensions && isRound && catalogRoundD > 0 && !useEditorFootprint;
    const useDrawnRound =
        useEditorFootprint && isRound && footprintFt > 0;
    const width = useCatalogRound
        ? catalogRoundD
        : useDrawnRound
          ? footprintFt
          : footprintFt > 0
            ? footprintFt
            : isRound
              ? FALLBACK_BIN_SIZE
              : bin.width || FALLBACK_BIN_SIZE;
    const length = useCatalogRound
        ? catalogRoundD
        : useDrawnRound
          ? footprintFt
          : footprintFt > 0
            ? footprintFt
            : isRound
              ? FALLBACK_BIN_SIZE
              : bin.length || FALLBACK_BIN_SIZE;

    const dx = (marker.x - group.centerX) * feetPerPixel;
    const dz = (marker.y - group.centerY) * feetPerPixel;

    return {
        binId: parseInt(bin.binDataId ?? bin.binId, 10) || 0,
        binName: String(bin.binName ?? ""),
        groupOrder: marker.groupOrder ?? 0,
        width,
        length,
        height: bin.height || 0,
        rotation: forPreview ? 0 : bin.degreesToNorth || 0,
        isRound,
        x: round1(dx),
        y: 0,
        z: round1(dz),
        cx: null,
        cy: null,
        cz: null,
        sides: [],
    };
};

const round1 = (v) => Math.round(v * 10) / 10;

const OPENCV_LOAD_TIMEOUT_MS = 25000;
const OPENCV_DETECT_TIMEOUT_MS = 15000;
const DETECT_MAX_SIDE = 900;
/** Keep layout images small enough for smooth canvas + React state. */
const MAX_LAYOUT_IMAGE_SIDE = 2400;

const withTimeout = (promise, ms, message) =>
    Promise.race([
        promise,
        new Promise((_, reject) => {
            setTimeout(() => reject(new Error(message)), ms);
        }),
    ]);

/** Downscale phone photos so upload does not freeze the page (OpenCV-sized payloads in state). */
const prepareLayoutImage = (img, originalDataUrl) =>
    new Promise((resolve) => {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        const maxSide = Math.max(w, h, 1);
        if (maxSide <= MAX_LAYOUT_IMAGE_SIDE) {
            resolve({ displayImg: img, displayUrl: originalDataUrl, width: w, height: h });
            return;
        }
        const scale = MAX_LAYOUT_IMAGE_SIDE / maxSide;
        const nw = Math.max(1, Math.round(w * scale));
        const nh = Math.max(1, Math.round(h * scale));
        const canvas = document.createElement("canvas");
        canvas.width = nw;
        canvas.height = nh;
        canvas.getContext("2d").drawImage(img, 0, 0, nw, nh);
        const displayUrl = canvas.toDataURL("image/jpeg", 0.88);
        const displayImg = new Image();
        displayImg.onload = () => {
            resolve({ displayImg, displayUrl, width: nw, height: nh });
        };
        displayImg.onerror = () => {
            resolve({ displayImg: img, displayUrl: originalDataUrl, width: w, height: h });
        };
        displayImg.src = displayUrl;
    });

/**
 * Lazy-load OpenCV from the bundled @techstark/opencv-js package (code-split chunk).
 * Only used when already warm or as an optional refine pass — never block Detect on first load.
 */
const loadOpenCV = async () => {
    if (typeof window === "undefined") {
        throw new Error("OpenCV is only available in the browser");
    }
    if (window.__siteLayoutOpenCv?.Mat && window.__siteLayoutOpenCv.HoughCircles) {
        return window.__siteLayoutOpenCv;
    }
    if (!window.__siteLayoutOpenCvPromise) {
        window.__siteLayoutOpenCvPromise = withTimeout(
            (async () => {
                const cvModule = await import("@techstark/opencv-js");
                let cv = cvModule.default ?? cvModule;
                if (cv instanceof Promise) {
                    cv = await cv;
                }
                if (cv?.Mat && cv.HoughCircles) {
                    window.__siteLayoutOpenCv = cv;
                    return cv;
                }
                await new Promise((resolve, reject) => {
                    const timeout = setTimeout(() => {
                        reject(new Error("OpenCV runtime did not initialise"));
                    }, OPENCV_LOAD_TIMEOUT_MS);
                    const finish = () => {
                        clearTimeout(timeout);
                        resolve();
                    };
                    if (cv?.onRuntimeInitialized) {
                        const prev = cv.onRuntimeInitialized;
                        cv.onRuntimeInitialized = () => {
                            try { prev?.(); } catch { /* ignore */ }
                            finish();
                        };
                    }
                    let tries = 0;
                    const poll = () => {
                        if (cv?.Mat && cv.HoughCircles) finish();
                        else if (tries++ > 100) {
                            clearTimeout(timeout);
                            reject(new Error("OpenCV runtime did not initialise"));
                        } else {
                            setTimeout(poll, 100);
                        }
                    };
                    poll();
                });
                window.__siteLayoutOpenCv = cv;
                return cv;
            })(),
            OPENCV_LOAD_TIMEOUT_MS,
            "OpenCV load timed out"
        ).catch((e) => {
            delete window.__siteLayoutOpenCvPromise;
            delete window.__siteLayoutOpenCv;
            throw e;
        });
    }
    return window.__siteLayoutOpenCvPromise;
};

/**
 * Hough Circle Transform on the uploaded image. We downscale to ≤1500px on the long edge so
 * detection stays sub-second, then map results back to original-image pixel coordinates.
 *
 * `radiusHints` provides feet-per-pixel-aware min/max radii when we already have a scale; otherwise
 * the caller passes a wide pixel range and we infer scale from the median detected radius.
 */
const detectCirclesInImage = async (image, radiusHints) => {
    const cv = window.__siteLayoutOpenCv;
    if (!cv?.Mat || !cv.HoughCircles) {
        throw new Error("OpenCV not ready");
    }
    const maxSide = DETECT_MAX_SIDE;
    const scale = Math.min(1, maxSide / Math.max(image.naturalWidth, image.naturalHeight, 1));
    const w = Math.max(1, Math.round(image.naturalWidth * scale));
    const h = Math.max(1, Math.round(image.naturalHeight * scale));

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(image, 0, 0, w, h);

    const src = cv.imread(canvas);
    const gray = new cv.Mat();
    const circles = new cv.Mat();
    const detected = [];
    try {
        cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
        cv.medianBlur(gray, gray, 5);

        const minR = Math.max(6, Math.floor((radiusHints.minRadiusPx ?? 8) * scale));
        const maxR = Math.max(minR + 4, Math.ceil((radiusHints.maxRadiusPx ?? Math.min(w, h) / 4) * scale));
        const minDist = Math.max(minR * 1.5, Math.ceil((radiusHints.minDistPx ?? minR * 1.8) * scale));
        const param1 = radiusHints.param1 ?? 80;
        const param2 = radiusHints.param2 ?? 32;

        cv.HoughCircles(
            gray,
            circles,
            cv.HOUGH_GRADIENT,
            1,
            minDist,
            param1,
            param2,
            minR,
            maxR
        );

        for (let i = 0; i < circles.cols; i++) {
            const x = circles.data32F[i * 3];
            const y = circles.data32F[i * 3 + 1];
            const r = circles.data32F[i * 3 + 2];
            detected.push({ x: x / scale, y: y / scale, r: r / scale });
        }
    } finally {
        src.delete();
        gray.delete();
        circles.delete();
    }
    return detected;
};

/**
 * Lightweight fallback when OpenCV cannot load: threshold + blob centroids.
 * Less accurate than Hough circles but works offline with no WASM/script CDN.
 */
/** Fast bin detection (no WASM). Default for Detect bins. */
const detectCirclesCanvasFallback = (image, radiusHints) => {
    const maxSide = DETECT_MAX_SIDE;
    const scale = Math.min(1, maxSide / Math.max(image.naturalWidth, image.naturalHeight, 1));
    const w = Math.max(1, Math.round(image.naturalWidth * scale));
    const h = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(image, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);
    const n = w * h;
    const gray = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
        const j = i * 4;
        gray[i] = (data[j] * 0.299 + data[j + 1] * 0.587 + data[j + 2] * 0.114) | 0;
    }
    let sum = 0;
    for (let i = 0; i < n; i++) sum += gray[i];
    const thresh = (sum / n) * 0.9;
    const fg = new Uint8Array(n);
    for (let i = 0; i < n; i++) fg[i] = gray[i] < thresh ? 1 : 0;

    const minR = radiusHints.minRadiusPx ?? 8;
    const maxR = radiusHints.maxRadiusPx ?? Math.min(w, h) / 4;
    const minArea = Math.PI * minR * minR * scale * scale * 0.25;
    const maxArea = Math.PI * maxR * maxR * scale * scale * 2.5;
    const visited = new Uint8Array(n);
    const circles = [];

    const flood = (start) => {
        const stack = [start];
        let area = 0;
        let sx = 0;
        let sy = 0;
        let minX = w;
        let maxX = 0;
        let minY = h;
        let maxY = 0;
        while (stack.length) {
            const idx = stack.pop();
            if (visited[idx] || !fg[idx]) continue;
            visited[idx] = 1;
            area++;
            if (area > maxArea * 1.5) return;
            const x = idx % w;
            const y = (idx / w) | 0;
            sx += x;
            sy += y;
            minX = Math.min(minX, x);
            maxX = Math.max(maxX, x);
            minY = Math.min(minY, y);
            maxY = Math.max(maxY, y);
            if (x > 0) stack.push(idx - 1);
            if (x < w - 1) stack.push(idx + 1);
            if (y > 0) stack.push(idx - w);
            if (y < h - 1) stack.push(idx + w);
        }
        if (area < minArea || area > maxArea) return;
        const bw = maxX - minX + 1;
        const bh = maxY - minY + 1;
        if (Math.min(bw, bh) / Math.max(bw, bh, 1) < 0.5) return;
        const cx = sx / area;
        const cy = sy / area;
        const r = Math.sqrt(area / Math.PI);
        circles.push({ x: cx / scale, y: cy / scale, r: r / scale });
    };

    for (let i = 0; i < n; i++) {
        if (fg[i] && !visited[i]) flood(i);
    }
    return circles;
};

const median = (values) => {
    if (!values.length) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0
        ? (sorted[mid - 1] + sorted[mid]) / 2
        : sorted[mid];
};

/** Circle radius on the layout image (image px) — from the image only, not BTS diameters. */
const getMarkerDrawRadiusCanvas = (marker, _bin, _feetPerPixel, scaleMin) =>
    getMarkerRadiusImagePx(marker) * scaleMin;

/** Layout feet ↔ image px; default matches Site 3D catalog bin size on the default marker circle. */
const resolveFeetPerPixel = (_markers, siteBins, feetPerPixel) =>
    feetPerPixel > 0 ? feetPerPixel : defaultLayoutFeetPerPixel(siteBins);

const layoutScaleSummary = (fpp) =>
    `${GRAPH_GRID_FEET} ft per graph square (${fpp.toFixed(4)} ft/px)`;

/**
 * Greedy DBSCAN-style clustering on pixel centroids. Markers within `epsPx` of any marker
 * already in a cluster join that cluster. Produces stable group ids (1-based).
 */
const clusterMarkers = (markers, epsPx) => {
    const clusters = [];
    const assigned = new Array(markers.length).fill(-1);
    for (let i = 0; i < markers.length; i++) {
        if (assigned[i] !== -1) continue;
        const queue = [i];
        const clusterIdx = clusters.length;
        clusters.push([]);
        while (queue.length > 0) {
            const idx = queue.shift();
            if (assigned[idx] !== -1) continue;
            assigned[idx] = clusterIdx;
            clusters[clusterIdx].push(idx);
            for (let j = 0; j < markers.length; j++) {
                if (assigned[j] !== -1) continue;
                const dx = markers[idx].x - markers[j].x;
                const dy = markers[idx].y - markers[j].y;
                if (Math.sqrt(dx * dx + dy * dy) <= epsPx) queue.push(j);
            }
        }
    }
    return assigned; // index per marker → cluster idx
};

/** Assign stable Area names to new markers only (never rename after enclosure refresh). */
const mergeMarkerAreaNamesFromSlots = (namesIn, slots) => {
    const next = { ...(namesIn || {}) };
    let changed = false;
    (slots || []).forEach((slot) => {
        if (slot.kind === "marker" && slot.areaLabel && !next[slot.marker.id]) {
            next[slot.marker.id] = slot.areaLabel;
            changed = true;
        }
    });
    return changed ? next : namesIn || {};
};

/** Stable Area letter/name per flat rect (survives enclosure face-id churn). */
const mergeFaceAreaNamesByRectId = (namesIn, slots, segments) => {
    const next = { ...(namesIn || {}) };
    let changed = false;
    (slots || []).forEach((slot) => {
        if (slot.kind !== "face") return;
        const rid = closedRectIdForFace(slot.face, segments);
        if (rid && slot.areaLabel && !next[rid]) {
            next[rid] = slot.areaLabel;
            changed = true;
        }
    });
    return changed ? next : namesIn || {};
};

/** Stable assignment-panel row id for flats (survives enclosure face id changes). */
const assignmentFaceRowDomId = (face, segments) => {
    const rid = closedRectIdForFace(face, segments);
    return rid ? `layout-assign-face-${rid}` : `layout-assign-face-${face.id}`;
};

/** One enclosed face per flat rect — drop stale `flat:` synthetics when a real face exists. */
const dedupeEnclosedFacesForFlatRects = (faces, segments) => {
    const byRectId = new Map();
    const rest = [];
    (faces || []).forEach((f) => {
        const rid = closedRectIdForFace(f, segments);
        if (!rid) {
            rest.push(f);
            return;
        }
        const existing = byRectId.get(rid);
        if (!existing) {
            byRectId.set(rid, f);
            return;
        }
        if (!f.id.startsWith("flat:") && existing.id.startsWith("flat:")) {
            byRectId.set(rid, f);
        } else if (
            f.id.startsWith("flat:") &&
            !existing.id.startsWith("flat:")
        ) {
            byRectId.set(rid, existing);
        }
    });
    return [...rest, ...byRectId.values()];
};

const buildSyntheticFlatFace = (segments, rectId) => {
    const bounds = getFlatRectBounds(segments, rectId);
    if (!bounds) return null;
    const stableFaceId = `flat:${rectId}`;
    const center = {
        x: (bounds.minX + bounds.maxX) / 2,
        y: (bounds.minY + bounds.maxY) / 2,
    };
    return {
        id: stableFaceId,
        segmentIds: [...bounds.segmentIds],
        polygon: [
            { x: bounds.minX, y: bounds.minY },
            { x: bounds.maxX, y: bounds.minY },
            { x: bounds.maxX, y: bounds.maxY },
            { x: bounds.minX, y: bounds.maxY },
        ],
        centroid: center,
        labelPoint: center,
    };
};

/** Preserve flat/wall-pocket assignments when planar enclosure regenerates face ids. */
const migrateFaceAssignmentsForFaces = (
    prevAssign,
    prevFaces,
    newFaces,
    segments,
    prevSegments
) => {
    const prev = prevAssign || {};
    const byRectId = new Map();
    const byTopology = new Map();
    const byAreaOrderIndex = new Map();
    (prevFaces || []).forEach((f) => {
        if (prev[f.id] == null) return;
        const rid = closedRectIdForFace(f, prevSegments);
        if (rid) byRectId.set(rid, prev[f.id]);
        const topo = faceTopologyKey(f);
        if (topo) byTopology.set(topo, prev[f.id]);
        if (f.areaOrderIndex != null) {
            byAreaOrderIndex.set(f.areaOrderIndex, prev[f.id]);
        }
    });
    const next = {};
    (newFaces || []).forEach((f) => {
        const rid = closedRectIdForFace(f, segments);
        const topo = faceTopologyKey(f);
        next[f.id] =
            prev[f.id] ??
            (rid ? byRectId.get(rid) ?? null : null) ??
            (topo ? byTopology.get(topo) ?? null : null) ??
            (f.areaOrderIndex != null
                ? byAreaOrderIndex.get(f.areaOrderIndex) ?? null
                : null);
    });
    return next;
};

const migrateFaceAssignmentState = (
    prevAssign,
    prevLabels,
    prevFaces,
    newFaces,
    segments,
    prevSegments
) => {
    const nextAssignments = migrateFaceAssignmentsForFaces(
        prevAssign,
        prevFaces,
        newFaces,
        segments,
        prevSegments
    );
    const nextLabels = migrateFaceLabelsForFaces(
        prevLabels,
        prevFaces,
        newFaces,
        segments,
        prevSegments,
        nextAssignments
    );
    return { nextAssignments, nextLabels };
};

const migrateFaceLabelsForFaces = (
    prevLabels,
    prevFaces,
    newFaces,
    segments,
    prevSegments,
    nextAssignments = {}
) => {
    const prev = prevLabels || {};
    const byRectId = new Map();
    const byTopology = new Map();
    const byAreaOrderIndex = new Map();
    (prevFaces || []).forEach((f) => {
        if (!prev[f.id]) return;
        const rid = closedRectIdForFace(f, prevSegments);
        if (rid) byRectId.set(rid, prev[f.id]);
        const topo = faceTopologyKey(f);
        if (topo) byTopology.set(topo, prev[f.id]);
        if (f.areaOrderIndex != null) {
            byAreaOrderIndex.set(f.areaOrderIndex, prev[f.id]);
        }
    });
    const next = {};
    (newFaces || []).forEach((f) => {
        if (nextAssignments[f.id] != null) return;
        const rid = closedRectIdForFace(f, segments);
        const topo = faceTopologyKey(f);
        const val =
            prev[f.id] ??
            (rid ? byRectId.get(rid) : undefined) ??
            (topo ? byTopology.get(topo) : undefined) ??
            (f.areaOrderIndex != null
                ? byAreaOrderIndex.get(f.areaOrderIndex)
                : undefined);
        if (val) next[f.id] = val;
    });
    return next;
};

const SiteLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const narrowLayout = useNarrowChrome();
    const canvasRef = useRef(null);
    const imageRef = useRef(null);
    const containerRef = useRef(null);
    const pageContainerRef = useRef(null);

    const [imageDataUrl, setImageDataUrl] = useState(null);
    const [blankCanvas, setBlankCanvas] = useState(false);
    /** When an image is uploaded, false hides the photo and shows the blank grid surface. */
    const [showUploadedImage, setShowUploadedImage] = useState(true);
    const [showGraphGrid, setShowGraphGrid] = useState(true);
    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
    const layoutSurfaceActive = Boolean(imageDataUrl) || blankCanvas;
    const [canvasSize, setCanvasSize] = useState({ width: 1, height: 1 });
    const [mode, setMode] = useState(MODES.PLACE);
    const [markers, setMarkers] = useState([]); // {id, x, y, binDataId?, radiusPx? (image px)}
    const [opencvReady, setOpencvReady] = useState(
        () => typeof window !== "undefined" && !!window.__siteLayoutOpenCv
    );
    const [selectedMarkerId, setSelectedMarkerId] = useState(null);
    const [selectedMarkerIds, setSelectedMarkerIds] = useState([]);
    /** First bin selected in the group — reference for align X/Y and same size. */
    const [groupAnchorMarkerId, setGroupAnchorMarkerId] = useState(null);
    /** First flat house selected in the group — reference for align X/Y and same size. */
    const [groupAnchorFlatRectId, setGroupAnchorFlatRectId] = useState(null);
    const [selectedSegmentIds, setSelectedSegmentIds] = useState([]);
    const [selectionRect, setSelectionRect] = useState(null);
    const [historyTick, setHistoryTick] = useState(0);
    const [feetPerPixel, setFeetPerPixel] = useState(PIXEL_TO_FEET_DEFAULT);
    const [clusterEpsFt, setClusterEpsFt] = useState(40); // groups split when bins are farther than this
    const [forceUpdate, setForceUpdate] = useState(0);
    const [busy, setBusy] = useState(false);
    const [detectBusy, setDetectBusy] = useState(false);
    /** Walls/arcs between bin snap points (global list). */
    const [layoutSegments, setLayoutSegments] = useState([]);

    useEffect(() => {
        layoutSegmentsRef.current = layoutSegments;
    }, [layoutSegments]);
    /** Enclosed face id → flat binDataId or null (unassigned). */
    const [faceAssignments, setFaceAssignments] = useState({});
    /** Optional display names for enclosed areas (keyed by face id). */
    const [faceLabels, setFaceLabels] = useState({});
    const [markerLabels, setMarkerLabels] = useState({});
    /** Stable Area A/B/… per marker id — survives enclosure refresh. */
    const [markerAreaNames, setMarkerAreaNames] = useState({});
    const markerAreaNamesRef = useRef({});
    const [faceAreaNamesByRectId, setFaceAreaNamesByRectId] = useState({});
    const faceAreaNamesByRectIdRef = useRef({});
    const [selectedFaceId, setSelectedFaceId] = useState(null);
    const [selectedSegmentId, setSelectedSegmentId] = useState(null);
    const [sticeHoverSnap, setSticeHoverSnap] = useState(null);
    /** Wall/Arc mode: crosshair on snap hit, open-hand (grab) otherwise. */
    const [wallArcCursor, setWallArcCursor] = useState("grab");
    const wallArcCursorRef = useRef("grab");
    /** Image-space point for extended crosshairs while dragging bins (place or group). */
    const [moveGuidePoint, setMoveGuidePoint] = useState(null);
    const [sticeFreePoint, setSticeFreePoint] = useState(null);
    const [viewZoom, setViewZoom] = useState(DEFAULT_VIEW_ZOOM);
    const [viewPan, setViewPan] = useState({ x: 0, y: 0 });
    const baseCanvasSizeRef = useRef({ width: 1, height: 1 });
    const prevBaseCanvasSizeRef = useRef({ width: 0, height: 0 });
    const viewportRef = useRef(null);
    const viewportInitRef = useRef(false);
    const sidebarRef = useRef(null);
    const panDragRef = useRef(null);
    const canvasPanWrapperRef = useRef(null);
    const viewPanLiveRef = useRef({ x: 0, y: 0 });
    const viewZoomRef = useRef(DEFAULT_VIEW_ZOOM);
    /** Bitmap zoom committed on canvas; CSS scale bridges preview zoom. */
    const committedViewZoomRef = useRef(DEFAULT_VIEW_ZOOM);
    const zoomCommitTimerRef = useRef(null);
    /** Batched +/− zoom clicks → one canvas resize per animation frame. */
    const zoomButtonStepsRef = useRef(0);
    const zoomButtonRafRef = useRef(null);
    const wheelZoomAccumRef = useRef(0);
    const wheelZoomClientRef = useRef({ x: null, y: null });
    const wheelZoomRafRef = useRef(null);
    /** True while wheel/button zoom uses CSS scale before bitmap commit. */
    const zoomPreviewActiveRef = useRef(false);
    /** True while viewport pan uses live refs (no React viewPan / zoom commit). */
    const panActiveRef = useRef(false);
    const pendingZoomCommitAfterPanRef = useRef(false);
    const isPanningRef = useRef(false);
    const panMoveRafRef = useRef(null);
    /** Bumped to cancel deferred viewPan → React sync after pan. */
    const viewPanSyncGenRef = useRef(0);
    const viewPanSyncTimerRef = useRef(null);
    const layoutBgCacheRef = useRef(null);
    const canvasInteractingRef = useRef(false);
    const layoutPaintDataRef = useRef({
        markers: [],
        layoutSegments: [],
        enclosedFaces: [],
        faceAssignments: {},
        faceLabels: {},
        markerLabels: {},
        imageDataUrl: null,
        showUploadedImage: true,
        blankCanvas: false,
        showGraphGrid: true,
        feetPerPixel: 0,
        imageSize: BLANK_LAYOUT_SIZE,
        siteBins: [],
        siteBinByDataId: new Map(),
        layoutAreaSlotByMarkerId: new Map(),
        markerClusterAssignments: [],
        layoutHeavyGeometry: false,
    });
    const canvasSelectionRef = useRef({
        selectedMarkerId: null,
        selectedMarkerIds: [],
        selectedFaceId: null,
        selectedSegmentId: null,
        selectedSegmentIds: [],
        mode: null,
        flatHandleRectId: null,
        flatSelectedRectId: null,
        selectionRect: null,
    });
    /** Pending click vs drag on empty canvas (pan if moved, else place bin in PLACE mode). */
    const emptyDragRef = useRef(null);
    const spaceHeldRef = useRef(false);
    const [spaceHeld, setSpaceHeld] = useState(false);
    const [statusMessage, setStatusMessage] = useState("");
    const [preview3dOpen, setPreview3dOpen] = useState(false);
    const [previewDraft, setPreviewDraft] = useState(null);
    /** 2D viewport zoom/pan snapshot when Preview opens (matches 3D camera). */
    const [previewEditorView, setPreviewEditorView] = useState(null);
    const [diameterDrafts, setDiameterDrafts] = useState({});
    const [editingDiameterMarkerId, setEditingDiameterMarkerId] = useState(null);
    const [faceDimensionDrafts, setFaceDimensionDrafts] = useState({});
    const [editingFaceDimensionsId, setEditingFaceDimensionsId] = useState(null);
    const [sitesLoading, setSitesLoading] = useState(false);
    const [siteLoading, setSiteLoading] = useState(false);
    /** True after DB/session restore attempt finishes (drives initial Site 3D grid import). */
    const [editorRestoreComplete, setEditorRestoreComplete] = useState(false);
    const dragRef = useRef(null);
    const resizeRef = useRef(null);
    /** True while a pointer (touch/pen/mouse) is captured on the layout canvas. */
    const layoutPointerActiveRef = useRef(false);
    const sticeDrawRef = useRef(null); // { kind:'wall'|'arc', from: snap, to?: snap }
    const flatEditRef = useRef(null); // move | resize of placed flat house
    /** Defer flat move until pointer moves — short drag pans instead. */
    const flatPendingDragRef = useRef(null);
    /** Drives corner-handle repaint while editing (refs alone do not redraw the canvas). */
    const [flatHandleRectId, setFlatHandleRectId] = useState(null);
    const layoutSegmentsRef = useRef([]);
    const groupDragRef = useRef(null);
    /** While true, skip computeEnclosedFaces until mouse up (avoids OOM during drag). */
    const layoutGeometryBusyRef = useRef(false);
    /** Last counts after a successful enclosure run (bin-only add detection). */
    const layoutGeometryCountsRef = useRef({ markers: 0, segments: 0 });
    /** Newly placed markers for canvas paint before transition render commits. */
    const pendingPlacedMarkersRef = useRef(null);
    const layoutAreaSlotsCacheRef = useRef([]);
    const deferredMarkerEnclosureTimerRef = useRef(null);
    const flatEnclosureDebounceRef = useRef(null);
    const flatFacesStateDebounceRef = useRef(null);
    const groupAnchorFlatRectIdRef = useRef(null);
    const placeHistoryIdleRef = useRef(null);
    const paintRafRef = useRef(null);
    const sticeHoverSnapRef = useRef(null);
    const sticeFreePointRef = useRef(null);
    const moveGuidePointRef = useRef(null);
    const selectionRectLiveRef = useRef(null);
    /** Live geometry during drag — avoids setState per mousemove. */
    const interactionMarkersRef = useRef(null);
    const interactionSegmentsRef = useRef(null);
    /** Arrow-key nudge batching (one history entry + rAF updates per key hold). */
    const nudgePendingRef = useRef({ dx: 0, dy: 0 });
    const nudgeRafRef = useRef(null);
    const nudgeHistoryRecordedRef = useRef(false);
    const nudgeArrowKeysHeldRef = useRef(0);
    /** Live geometry during arrow nudge — canvas only until keyup (avoids React churn). */
    const nudgeLiveMarkersRef = useRef(null);
    const nudgeLiveSegmentsRef = useRef(null);
    /** Latest selection/mode for stable window keyboard listeners. */
    const keyboardNudgeCtxRef = useRef({});
    /** One auto Site 3D grid import per site open when no draft and no persisted layout. */
    const autoGridAppliedForGuidRef = useRef(null);
    const autoBlankLayoutForGuidRef = useRef(null);

    const clearPlaceModeSelection = useCallback(() => {
        setSelectedMarkerId(null);
        setSelectedMarkerIds([]);
        setGroupAnchorMarkerId(null);
        setGroupAnchorFlatRectId(null);
        setSelectedFaceId(null);
        setSelectedSegmentId(null);
        setSelectedSegmentIds([]);
        setEditingDiameterMarkerId(null);
        setFlatHandleRectId(null);
    }, []);
    const marqueeRef = useRef(null);
    /** Right-click on object without drag — toggle group membership on mouseup. */
    const groupRightPickRef = useRef(null);
    const undoStackRef = useRef([]);
    const layoutHistorySiteGuidRef = useRef("");
    const redoStackRef = useRef([]);
    const skipHistoryRef = useRef(false);
    const layoutLiveRef = useRef({});
    const [pickerSiteGuid, setPickerSiteGuid] = useState(() => {
        const restored = global.restoreSelectedSite?.();
        return global.gw?.selectedSite?.siteGuid ?? restored?.siteGuid ?? "";
    });
    const [siteSetupOpen, setSiteSetupOpen] = useState(() => {
        const site = global.gw?.selectedSite;
        return !(site?.siteGuid && site.isSiteInitialized);
    });
    const confirmResolveRef = useRef(null);
    const [confirmDialog, setConfirmDialog] = useState(null);

    const openConfirmDialog = useCallback(
        ({ title, message, confirmLabel = "OK", cancelLabel = "Cancel" }) =>
            new Promise((resolve) => {
                confirmResolveRef.current = resolve;
                setConfirmDialog({ title, message, confirmLabel, cancelLabel });
            }),
        []
    );

    const closeConfirmDialog = useCallback((confirmed) => {
        setConfirmDialog(null);
        const resolve = confirmResolveRef.current;
        confirmResolveRef.current = null;
        resolve?.(confirmed);
    }, []);

    const siteOptions = useMemo(() => {
        const list = (global.gw?.siteInfos ?? [])
            .filter(s => s?.siteGuid && !s.isHms)
            .map(s => ({
                siteGuid: s.siteGuid,
                siteName: s.siteName || s.siteGuid,
                siteIndex: s.siteIndex,
                lastScan: s.lastScan,
            }));
        return list.sort((a, b) =>
            String(a.siteName).localeCompare(String(b.siteName), undefined, { sensitivity: "base" })
        );
    }, [forceUpdate, sitesLoading]);

    const activeSiteGuid = global.gw?.selectedSite?.siteGuid ?? pickerSiteGuid;

    const siteBins = useMemo(() => {
        if (!activeSiteGuid || !global.gw?.selectedSite?.isSiteInitialized) return [];
        const fromInit = (global.gw?.selectedSite?.bins ?? [])
            .map(mapBinRowForLayoutAssignment)
            .filter(Boolean);
        if (fromInit.length > 0) return fromInit;
        return layoutBinsFromSiteInfos(activeSiteGuid);
    }, [forceUpdate, activeSiteGuid, siteLoading]);

    const siteBinByDataId = useMemo(() => {
        const map = new Map();
        siteBins.forEach((b) => {
            if (b?.binDataId != null) {
                map.set(String(b.binDataId), b);
            }
        });
        return map;
    }, [siteBins]);

    const layoutObjectCount = markers.length + layoutSegments.length;
    const layoutHeavyGeometry = layoutObjectCount >= 8;

    const paintLayoutCanvasRef = useRef(() => {});
    const scheduleLayoutPaint = useCallback(() => {
        if (paintRafRef.current != null) return;
        paintRafRef.current = requestAnimationFrame(() => {
            paintRafRef.current = null;
            paintLayoutCanvasRef.current();
        });
    }, []);

    const applyCanvasWrapperTransform = useCallback(() => {
        const el = canvasPanWrapperRef.current;
        if (!el) return;
        const pan = viewPanLiveRef.current;
        const committed = committedViewZoomRef.current;
        const scale =
            committed > 0 ? viewZoomRef.current / committed : 1;
        el.style.transformOrigin = "0 0";
        el.style.transform = `translate(${pan.x}px, ${pan.y}px) scale(${scale})`;
    }, []);

    const restoreLayoutViewportCursors = useCallback(() => {
        const vp = viewportRef.current;
        const canvas = canvasRef.current;
        if (vp) {
            vp.style.cursor = spaceHeldRef.current ? "grab" : "";
        }
        if (canvas) {
            canvas.style.cursor = spaceHeldRef.current
                ? "grab"
                : mode === MODES.WALL || mode === MODES.ARC
                  ? wallArcCursorRef.current
                  : "crosshair";
        }
    }, [mode]);

    const applyPanGrabCursor = useCallback(
        (grabbing) => {
            isPanningRef.current = grabbing;
            const vp = viewportRef.current;
            const canvas = canvasRef.current;
            if (grabbing) {
                if (vp) vp.style.cursor = "grabbing";
                if (canvas) canvas.style.cursor = "grabbing";
            } else {
                restoreLayoutViewportCursors();
            }
        },
        [restoreLayoutViewportCursors]
    );

    const schedulePanTransform = useCallback(() => {
        if (panMoveRafRef.current != null) return;
        panMoveRafRef.current = requestAnimationFrame(() => {
            panMoveRafRef.current = null;
            applyCanvasWrapperTransform();
        });
    }, [applyCanvasWrapperTransform]);

    const cancelViewPanStateSync = useCallback(() => {
        viewPanSyncGenRef.current += 1;
        if (viewPanSyncTimerRef.current != null) {
            clearTimeout(viewPanSyncTimerRef.current);
            viewPanSyncTimerRef.current = null;
        }
    }, []);

    const scheduleViewPanStateSync = useCallback(() => {
        cancelViewPanStateSync();
        const gen = viewPanSyncGenRef.current;
        viewPanSyncTimerRef.current = setTimeout(() => {
            viewPanSyncTimerRef.current = null;
            if (gen !== viewPanSyncGenRef.current) return;
            if (panActiveRef.current || zoomPreviewActiveRef.current) return;
            const p = viewPanLiveRef.current;
            setViewPan((prev) => {
                if (prev.x === p.x && prev.y === p.y) return prev;
                return { x: p.x, y: p.y };
            });
        }, VIEW_PAN_SYNC_DELAY_MS);
    }, [cancelViewPanStateSync]);

    /** Commit live pan to React state (e.g. before save) without moving the canvas. */
    const flushViewportPanFromLive = useCallback(() => {
        cancelViewPanStateSync();
        const p = viewPanLiveRef.current;
        viewPanLiveRef.current = p;
        setViewPan((prev) => {
            if (prev.x === p.x && prev.y === p.y) return prev;
            return { x: p.x, y: p.y };
        });
        applyCanvasWrapperTransform();
    }, [cancelViewPanStateSync, applyCanvasWrapperTransform]);

    const beginPanGesture = useCallback(() => {
        cancelViewPanStateSync();
        if (zoomCommitTimerRef.current) {
            clearTimeout(zoomCommitTimerRef.current);
            zoomCommitTimerRef.current = null;
        }
        panActiveRef.current = true;
        canvasInteractingRef.current = true;
        applyPanGrabCursor(true);
    }, [applyPanGrabCursor, cancelViewPanStateSync]);

    const syncCanvasSelection = useCallback(
        (patch) => {
            Object.assign(canvasSelectionRef.current, patch);
            scheduleLayoutPaint();
        },
        [scheduleLayoutPaint]
    );

    useEffect(() => {
        if (zoomPreviewActiveRef.current || panActiveRef.current) {
            applyCanvasWrapperTransform();
            return;
        }
        if (viewPanSyncTimerRef.current != null) {
            applyCanvasWrapperTransform();
            return;
        }
        viewPanLiveRef.current = viewPan;
        applyCanvasWrapperTransform();
    }, [viewPan, applyCanvasWrapperTransform]);

    useEffect(() => {
        if (zoomPreviewActiveRef.current) {
            applyCanvasWrapperTransform();
            return;
        }
        viewZoomRef.current = viewZoom;
        committedViewZoomRef.current = viewZoom;
        applyCanvasWrapperTransform();
    }, [viewZoom, applyCanvasWrapperTransform]);

    useEffect(
        () => () => {
            if (wheelZoomRafRef.current != null) {
                cancelAnimationFrame(wheelZoomRafRef.current);
            }
            if (panMoveRafRef.current != null) {
                cancelAnimationFrame(panMoveRafRef.current);
            }
            if (zoomCommitTimerRef.current) clearTimeout(zoomCommitTimerRef.current);
            if (viewPanSyncTimerRef.current) clearTimeout(viewPanSyncTimerRef.current);
            viewPanSyncGenRef.current += 1;
        },
        []
    );

    const commitInteractionGeometry = useCallback(() => {
        if (interactionMarkersRef.current) {
            setMarkers(interactionMarkersRef.current);
            interactionMarkersRef.current = null;
        }
        if (interactionSegmentsRef.current) {
            setLayoutSegments(interactionSegmentsRef.current);
            layoutSegmentsRef.current = interactionSegmentsRef.current;
            interactionSegmentsRef.current = null;
        }
    }, []);

    const markerClusterAssignments = useMemo(() => {
        if (!markers.length) return [];
        const epsPx =
            clusterEpsFt > 0 && feetPerPixel > 0 ? clusterEpsFt / feetPerPixel : 80;
        return clusterMarkers(markers, epsPx);
    }, [markers, clusterEpsFt, feetPerPixel]);

    const siteReady = Boolean(
        activeSiteGuid && global.gw?.selectedSite?.isSiteInitialized
    );

    /** Admin menu → Site Layout: always show site picker (not skip when a site is already loaded). */
    useEffect(() => {
        if (!location.state?.openSitePicker) return;
        global.clearSiteLayoutReturn?.();
        setPickerSiteGuid("");
        setSiteSetupOpen(true);
        navigate("/siteLayout", { replace: true, state: {} });
    }, [location.state?.openSitePicker, navigate]);

    useEffect(() => {
        const handleThemeChange = () => setForceUpdate(p => p + 1);
        window.addEventListener("themeChanged", handleThemeChange);
        return () => window.removeEventListener("themeChanged", handleThemeChange);
    }, []);

    layoutLiveRef.current = {
        siteGuid: activeSiteGuid,
        markers,
        layoutSegments,
        faceAssignments,
        faceLabels,
        markerLabels,
        markerAreaNames,
        viewPan: viewPanLiveRef.current,
        viewZoom: viewZoomRef.current,
    };

    groupAnchorFlatRectIdRef.current = groupAnchorFlatRectId;

    markerAreaNamesRef.current = markerAreaNames;

    const applyLayoutSnapshot = useCallback((snap) => {
        skipHistoryRef.current = true;
        const nextMarkers = snap.markers ?? [];
        const nextSegments = snap.layoutSegments ?? [];
        pendingPlacedMarkersRef.current = null;
        const resetAreaNames = snap.markerAreaNames ?? {};
        markerAreaNamesRef.current = resetAreaNames;
        setMarkerAreaNames(resetAreaNames);
        layoutGeometryCountsRef.current = {
            markers: nextMarkers.length,
            segments: nextSegments.length,
        };
        setMarkers(nextMarkers);
        setLayoutSegments(nextSegments);
        setFaceAssignments(snap.faceAssignments ?? {});
        setFaceLabels(snap.faceLabels ?? {});
        setMarkerLabels(snap.markerLabels ?? {});
        setMarkerAreaNames(snap.markerAreaNames ?? {});
        const pan = snap.viewPan ?? { x: 0, y: 0 };
        const zoom = snap.viewZoom ?? 1;
        cancelViewPanStateSync();
        viewPanLiveRef.current = pan;
        viewZoomRef.current = zoom;
        committedViewZoomRef.current = zoom;
        setViewPan(pan);
        setViewZoom(zoom);
        setSelectedMarkerIds([]);
        setGroupAnchorMarkerId(null);
        setSelectedSegmentIds([]);
        setSelectedMarkerId(null);
        setSelectedSegmentId(null);
        setSelectedFaceId(null);
        skipHistoryRef.current = false;
    }, []);

    useEffect(() => {
        setGroupAnchorMarkerId((anchor) => {
            if (selectedMarkerIds.length === 0) return null;
            if (anchor && selectedMarkerIds.includes(anchor)) return anchor;
            return selectedMarkerIds[0];
        });
    }, [selectedMarkerIds]);

    const resetLayoutHistory = useCallback((siteGuid) => {
        undoStackRef.current = [];
        redoStackRef.current = [];
        if (siteGuid != null && siteGuid !== "") {
            layoutHistorySiteGuidRef.current =
                normalizeLayoutHistorySiteGuid(siteGuid);
        }
        setHistoryTick((t) => t + 1);
    }, []);

    const recordHistory = useCallback(() => {
        if (skipHistoryRef.current) return;
        const siteGuid = normalizeLayoutHistorySiteGuid(activeSiteGuid);
        layoutHistorySiteGuidRef.current = siteGuid;
        undoStackRef.current.push(
            cloneLayoutSnapshot({ ...layoutLiveRef.current, siteGuid })
        );
        if (undoStackRef.current.length > MAX_LAYOUT_UNDO) undoStackRef.current.shift();
        redoStackRef.current = [];
        setHistoryTick((t) => t + 1);
    }, [activeSiteGuid]);

    const cancelArrowNudgeSessionWithoutCommit = useCallback(() => {
        if (nudgeRafRef.current != null) {
            cancelAnimationFrame(nudgeRafRef.current);
            nudgeRafRef.current = null;
        }
        nudgePendingRef.current = { dx: 0, dy: 0 };
        nudgeLiveMarkersRef.current = null;
        nudgeLiveSegmentsRef.current = null;
        nudgeHistoryRecordedRef.current = false;
        nudgeArrowKeysHeldRef.current = 0;
        if (
            !interactionMarkersRef.current &&
            !interactionSegmentsRef.current &&
            !groupDragRef.current &&
            !dragRef.current &&
            !flatEditRef.current
        ) {
            layoutGeometryBusyRef.current = false;
        }
    }, []);

    const handleUndo = useCallback(() => {
        cancelArrowNudgeSessionWithoutCommit();
        const activeNorm = normalizeLayoutHistorySiteGuid(activeSiteGuid);
        while (undoStackRef.current.length > 0) {
            const peek =
                undoStackRef.current[undoStackRef.current.length - 1];
            const peekNorm = normalizeLayoutHistorySiteGuid(peek.siteGuid);
            if (!activeNorm || !peekNorm || peekNorm === activeNorm) break;
            undoStackRef.current.pop();
        }
        if (!undoStackRef.current.length) {
            setStatusMessage("Nothing to undo for this site.");
            return;
        }
        const prev = undoStackRef.current.pop();
        const prevNorm = normalizeLayoutHistorySiteGuid(prev.siteGuid);
        if (activeNorm && prevNorm && prevNorm !== activeNorm) {
            setStatusMessage("Undo unavailable — history is from another site.");
            return;
        }
        redoStackRef.current.push(
            cloneLayoutSnapshot({ ...layoutLiveRef.current, siteGuid: activeNorm })
        );
        applyLayoutSnapshot(prev);
        setHistoryTick((t) => t + 1);
        setStatusMessage("Undo.");
    }, [applyLayoutSnapshot, activeSiteGuid, cancelArrowNudgeSessionWithoutCommit]);

    const handleRedo = useCallback(() => {
        cancelArrowNudgeSessionWithoutCommit();
        const activeNorm = normalizeLayoutHistorySiteGuid(activeSiteGuid);
        while (redoStackRef.current.length > 0) {
            const peek =
                redoStackRef.current[redoStackRef.current.length - 1];
            const peekNorm = normalizeLayoutHistorySiteGuid(peek.siteGuid);
            if (!activeNorm || !peekNorm || peekNorm === activeNorm) break;
            redoStackRef.current.pop();
        }
        if (!redoStackRef.current.length) {
            setStatusMessage("Nothing to redo for this site.");
            return;
        }
        const next = redoStackRef.current.pop();
        const nextNorm = normalizeLayoutHistorySiteGuid(next.siteGuid);
        if (activeNorm && nextNorm && nextNorm !== activeNorm) {
            setStatusMessage("Redo unavailable — history is from another site.");
            return;
        }
        undoStackRef.current.push(
            cloneLayoutSnapshot({ ...layoutLiveRef.current, siteGuid: activeNorm })
        );
        applyLayoutSnapshot(next);
        setHistoryTick((t) => t + 1);
        setStatusMessage("Redo.");
    }, [applyLayoutSnapshot, activeSiteGuid, cancelArrowNudgeSessionWithoutCommit]);

    const undoStackLen = undoStackRef.current.length;
    const redoStackLen = redoStackRef.current.length;
    void historyTick;

    // Ensure site list is available (user may open Site Layout without visiting /sites first).
    useEffect(() => {
        let cancelled = false;
        const loadSites = async () => {
            if ((global.gw?.siteInfos?.length ?? 0) > 0) {
                setForceUpdate(p => p + 1);
                return;
            }
            setSitesLoading(true);
            try {
                const response = await createApiEndpoint().fetchUserSites("direct");
                if (cancelled) return;
                if (response?.data?.fetchUserAttribute?.sites) {
                    // CustomerSites builds siteInfos from the same payload; if we're here alone,
                    // mirror the minimal fields we need for the picker.
                    const sites = response.data.fetchUserAttribute.sites;
                    if (!global.gw.siteInfos?.length) {
                        global.gw.siteInfos = sites.map((_, siteIndex) => ({
                            siteIndex,
                            siteGuid: _.siteGuid,
                            siteName: _.main?.siteName ?? "Site",
                            lastScan: _.lastScan,
                            taskSlots: Array.isArray(_.taskSlots) ? _.taskSlots : [],
                            isHms: _.isHms,
                            bins: _.bins ?? [],
                        })).filter(s => s.siteGuid && !s.isHms);
                    }
                }
            } catch {
                if (!cancelled) {
                    setStatusMessage("Could not load site list. Try opening Customer Sites first.");
                }
            } finally {
                if (!cancelled) {
                    setSitesLoading(false);
                    setForceUpdate(p => p + 1);
                }
            }
        };
        loadSites();
        return () => { cancelled = true; };
    }, []);

    const selectSite = useCallback(async (siteGuid, options = {}) => {
        if (!siteGuid) return;
        const { preserveEditor = false } = options;
        const info = (global.gw?.siteInfos ?? []).find(
            s => global.normalizeGuidForCompare(s.siteGuid) === global.normalizeGuidForCompare(siteGuid)
        );
        if (!info) {
            setStatusMessage("Site not found in your site list.");
            return;
        }

        setPickerSiteGuid(siteGuid);
        const normSite = normalizeLayoutHistorySiteGuid(siteGuid);
        if (normSite && normSite !== layoutHistorySiteGuidRef.current) {
            resetLayoutHistory(siteGuid);
        }
        setSiteLoading(true);
        setStatusMessage(preserveEditor ? "Restoring site layout…" : "Loading site bins…");
        didRestoreSessionRef.current = false;
        sessionHydratedRef.current = false;
        editorRestoreCompleteRef.current = false;
        setEditorRestoreComplete(false);
        autoGridAppliedForGuidRef.current = null;
        autoBlankLayoutForGuidRef.current = null;
        if (!preserveEditor) {
            clearPlaceModeSelection();
            setMarkers([]);
            setLayoutSegments([]);
            setFaceAssignments({});
            setFaceLabels({});
            setMarkerLabels({});
            setImageDataUrl(null);
            setBlankCanvas(false);
            setImageSize({ width: 0, height: 0 });
        }

        if (global.gw.selectedSite?.refreshId) {
            clearTimeout(global.gw.selectedSite.refreshId);
        }

        global.gw.selectedSite = new global.Site();
        global.gw.selectedSite.siteIndex = info.siteIndex;
        global.gw.selectedSite.siteGuid = info.siteGuid;
        global.gw.selectedSite.siteName = info.siteName;
        global.gw.selectedSite.lastScan = info.lastScan;
        global.gw.selectedSite.isSiteInitialized = false;
        global.saveSelectedSite();

        const initWithTimeout = (timeoutMs) =>
            Promise.race([
                Promise.resolve()
                    .then(() => global.initializeCustomerSite(navigate))
                    .catch((e) => {
                        console.error("initializeCustomerSite failed", e);
                    }),
                new Promise((resolve) =>
                    setTimeout(() => resolve("__timeout__"), timeoutMs)
                ),
            ]);

        try {
            const result = await initWithTimeout(20000);
            if (result === "__timeout__") {
                global.gw.selectedSite.isSiteInitialized = false;
                global.gw.selectedSite.bins = global.gw.selectedSite.bins || [];
                const fallbackBins = hydrateSelectedSiteBinsFromSiteInfo(siteGuid);
                setStatusMessage(
                    fallbackBins > 0
                        ? `Site fetch timed out; loaded ${fallbackBins} bins from site list for assignment.`
                        : "Site fetch timed out. You can still draw on a blank canvas; open Customer Sites first if bin list is empty."
                );
            } else {
                let binCount = global.gw?.selectedSite?.bins?.length ?? 0;
                if (binCount === 0) {
                    global.gw.selectedSite.isSiteInitialized = false;
                    binCount = hydrateSelectedSiteBinsFromSiteInfo(siteGuid);
                }
                if (binCount === 0) {
                    setStatusMessage(
                        "Site loaded but no bins were returned. Open Customer Sites once, then retry, or check BTS for this site."
                    );
                } else {
                    setStatusMessage(
                        `Loaded ${binCount} bins for ${info.siteName}.`
                    );
                }
            }
        } catch (e) {
            global.gw.selectedSite.isSiteInitialized = false;
            setStatusMessage(`Failed to load site: ${e?.message ?? e}`);
        } finally {
            if (!preserveEditor) {
                const bins = global.gw?.selectedSite?.bins ?? [];
                setFeetPerPixel(defaultLayoutFeetPerPixel(bins));
            }
            setSiteLoading(false);
            setForceUpdate(p => p + 1);
        }
    }, [navigate, clearPlaceModeSelection, resetLayoutHistory]);

    const didRestoreSessionRef = useRef(false);
    /** Session-first editor restore after returning from production Site 3D. */
    const pendingSite3dEditorRestoreRef = useRef(false);
    /** Blocks autosave until session/DB restore finishes (avoids overwriting walls with empty state). */
    const sessionHydratedRef = useRef(false);
    const editorRestoreCompleteRef = useRef(false);
    /** Skip pruning unsnapped walls immediately after restore. */
    const skipPruneUntilRef = useRef(0);
    const siteLayoutReturnHandledRef = useRef(false);
    const loadSiteForLayoutHandledRef = useRef(false);

    // Customer Sites site card → Layout: open this site directly (skip picker).
    useEffect(() => {
        if (loadSiteForLayoutHandledRef.current) return;
        if (!location.state?.loadSiteForLayout) return;
        const siteGuid =
            location.state?.siteGuid ??
            global.gw?.selectedSite?.siteGuid ??
            pickerSiteGuid;
        if (!siteGuid) return;
        loadSiteForLayoutHandledRef.current = true;

        setSiteSetupOpen(false);
        setPickerSiteGuid(siteGuid);
        navigate("/siteLayout", { replace: true, state: {} });

        const sameSite =
            global.normalizeGuidForCompare(global.gw?.selectedSite?.siteGuid) ===
            global.normalizeGuidForCompare(siteGuid);
        const siteLoaded =
            sameSite && global.gw?.selectedSite?.isSiteInitialized;

        if (!siteLoaded) {
            selectSite(siteGuid);
        }
    }, [
        location.state?.loadSiteForLayout,
        location.state?.siteGuid,
        navigate,
        pickerSiteGuid,
        selectSite,
    ]);

    // Returning from Site 3D preview: reopen the same site and editor (not the site picker).
    useEffect(() => {
        if (siteLayoutReturnHandledRef.current) return;
        const ret = global.peekSiteLayoutReturn?.();
        if (!ret?.siteGuid) return;
        siteLayoutReturnHandledRef.current = true;

        setPickerSiteGuid(ret.siteGuid);
        setSiteSetupOpen(false);

        const sameSite = global.normalizeGuidForCompare(global.gw?.selectedSite?.siteGuid)
            === global.normalizeGuidForCompare(ret.siteGuid);
        const siteLoaded = sameSite
            && global.gw?.selectedSite?.isSiteInitialized
            && (global.gw?.selectedSite?.bins?.length ?? 0) > 0;

        if (siteLoaded) {
            pendingSite3dEditorRestoreRef.current = true;
            didRestoreSessionRef.current = false;
            global.clearSiteLayoutReturn();
            return;
        }

        selectSite(ret.siteGuid, { preserveEditor: true }).finally(() => {
            pendingSite3dEditorRestoreRef.current = true;
            didRestoreSessionRef.current = false;
            global.clearSiteLayoutReturn();
        });
    }, [selectSite]);

    const applyEditorSessionPayload = useCallback(
        (saved, statusMsg) => {
            const payload = normalizeEditorDraftPayload(saved);
            if (!payload) return false;

            let markersToApply = payload.markers ?? [];
            let segmentsToApply = payload.layoutSegments?.length
                ? payload.layoutSegments
                : payload.stices?.length
                  ? flattenLayoutSegments(payload.stices)
                  : [];

            const isBlankLayout =
                !payload.imageDataUrl &&
                (payload.blankCanvas ||
                    markersToApply.length > 0 ||
                    segmentsToApply.length > 0);

            let blankImageSize =
                payload.blankImageSize?.width > 0 &&
                payload.blankImageSize?.height > 0
                    ? payload.blankImageSize
                    : { ...BLANK_LAYOUT_SIZE };

            if (
                isBlankLayout &&
                (markersToApply.length > 0 || segmentsToApply.length > 0)
            ) {
                const fitted = fitBlankLayoutCanvasSize(
                    markersToApply,
                    segmentsToApply,
                    {
                        minWidth: blankImageSize.width,
                        minHeight: blankImageSize.height,
                        preservePositionWhenFits: true,
                    }
                );
                markersToApply = fitted.markers;
                segmentsToApply = fitted.layoutSegments;
                blankImageSize = {
                    width: fitted.width,
                    height: fitted.height,
                };
            }

            if (markersToApply.length) setMarkers(markersToApply);
            if (segmentsToApply.length) setLayoutSegments(segmentsToApply);
            if (payload.faceAssignments) setFaceAssignments(payload.faceAssignments);
            if (payload.faceLabels) setFaceLabels(payload.faceLabels);
            if (payload.markerLabels) setMarkerLabels(payload.markerLabels);
            if (payload.markerAreaNames) {
                markerAreaNamesRef.current = payload.markerAreaNames;
                setMarkerAreaNames(payload.markerAreaNames);
            }
            if (payload.feetPerPixel > 0) {
                const legacyGraphDefault =
                    Math.abs(payload.feetPerPixel - 1) < 0.0001;
                setFeetPerPixel(
                    legacyGraphDefault
                        ? defaultLayoutFeetPerPixel(siteBins)
                        : payload.feetPerPixel
                );
            }
            if (payload.clusterEpsFt != null) setClusterEpsFt(payload.clusterEpsFt);
            if (payload.mode) {
                const restored =
                    payload.mode === "calibrate" || payload.mode === "selectGroup"
                        ? MODES.PLACE
                        : payload.mode;
                if (Object.values(MODES).includes(restored)) {
                    setMode(restored);
                }
            }
            if (isBlankLayout) {
                setBlankCanvas(true);
                setImageSize(blankImageSize);
            }
            if (payload.imageDataUrl) {
                setBlankCanvas(false);
                setImageDataUrl(payload.imageDataUrl);
                const img = new Image();
                img.onload = () => {
                    imageRef.current = img;
                    setImageSize({
                        width: img.naturalWidth,
                        height: img.naturalHeight,
                    });
                };
                img.src = payload.imageDataUrl;
            }
            setEnclosureRequestId((id) => id + 1);
            clearPlaceModeSelection();
            resetLayoutHistory(activeSiteGuid);
            if (statusMsg) setStatusMessage(statusMsg);
            return true;
        },
        [siteBins, clearPlaceModeSelection, activeSiteGuid, resetLayoutHistory]
    );

    const runEditorRestoreFromStorage = useCallback(
        async (siteGuid, { preferSession = false } = {}) => {
            if (!siteGuid) return false;

            sessionHydratedRef.current = false;
            editorRestoreCompleteRef.current = false;
            setEditorRestoreComplete(false);

            const finishRestore = (payload, statusMsg) => {
                const applied = applyEditorSessionPayload(payload, statusMsg);
                if (applied) {
                    skipPruneUntilRef.current = Date.now() + 2500;
                }
                editorRestoreCompleteRef.current = true;
                sessionHydratedRef.current = true;
                setEditorRestoreComplete(true);
                return applied;
            };

            const finishEmpty = () => {
                editorRestoreCompleteRef.current = true;
                sessionHydratedRef.current = true;
                setEditorRestoreComplete(true);
            };

            const trySession = () =>
                finishRestore(
                    normalizeEditorDraftPayload(loadSiteLayoutSession(siteGuid)),
                    "Restored your site layout work from this browser."
                );

            const tryDb = async () => {
                const fromDb = await createApiEndpoint().fetchSiteLayoutEditorDraft(
                    "direct",
                    siteGuid
                );
                const dbPayload = normalizeEditorDraftPayload(fromDb);
                if (!dbPayload) return false;
                delete dbPayload.imageDataUrl;
                delete dbPayload.imageOmitted;
                return finishRestore(
                    dbPayload,
                    "Restored site layout from saved editor draft."
                );
            };

            try {
                if (preferSession) {
                    if (trySession()) return true;
                    if (await tryDb()) return true;
                } else {
                    if (await tryDb()) return true;
                    if (trySession()) return true;
                }
                finishEmpty();
                return false;
            } catch {
                if (trySession()) return true;
                finishEmpty();
                return false;
            }
        },
        [applyEditorSessionPayload]
    );

    // Site 3D → Site Layout: re-hydrate canvas from browser session (latest in-editor work).
    useEffect(() => {
        if (!location.state?.restoreSiteLayoutEditor) return;
        const siteGuid =
            location.state?.siteGuid ??
            global.gw?.selectedSite?.siteGuid ??
            pickerSiteGuid;
        pendingSite3dEditorRestoreRef.current = true;
        didRestoreSessionRef.current = false;
        setSiteSetupOpen(false);
        if (siteGuid) setPickerSiteGuid(siteGuid);
        global.clearSiteLayoutReturn?.();
        navigate("/siteLayout", { replace: true, state: {} });
    }, [
        location.state?.restoreSiteLayoutEditor,
        location.state?.siteGuid,
        navigate,
        pickerSiteGuid,
    ]);

    // Initial canvas: EditorDraftJson (DB), then session; if neither and no layout tables, Site 3D default grid. Saved layout tables only via Load.
    useEffect(() => {
        if (!siteReady || !activeSiteGuid) return;
        const preferSession = pendingSite3dEditorRestoreRef.current;
        if (didRestoreSessionRef.current && !preferSession) return;

        pendingSite3dEditorRestoreRef.current = false;
        didRestoreSessionRef.current = true;

        let cancelled = false;
        (async () => {
            await runEditorRestoreFromStorage(activeSiteGuid, { preferSession });
            if (cancelled) return;
        })();
        return () => {
            cancelled = true;
        };
    }, [siteReady, activeSiteGuid, runEditorRestoreFromStorage]);

    const getSiteLayoutSessionPayload = useCallback(
        () => ({
            imageDataUrl,
            blankCanvas,
            blankImageSize: blankCanvas ? imageSize : null,
            markers,
            layoutSegments,
            faceAssignments,
            faceLabels,
            markerLabels,
            markerAreaNames,
            feetPerPixel,
            clusterEpsFt,
            mode,
        }),
        [
            imageDataUrl,
            blankCanvas,
            imageSize,
            markers,
            layoutSegments,
            faceAssignments,
            faceLabels,
            markerLabels,
            markerAreaNames,
            feetPerPixel,
            clusterEpsFt,
            mode,
        ]
    );

    useEffect(() => {
        if (!activeSiteGuid || !siteReady || !editorRestoreCompleteRef.current) return;
        if (!layoutSurfaceActive && markers.length === 0 && layoutSegments.length === 0) return;
        saveSiteLayoutSession(activeSiteGuid, getSiteLayoutSessionPayload());
    }, [activeSiteGuid, siteReady, layoutSurfaceActive, getSiteLayoutSessionPayload]);

    useEffect(() => {
        sticeDrawRef.current = null;
        setSticeHoverSnap(null);
        setSticeFreePoint(null);
        setMoveGuidePoint(null);
        wallArcCursorRef.current =
            mode === MODES.WALL || mode === MODES.ARC ? "grab" : "crosshair";
        setWallArcCursor(wallArcCursorRef.current);
    }, [mode]);

    // Unsnapped walls (orange dashed) are removed when leaving Wall/Arc mode — Delete/right-click also works.
    useEffect(() => {
        if (mode === MODES.WALL || mode === MODES.ARC || mode === MODES.FLAT) return;
        if (layoutGeometryBusyRef.current) return;
        if (Date.now() < skipPruneUntilRef.current) return;
        setLayoutSegments((prev) => {
            const next = pruneUnsnappedSegments(prev, markers);
            return next.length === prev.length ? prev : next;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- prune only when leaving Wall/Arc; markers read from latest render
    }, [mode]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.code !== "Space") return;
            const inTextField =
                /^(INPUT|TEXTAREA|SELECT)$/.test(e.target?.tagName) ||
                !!e.target?.closest?.(
                    "input, textarea, select, [contenteditable='true'], .MuiInputBase-input"
                );
            if (!inTextField) {
                spaceHeldRef.current = true;
                setSpaceHeld(true);
            }
        };
        const onKeyUp = (e) => {
            if (e.code === "Space") {
                spaceHeldRef.current = false;
                setSpaceHeld(false);
            }
        };
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("keyup", onKeyUp);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("keyup", onKeyUp);
        };
    }, []);

    const [enclosedFaces, setEnclosedFaces] = useState([]);
    const enclosedFacesCacheRef = useRef([]);
    const prevFaceIdsKeyRef = useRef("");
    const prevEnclosedFacesForAssignmentRef = useRef([]);
    const prevLayoutSegmentsForAssignmentRef = useRef([]);
    const enclosureTimerRef = useRef(null);
    const enclosureIdleRef = useRef(null);
    const [enclosureRequestId, setEnclosureRequestId] = useState(0);

    const runEnclosureFromLiveLayout = useCallback(() => {
        enclosureTimerRef.current = null;
        enclosureIdleRef.current = null;
        if (layoutGeometryBusyRef.current) {
            enclosureTimerRef.current = setTimeout(runEnclosureFromLiveLayout, 80);
            return;
        }
        const live = layoutLiveRef.current;
        if (!live?.markers) return;
        try {
            const faces = dedupeEnclosedFacesForFlatRects(
                computeEnclosedFaces(live.markers, live.layoutSegments),
                live.layoutSegments
            );
            enclosedFacesCacheRef.current = faces;
            if (typeof window !== "undefined") {
                window.__faces = faces;
                window.__markers = live.markers;
                window.__segments = live.layoutSegments;
            }
            layoutGeometryCountsRef.current = {
                markers: live.markers.length,
                segments: live.layoutSegments.length,
            };
            const faceIdsKey = faces
                .map((f) => f.id)
                .sort()
                .join("|");
            const prevFaces = prevEnclosedFacesForAssignmentRef.current;
            const prevSegments = prevLayoutSegmentsForAssignmentRef.current;
            if (faceIdsKey !== prevFaceIdsKeyRef.current) {
                const { nextAssignments, nextLabels } = migrateFaceAssignmentState(
                    live.faceAssignments,
                    live.faceLabels,
                    prevFaces,
                    faces,
                    live.layoutSegments,
                    prevSegments
                );
                setFaceAssignments(nextAssignments);
                setFaceLabels(nextLabels);
                prevFaceIdsKeyRef.current = faceIdsKey;
                prevEnclosedFacesForAssignmentRef.current = faces;
                prevLayoutSegmentsForAssignmentRef.current = live.layoutSegments;
                const slots = buildOrderedLayoutAreas(live.markers, faces);
                const nextFaceAreaNames = mergeFaceAreaNamesByRectId(
                    faceAreaNamesByRectIdRef.current,
                    slots,
                    live.layoutSegments
                );
                if (nextFaceAreaNames !== faceAreaNamesByRectIdRef.current) {
                    faceAreaNamesByRectIdRef.current = nextFaceAreaNames;
                    setFaceAreaNamesByRectId(nextFaceAreaNames);
                }
                startTransition(() => {
                    setEnclosedFaces(faces.map(enclosedFaceSummaryForState));
                });
            } else {
                prevEnclosedFacesForAssignmentRef.current = faces;
                prevLayoutSegmentsForAssignmentRef.current = live.layoutSegments;
                requestAnimationFrame(() => paintLayoutCanvasRef.current?.());
            }
            const anchorRid = groupAnchorFlatRectIdRef.current;
            if (anchorRid) {
                const anchored = faces.find(
                    (f) => closedRectIdForFace(f, live.layoutSegments) === anchorRid
                );
                if (anchored) {
                    syncCanvasSelection({ selectedFaceId: anchored.id });
                    startTransition(() => setSelectedFaceId(anchored.id));
                }
            }
        } catch (err) {
            console.error("computeEnclosedFaces failed", err);
            startTransition(() => {
                setEnclosedFaces(
                    enclosedFacesCacheRef.current.map(enclosedFaceSummaryForState)
                );
            });
        }
    }, []);

    const scheduleDeferredMarkerEnclosure = useCallback(() => {
        if (deferredMarkerEnclosureTimerRef.current) {
            clearTimeout(deferredMarkerEnclosureTimerRef.current);
        }
        deferredMarkerEnclosureTimerRef.current = setTimeout(() => {
            deferredMarkerEnclosureTimerRef.current = null;
            if (typeof requestIdleCallback === "function") {
                enclosureIdleRef.current = requestIdleCallback(
                    runEnclosureFromLiveLayout,
                    { timeout: 8000 }
                );
            } else {
                runEnclosureFromLiveLayout();
            }
        }, MARKER_ADD_ENCLOSURE_DEFER_MS);
    }, [runEnclosureFromLiveLayout]);

    /** Coalesce flat move/resize commits — avoids a new assignment row per corner drag. */
    const scheduleDebouncedFlatEnclosure = useCallback(() => {
        if (flatEnclosureDebounceRef.current) {
            clearTimeout(flatEnclosureDebounceRef.current);
        }
        flatEnclosureDebounceRef.current = setTimeout(() => {
            flatEnclosureDebounceRef.current = null;
            scheduleDeferredMarkerEnclosure();
        }, 2200);
    }, [scheduleDeferredMarkerEnclosure]);

    /** List new flat in assignment panel immediately without full-site enclosure. */
    const appendFlatToLayoutAfterPlace = useCallback(
        (nextSegments, prevSegments, rectId) => {
            const synthetic = buildSyntheticFlatFace(nextSegments, rectId);
            if (!synthetic) return null;
            const prevFaces =
                enclosedFacesCacheRef.current.length > 0
                    ? enclosedFacesCacheRef.current
                    : enclosedFaces;
            const faces = [
                ...prevFaces.filter(
                    (f) => closedRectIdForFace(f, nextSegments) !== rectId
                ),
                synthetic,
            ];
            enclosedFacesCacheRef.current = faces;
            layoutGeometryCountsRef.current = {
                markers: markers.length,
                segments: nextSegments.length,
            };
            const slots = buildOrderedLayoutAreas(markers, faces);
            layoutAreaSlotsCacheRef.current = slots;
            const nextFaceAreaNames = mergeFaceAreaNamesByRectId(
                faceAreaNamesByRectIdRef.current,
                slots,
                nextSegments
            );
            faceAreaNamesByRectIdRef.current = nextFaceAreaNames;
            setFaceAreaNamesByRectId(nextFaceAreaNames);
            const faceIdsKey = faces
                .map((f) => f.id)
                .sort()
                .join("|");
            const live = layoutLiveRef.current;
            const { nextAssignments, nextLabels } = migrateFaceAssignmentState(
                live.faceAssignments,
                live.faceLabels,
                prevFaces,
                faces,
                nextSegments,
                prevSegments
            );
            setFaceAssignments(nextAssignments);
            setFaceLabels(nextLabels);
            prevFaceIdsKeyRef.current = faceIdsKey;
            prevEnclosedFacesForAssignmentRef.current = faces;
            prevLayoutSegmentsForAssignmentRef.current = nextSegments;
            setEnclosedFaces(faces.map(enclosedFaceSummaryForState));
            return synthetic.id;
        },
        [markers, enclosedFaces]
    );

    /** Update flat row/polygon after move-resize without blocking on full-site enclosure. */
    const syncFlatFaceInCacheAfterEdit = useCallback(
        (segments, rectId) => {
            const synthetic = buildSyntheticFlatFace(segments, rectId);
            if (!synthetic) return;
            const prev =
                enclosedFacesCacheRef.current.length > 0
                    ? enclosedFacesCacheRef.current
                    : enclosedFaces;
            const faces = dedupeEnclosedFacesForFlatRects(
                [
                    ...prev.filter(
                        (f) => closedRectIdForFace(f, segments) !== rectId
                    ),
                    synthetic,
                ],
                segments
            );
            enclosedFacesCacheRef.current = faces;
            layoutAreaSlotsCacheRef.current = buildOrderedLayoutAreas(
                markers,
                faces
            );
            if (flatFacesStateDebounceRef.current) {
                clearTimeout(flatFacesStateDebounceRef.current);
            }
            flatFacesStateDebounceRef.current = setTimeout(() => {
                flatFacesStateDebounceRef.current = null;
                startTransition(() => {
                    setEnclosedFaces(
                        faces.map(enclosedFaceSummaryForState)
                    );
                });
            }, 280);
            requestAnimationFrame(() => paintLayoutCanvasRef.current?.());
        },
        [markers, enclosedFaces]
    );

    useEffect(() => {
        const pending = pendingPlacedMarkersRef.current;
        if (!pending?.length || pending.length !== markers.length) return;
        const lastPending = pending[pending.length - 1];
        const lastMarker = markers[markers.length - 1];
        if (lastPending?.id === lastMarker?.id) {
            pendingPlacedMarkersRef.current = null;
        }
    }, [markers]);

    useEffect(() => {
        if (enclosureTimerRef.current) clearTimeout(enclosureTimerRef.current);
        if (deferredMarkerEnclosureTimerRef.current) {
            clearTimeout(deferredMarkerEnclosureTimerRef.current);
            deferredMarkerEnclosureTimerRef.current = null;
        }
        if (enclosureIdleRef.current != null) {
            if (typeof cancelIdleCallback === "function") {
                cancelIdleCallback(enclosureIdleRef.current);
            }
            enclosureIdleRef.current = null;
        }
        if (layoutGeometryBusyRef.current || flatEditRef.current) {
            return () => {
                if (enclosureTimerRef.current) clearTimeout(enclosureTimerRef.current);
                if (enclosureIdleRef.current != null && typeof cancelIdleCallback === "function") {
                    cancelIdleCallback(enclosureIdleRef.current);
                }
            };
        }
        const prevCounts = layoutGeometryCountsRef.current;
        const markersOnlyDelta =
            prevCounts.markers > 0 &&
            markers.length > prevCounts.markers &&
            layoutSegments.length === prevCounts.segments;
        const segmentsOnlyDelta =
            prevCounts.segments > 0 &&
            layoutSegments.length > prevCounts.segments &&
            markers.length === prevCounts.markers;

        /** Bin-only append: defer planar refresh. */
        if (
            markersOnlyDelta &&
            enclosedFacesCacheRef.current.length > 0
        ) {
            scheduleDeferredMarkerEnclosure();
            return () => {
                if (deferredMarkerEnclosureTimerRef.current) {
                    clearTimeout(deferredMarkerEnclosureTimerRef.current);
                }
                if (enclosureIdleRef.current != null && typeof cancelIdleCallback === "function") {
                    cancelIdleCallback(enclosureIdleRef.current);
                }
            };
        }

        /** Debounce only while a wall/arc click sequence is in progress, not for the whole tool mode. */
        const drawingWallOrArc = !!sticeDrawRef.current?.from;
        const delayMs = drawingWallOrArc
            ? 420
            : layoutHeavyGeometry
                ? Math.min(900, 220 + layoutObjectCount * 6)
                : 140;

        const armEnclosure = () => {
            if (
                layoutHeavyGeometry &&
                typeof requestIdleCallback === "function"
            ) {
                enclosureIdleRef.current = requestIdleCallback(
                    runEnclosureFromLiveLayout,
                    { timeout: 1400 }
                );
            } else {
                runEnclosureFromLiveLayout();
            }
        };

        if (delayMs > 0) {
            enclosureTimerRef.current = setTimeout(armEnclosure, delayMs);
        } else {
            armEnclosure();
        }

        return () => {
            if (enclosureTimerRef.current) clearTimeout(enclosureTimerRef.current);
            if (enclosureIdleRef.current != null && typeof cancelIdleCallback === "function") {
                cancelIdleCallback(enclosureIdleRef.current);
            }
        };
    }, [
        ...(layoutHeavyGeometry
            ? [
                  enclosureRequestId,
                  mode,
                  layoutObjectCount,
                  markers.length,
                  layoutSegments.length,
              ]
            : [markers, layoutSegments, mode, enclosureRequestId, layoutObjectCount]),
        scheduleDeferredMarkerEnclosure,
        runEnclosureFromLiveLayout,
    ]);

    useEffect(() => {
        const faces =
            enclosedFacesCacheRef.current.length > 0
                ? enclosedFacesCacheRef.current
                : enclosedFaces;
        const faceIdsKey = faces
            .map((f) => f.id)
            .sort()
            .join("|");
        if (faceIdsKey === prevFaceIdsKeyRef.current) return;

        const prevFaces = prevEnclosedFacesForAssignmentRef.current;
        const prevSegments = prevLayoutSegmentsForAssignmentRef.current;

        const live = layoutLiveRef.current;
        const { nextAssignments, nextLabels } = migrateFaceAssignmentState(
            live.faceAssignments,
            live.faceLabels,
            prevFaces,
            faces,
            layoutSegments,
            prevSegments
        );
        setFaceAssignments(nextAssignments);
        setFaceLabels(nextLabels);

        prevFaceIdsKeyRef.current = faceIdsKey;
        prevEnclosedFacesForAssignmentRef.current = faces;
        prevLayoutSegmentsForAssignmentRef.current = layoutSegments;
    }, [enclosedFaces, layoutSegments]);

    const layoutReadyCount = useMemo(() => {
        const roundReady = markers.filter((m) => m.binDataId != null).length;
        const flatReady = enclosedFaces.filter((f) => faceAssignments[f.id] != null).length;
        return roundReady + flatReady;
    }, [markers, enclosedFaces, faceAssignments]);

    /** Preview is allowed whenever there is layout geometry to show (assignment not required). */
    const canPreviewLayout = useMemo(
        () =>
            markers.length > 0 ||
            layoutSegments.length > 0 ||
            enclosedFaces.length > 0,
        [markers.length, layoutSegments.length, enclosedFaces.length]
    );

    const canSaveLayoutTables = canPreviewLayout;

    const flatSelectedRectId = useMemo(() => {
        if (groupAnchorFlatRectId) return groupAnchorFlatRectId;
        if (selectedFaceId?.startsWith("flat:")) {
            return selectedFaceId.slice(5);
        }
        const faces =
            enclosedFacesCacheRef.current.length > 0
                ? enclosedFacesCacheRef.current
                : enclosedFaces;
        const face = selectedFaceId
            ? faces.find((f) => f.id === selectedFaceId)
            : null;
        const fromFace = closedRectIdForFace(face, layoutSegments);
        if (fromFace) return fromFace;
        const first = layoutSegments.find((s) => s.id === selectedSegmentIds[0]);
        if (
            first?.closedRectId &&
            selectedSegmentIds.every((id) => {
                const s = layoutSegments.find((seg) => seg.id === id);
                return s?.closedRectId === first.closedRectId;
            })
        ) {
            return first.closedRectId;
        }
        return null;
    }, [
        groupAnchorFlatRectId,
        selectedFaceId,
        selectedSegmentIds,
        layoutSegments,
        enclosedFaces,
    ]);

    keyboardNudgeCtxRef.current = {
        mode,
        selectedMarkerId,
        selectedMarkerIds,
        selectedSegmentId,
        selectedSegmentIds,
        flatSelectedRectId,
        markers,
        layoutSegments,
    };

    const groupSelectedFlatRectIds = useMemo(
        () => flatRectIdsFromSegmentIds(layoutSegments, selectedSegmentIds),
        [layoutSegments, selectedSegmentIds]
    );

    const groupAlignEnabled =
        selectedMarkerIds.length >= 2 || groupSelectedFlatRectIds.length >= 2;

    const groupRotateEnabled =
        selectedMarkerIds.length > 0 || selectedSegmentIds.length > 0;

    const showGroupOverlay =
        selectedMarkerIds.length > 0 ||
        selectedSegmentIds.length > 0 ||
        !!selectedMarkerId ||
        !!selectedSegmentId;

    /** Unique A,B,C… slots — same order on canvas tags and assignment panel (Y then X). */
    const layoutAreaSlots = useMemo(() => {
        const faces =
            enclosedFacesCacheRef.current.length > 0
                ? enclosedFacesCacheRef.current
                : enclosedFaces;
        const next = buildOrderedLayoutAreas(markers, faces);
        layoutAreaSlotsCacheRef.current = next;
        return next;
    }, [markers, enclosedFaces]);

    useEffect(() => {
        const merged = mergeMarkerAreaNamesFromSlots(
            markerAreaNamesRef.current,
            layoutAreaSlots
        );
        if (merged !== markerAreaNamesRef.current) {
            markerAreaNamesRef.current = merged;
            setMarkerAreaNames(merged);
        }
        const nextFaceNames = mergeFaceAreaNamesByRectId(
            faceAreaNamesByRectIdRef.current,
            layoutAreaSlots,
            layoutSegments
        );
        if (nextFaceNames !== faceAreaNamesByRectIdRef.current) {
            faceAreaNamesByRectIdRef.current = nextFaceNames;
            setFaceAreaNamesByRectId(nextFaceNames);
        }
    }, [layoutAreaSlots, layoutSegments]);

    const layoutAreaSlotByMarkerId = useMemo(() => {
        const map = new Map();
        layoutAreaSlots.forEach((slot) => {
            if (slot.kind === "marker") map.set(slot.marker.id, slot);
        });
        return map;
    }, [layoutAreaSlots]);

    const layoutFaceSlotById = useMemo(() => {
        const map = new Map();
        layoutAreaSlots.forEach((slot) => {
            if (slot.kind === "face") map.set(slot.face.id, slot);
        });
        return map;
    }, [layoutAreaSlots]);

    const layoutAssignmentRows = useMemo(
        () =>
            layoutAreaSlots.map((slot) => ({
                key: slot.kind === "face" ? `face-${slot.face.id}` : `marker-${slot.marker.id}`,
                kind: slot.kind,
                face: slot.face,
                marker: slot.marker,
                areaIndex: slot.areaIndex,
                areaLabel: slot.areaLabel,
                labelId:
                    slot.kind === "face"
                        ? assignmentFaceRowDomId(slot.face, layoutSegments)
                        : `layout-assign-marker-${slot.marker.id}`,
            })),
        [layoutAreaSlots, layoutSegments]
    );

    useEffect(() => {
        if (!siteSetupOpen || siteOptions.length === 0) return;
        setPickerSiteGuid((prev) => {
            if (!prev) return prev;
            const stillThere = siteOptions.some(
                (s) =>
                    global.normalizeGuidForCompare(s.siteGuid) ===
                    global.normalizeGuidForCompare(prev),
            );
            return stillThere ? prev : "";
        });
    }, [siteSetupOpen, siteOptions]);

    const handleSiteSetupContinue = useCallback(async (guid) => {
        const next = guid || pickerSiteGuid;
        if (!next) return;
        setPickerSiteGuid(next);
        setSiteSetupOpen(false);
        await selectSite(next);
    }, [pickerSiteGuid, selectSite]);

    const handleExitSiteLayout = useCallback(() => {
        global.clearSiteLayoutReturn?.();
        setSiteSetupOpen(false);
        navigate("/sites");
    }, [navigate]);

    const handleOpenSiteLayoutHelp = useCallback(() => {
        window.open(siteLayoutHelpUrl(), "_blank", "noopener,noreferrer");
    }, []);

    const getFitViewZoom = useCallback(() => {
        const viewport = viewportRef.current;
        const base = baseCanvasSizeRef.current;
        if (!viewport?.clientWidth || !base.width || !base.height) {
            return DEFAULT_VIEW_ZOOM;
        }
        const zx = (viewport.clientWidth * VIEW_FIT_PADDING) / base.width;
        const zy = (viewport.clientHeight * VIEW_FIT_PADDING) / base.height;
        return clampViewZoom(Math.min(zx, zy));
    }, []);

    const panViewportToImagePoint = useCallback(
        (imageX, imageY, zoom) => {
            const viewport = viewportRef.current;
            const base = baseCanvasSizeRef.current;
            const imgW = Math.max(imageSize.width, 1);
            const imgH = Math.max(imageSize.height, 1);
            if (!viewport?.clientWidth || !base.width) return;
            const z = clampViewZoom(zoom);
            const canvas = scaledCanvasPixelSize(base, z);
            const u = imageX / imgW;
            const v = imageY / imgH;
            const pan = {
                x: viewport.clientWidth / 2 - u * canvas.width,
                y: viewport.clientHeight / 2 - v * canvas.height,
            };
            cancelViewPanStateSync();
            viewPanLiveRef.current = pan;
            viewZoomRef.current = z;
            committedViewZoomRef.current = z;
            setViewZoom(z);
            setCanvasSize(canvas);
            setViewPan(pan);
        },
        [imageSize.width, imageSize.height, cancelViewPanStateSync]
    );

    const fitGraphInViewport = useCallback(() => {
        const z = getFitViewZoom();
        const imgW = Math.max(imageSize.width, 1);
        const imgH = Math.max(imageSize.height, 1);
        const center = computeLayoutSiteCenterPx(markers, enclosedFaces, {
            width: imgW,
            height: imgH,
        });
        panViewportToImagePoint(
            center?.x ?? imgW / 2,
            center?.y ?? imgH / 2,
            z
        );
    }, [
        getFitViewZoom,
        panViewportToImagePoint,
        imageSize.width,
        imageSize.height,
        markers,
        enclosedFaces,
    ]);

    const centerBinsOnGraph = useCallback(() => {
        const imgW = Math.max(imageSize.width, 1);
        const imgH = Math.max(imageSize.height, 1);
        const targetX = imgW / 2;
        const targetY = imgH / 2;
        const siteCenter = computeLayoutSiteCenterPx(markers, enclosedFaces);
        if (!siteCenter) {
            fitGraphInViewport();
            setStatusMessage(
                "Place bins or draw walls/arcs before centering on the graph."
            );
            return;
        }
        const dx = targetX - siteCenter.x;
        const dy = targetY - siteCenter.y;
        if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01) {
            panViewportToImagePoint(targetX, targetY, viewZoom);
            setStatusMessage("Layout is already centered on the graph.");
            return;
        }
        recordHistory();
        setMarkers((prev) =>
            prev.map((m) => ({ ...m, x: m.x + dx, y: m.y + dy }))
        );
        setLayoutSegments((prev) =>
            prev.map((s) => applyDeltaToSegment(s, dx, dy))
        );
        panViewportToImagePoint(targetX, targetY, viewZoom);
        setStatusMessage("Centered layout on the graph.");
    }, [
        markers,
        enclosedFaces,
        imageSize.width,
        imageSize.height,
        viewZoom,
        panViewportToImagePoint,
        fitGraphInViewport,
        recordHistory,
    ]);

    const resetViewportToDefault = useCallback(() => {
        viewportInitRef.current = false;
        requestAnimationFrame(() => fitGraphInViewport());
    }, [fitGraphInViewport]);

    const prevBlankImageSizeRef = useRef({ width: 0, height: 0 });
    useEffect(() => {
        if (!layoutSurfaceActive || !blankCanvas) return;
        const prev = prevBlankImageSizeRef.current;
        const w = imageSize.width || 0;
        const h = imageSize.height || 0;
        if (prev.width > 0 && (prev.width !== w || prev.height !== h)) {
            requestAnimationFrame(() => fitGraphInViewport());
        }
        prevBlankImageSizeRef.current = { width: w, height: h };
    }, [
        blankCanvas,
        imageSize.width,
        imageSize.height,
        layoutSurfaceActive,
        fitGraphInViewport,
    ]);

    const startBlankLayout = useCallback(({ silent = false } = {}) => {
        setBlankCanvas(true);
        setImageDataUrl(null);
        imageRef.current = null;
        setImageSize({ ...BLANK_LAYOUT_SIZE });
        const fpp = defaultLayoutFeetPerPixel(siteBins);
        setFeetPerPixel(fpp);
        resetViewportToDefault();
        if (!silent) {
            setStatusMessage(
                `Blank canvas ready (${layoutScaleSummary(fpp)}). Place bins or draw walls/arcs.`
            );
        }
    }, [resetViewportToDefault, siteBins]);

    const clearGroupSelection = useCallback(() => {
        clearPlaceModeSelection();
        setSelectionRect(null);
        marqueeRef.current = null;
    }, [clearPlaceModeSelection]);

    const hasAnyGroupSelection = useCallback(
        () =>
            selectedMarkerIds.length > 0 ||
            selectedSegmentIds.length > 0 ||
            !!selectedMarkerId ||
            !!selectedSegmentId ||
            !!selectedFaceId,
        [
            selectedMarkerIds,
            selectedSegmentIds,
            selectedMarkerId,
            selectedSegmentId,
            selectedFaceId,
        ]
    );

    /** Any toolbar control click clears the current group selection. */
    const onToolbarClick = useCallback(
        (handler) =>
            (...args) => {
                if (hasAnyGroupSelection()) {
                    clearGroupSelection();
                }
                return handler?.(...args);
            },
        [hasAnyGroupSelection, clearGroupSelection]
    );

    const changeLayoutMode = useCallback(
        (nextMode) => {
            if (hasAnyGroupSelection()) {
                clearGroupSelection();
            }
            setMode(nextMode);
            sticeDrawRef.current = null;
            if (flatEditRef.current) {
                const pid = flatEditRef.current.pointerId;
                if (pid != null && canvasRef.current?.releasePointerCapture) {
                    try {
                        canvasRef.current.releasePointerCapture(pid);
                    } catch (_) {
                        /* ignore */
                    }
                }
                flatEditRef.current = null;
            }
            setFlatHandleRectId(null);
            setSticeHoverSnap(null);
            setSticeFreePoint(null);
        },
        [hasAnyGroupSelection, clearGroupSelection]
    );

    const selectSegmentById = useCallback(
        (segmentId, segments = layoutSegments) => {
            const seg = segments.find((s) => s.id === segmentId);
            if (seg?.closedRectId) {
                const rectId = seg.closedRectId;
                const ids = segments
                    .filter((s) => s.closedRectId === rectId)
                    .map((s) => s.id);
                setSelectedSegmentIds(ids);
                setSelectedSegmentId(segmentId);
                setSelectedMarkerId(null);
                setSelectedMarkerIds([]);
                setGroupAnchorMarkerId(null);
                setGroupAnchorFlatRectId(rectId);
                const faces =
                    enclosedFacesCacheRef.current.length > 0
                        ? enclosedFacesCacheRef.current
                        : enclosedFaces;
                const face = faces.find((f) =>
                    (f.segmentIds || []).every((id) => ids.includes(id))
                );
                setSelectedFaceId(face?.id ?? null);
                return;
            }
            setSelectedSegmentId(segmentId);
            setSelectedSegmentIds([segmentId]);
            setSelectedMarkerId(null);
            setSelectedMarkerIds([]);
            setGroupAnchorMarkerId(null);
            setSelectedFaceId(null);
        },
        [layoutSegments, enclosedFaces]
    );

    const scrollAssignmentRowToCenter = useCallback((elementId) => {
        const container = sidebarRef.current;
        if (!container || !elementId) return;
        let attempts = 0;
        const run = () => {
            const el = document.getElementById(elementId);
            if (!el) {
                if (attempts++ < 5) requestAnimationFrame(run);
                return;
            }
            const cRect = container.getBoundingClientRect();
            const eRect = el.getBoundingClientRect();
            const relativeTop = eRect.top - cRect.top + container.scrollTop;
            const target =
                relativeTop + eRect.height / 2 - container.clientHeight / 2;
            container.scrollTo({
                top: Math.max(0, target),
                behavior: "smooth",
            });
        };
        requestAnimationFrame(run);
    }, []);

    const selectFlatRect = useCallback(
        (rectId, options = {}) => {
            const { scrollPanel = false, syncReactState = true } = options;
            const segs =
                interactionSegmentsRef.current ?? layoutSegments;
            const bounds = getFlatRectBounds(segs, rectId);
            if (!bounds) return;
            const stableFaceId = `flat:${rectId}`;
            const faces =
                enclosedFacesCacheRef.current.length > 0
                    ? enclosedFacesCacheRef.current
                    : enclosedFaces;
            const face = faces.find(
                (f) => closedRectIdForFace(f, segs) === rectId
            );
            const faceId = face?.id ?? stableFaceId;
            syncCanvasSelection({
                selectedFaceId: faceId,
                selectedMarkerId: null,
                selectedMarkerIds: [],
                selectedSegmentId: bounds.segmentIds[0] ?? null,
                selectedSegmentIds: bounds.segmentIds,
                flatHandleRectId: null,
                flatSelectedRectId: rectId,
            });
            if (syncReactState) {
                startTransition(() => {
                    setSelectedSegmentIds(bounds.segmentIds);
                    setSelectedSegmentId(bounds.segmentIds[0] ?? null);
                    setSelectedMarkerId(null);
                    setSelectedMarkerIds([]);
                    setGroupAnchorMarkerId(null);
                    setGroupAnchorFlatRectId(rectId);
                    setSelectedFaceId(faceId);
                });
            }
            if (scrollPanel) {
                const rowId = `layout-assign-face-${rectId}`;
                requestAnimationFrame(() =>
                    scrollAssignmentRowToCenter(rowId)
                );
            }
        },
        [
            layoutSegments,
            enclosedFaces,
            syncCanvasSelection,
            scrollAssignmentRowToCenter,
        ]
    );

    const startFlatRectEdit = useCallback(
        (rectId, x, y, scaleX, scaleY, e, knownCorner = null, moveOnly = false) => {
            const segsLive =
                interactionSegmentsRef.current ?? layoutSegments;
            const bounds = getFlatRectBounds(segsLive, rectId);
            if (!bounds) return false;
            const scaleMin = Math.min(scaleX, scaleY, 1e-6);
            const cornerTol = Math.max(
                FLAT_RECT_CORNER_HIT_TOL_IMAGE,
                FLAT_RECT_CORNER_HIT_TOL_CANVAS / scaleMin
            );
            const corner = moveOnly
                ? knownCorner
                : knownCorner ||
                  findFlatRectCornerAtImage(x, y, bounds, cornerTol);
            const inside = pointInFlatRectBounds(x, y, bounds);
            if (!corner && !inside) return false;

            selectFlatRect(rectId, { syncReactState: false });
            setFlatHandleRectId(rectId);
            layoutGeometryBusyRef.current = true;

            const pointerId = e.pointerId;
            if (
                pointerId != null &&
                canvasRef.current?.setPointerCapture
            ) {
                try {
                    canvasRef.current.setPointerCapture(pointerId);
                } catch (_) {
                    /* ignore */
                }
            }

            if (corner) {
                flatEditRef.current = {
                    type: "resize",
                    rectId,
                    corner,
                    pointerId: pointerId ?? null,
                    originBounds: {
                        minX: bounds.minX,
                        minY: bounds.minY,
                        maxX: bounds.maxX,
                        maxY: bounds.maxY,
                    },
                    moved: false,
                };
            } else {
                const segmentOrigins = new Map();
                segsLive
                    .filter((s) => s.closedRectId === rectId)
                    .forEach((s) => segmentOrigins.set(s.id, cloneSegmentForDrag(s)));
                flatEditRef.current = {
                    type: "move",
                    rectId,
                    pointerId: pointerId ?? null,
                    startX: x,
                    startY: y,
                    segmentOrigins,
                    moved: false,
                };
            }
            e.preventDefault();
            return true;
        },
        [layoutSegments, selectFlatRect]
    );

    const facesForPlacementCheck = useCallback(() => {
        if (enclosedFacesCacheRef.current.length > 0) {
            return enclosedFacesCacheRef.current;
        }
        return enclosedFaces;
    }, [enclosedFaces]);

    const placeFlatHouseAt = useCallback(
        (cx, cy, widthFt = DEFAULT_FLAT_WIDTH_FT, lengthFt = DEFAULT_FLAT_LENGTH_FT) => {
            const fpp = resolveFeetPerPixel(markers, siteBins, feetPerPixel);
            const { x1, y1, x2, y2 } = flatRectCornersFromCenter(
                cx,
                cy,
                widthFt,
                lengthFt,
                fpp
            );
            if (
                layoutPlacementBlockedForFlatRect({
                    minX: x1,
                    minY: y1,
                    maxX: x2,
                    maxY: y2,
                    markers,
                    segments: layoutSegments,
                    faces: facesForPlacementCheck(),
                })
            ) {
                setStatusMessage(
                    "Cannot place a flat here — it overlaps an existing bin, flat, wall, or area."
                );
                return null;
            }
            const segs = createFlatRectangleSegments(x1, y1, x2, y2);
            if (!segs.length) {
                setStatusMessage(
                    fpp > 0
                        ? "Could not place flat — try another spot."
                        : "Could not place flat — check graph grid scale."
                );
                return null;
            }
            const rectId = segs[0].closedRectId;
            const prevSegments = layoutSegments;
            const nextSegments = [...layoutSegments, ...segs];
            layoutSegmentsRef.current = nextSegments;
            interactionSegmentsRef.current = nextSegments;
            const stableFaceId = appendFlatToLayoutAfterPlace(
                nextSegments,
                prevSegments,
                rectId
            );
            paintLayoutCanvasRef.current?.();
            setLayoutSegments(nextSegments);
            setSelectedSegmentIds(segs.map((s) => s.id));
            setSelectedSegmentId(segs[0]?.id ?? null);
            setSelectedMarkerId(null);
            setSelectedMarkerIds([]);
            setGroupAnchorMarkerId(null);
            setGroupAnchorFlatRectId(rectId);
            setSelectedFaceId(stableFaceId);
            scheduleDeferredMarkerEnclosure();
            requestAnimationFrame(() =>
                scrollAssignmentRowToCenter(`layout-assign-face-${rectId}`)
            );
            if (placeHistoryIdleRef.current != null) {
                if (typeof cancelIdleCallback === "function") {
                    cancelIdleCallback(placeHistoryIdleRef.current);
                }
            }
            const queuePlaceHistory = () => {
                placeHistoryIdleRef.current = null;
                recordHistory();
            };
            if (typeof requestIdleCallback === "function") {
                placeHistoryIdleRef.current = requestIdleCallback(
                    queuePlaceHistory,
                    { timeout: 1200 }
                );
            } else {
                setTimeout(queuePlaceHistory, 0);
            }
            const wLabel = fpp > 0 ? round1(widthFt) : "?";
            const lLabel = fpp > 0 ? round1(lengthFt) : "?";
            setStatusMessage(
                fpp > 0
                    ? `Flat placed (${wLabel}×${lLabel} ft). Drag orange dot or interior to move; blue corners to resize.`
                    : "Flat placed. Drag orange dot or interior to move; blue corners to resize."
            );
            return rectId;
        },
        [
            markers,
            siteBins,
            feetPerPixel,
            layoutSegments,
            recordHistory,
            enclosedFaces,
            facesForPlacementCheck,
            appendFlatToLayoutAfterPlace,
            scheduleDeferredMarkerEnclosure,
            scrollAssignmentRowToCenter,
        ]
    );

    const queueFlatPendingDrag = useCallback(
        (rectId, x, y, scaleX, scaleY, e, corner, moveOnly) => {
            flatPendingDragRef.current = {
                rectId,
                x,
                y,
                scaleX,
                scaleY,
                corner: corner ?? null,
                moveOnly: !!moveOnly,
                pointerId: e.pointerId ?? null,
                clientX: e.clientX,
                clientY: e.clientY,
            };
            emptyDragRef.current = {
                clientX: e.clientX,
                clientY: e.clientY,
                panX: viewPanLiveRef.current.x,
                panY: viewPanLiveRef.current.y,
            };
            return true;
        },
        []
    );

    const tryBeginFlatRectEdit = useCallback(
        (x, y, canvasX, canvasY, scaleX, scaleY, e) => {
            if (
                spaceHeldRef.current ||
                e.altKey ||
                zoomPreviewActiveRef.current
            ) {
                return false;
            }
            if (hasMultiBinGroupSelection(selectedMarkerId, selectedMarkerIds)) {
                return false;
            }
            const activeFlatId =
                groupAnchorFlatRectId ||
                flatSelectedRectId ||
                canvasSelectionRef.current.flatSelectedRectId;
            const segsLive =
                interactionSegmentsRef.current ?? layoutSegments;
            const cornerHit = findFlatRectCornerHit(
                segsLive,
                x,
                y,
                scaleX,
                scaleY,
                FLAT_RECT_CORNER_HIT_TOL_IMAGE,
                activeFlatId || null
            );
            if (cornerHit && activeFlatId && cornerHit.rectId === activeFlatId) {
                return startFlatRectEdit(
                    cornerHit.rectId,
                    x,
                    y,
                    scaleX,
                    scaleY,
                    e,
                    cornerHit.corner,
                    false
                );
            }
            const centerHit =
                activeFlatId &&
                findFlatRectMoveCenterHit(
                    segsLive,
                    x,
                    y,
                    scaleX,
                    scaleY,
                    activeFlatId
                );
            if (centerHit && centerHit.rectId === activeFlatId) {
                return queueFlatPendingDrag(
                    centerHit.rectId,
                    x,
                    y,
                    scaleX,
                    scaleY,
                    e,
                    null,
                    true
                );
            }
            const hitRectId = findFlatRectAtPoint(segsLive, x, y);
            if (!hitRectId) return false;
            if (activeFlatId && hitRectId === activeFlatId) {
                return queueFlatPendingDrag(
                    hitRectId,
                    x,
                    y,
                    scaleX,
                    scaleY,
                    e,
                    null,
                    true
                );
            }
            selectFlatRect(hitRectId, { scrollPanel: true });
            return false;
        },
        [
            layoutSegments,
            groupAnchorFlatRectId,
            flatSelectedRectId,
            selectedMarkerId,
            selectedMarkerIds,
            startFlatRectEdit,
            selectFlatRect,
            queueFlatPendingDrag,
        ]
    );

    const releaseFlatEditPointerCapture = useCallback(() => {
        const pid = flatEditRef.current?.pointerId;
        if (pid != null && canvasRef.current?.releasePointerCapture) {
            try {
                canvasRef.current.releasePointerCapture(pid);
            } catch (_) {
                /* ignore */
            }
        }
    }, []);

    const finishFlatRectEdit = useCallback(() => {
        const ed = flatEditRef.current;
        const hadEdit = !!ed;
        const moved = ed?.moved;
        const rectId = ed?.rectId;
        releaseFlatEditPointerCapture();
        flatEditRef.current = null;
        setFlatHandleRectId(null);
        const canvas = canvasRef.current;
        if (canvas) canvas.style.cursor = "crosshair";
        if (hadEdit && moved && interactionSegmentsRef.current) {
            const segs = interactionSegmentsRef.current;
            layoutSegmentsRef.current = segs;
            interactionSegmentsRef.current = null;
            if (rectId) {
                syncFlatFaceInCacheAfterEdit(segs, rectId);
            }
            startTransition(() => {
                setLayoutSegments(segs);
                if (rectId) {
                    selectFlatRect(rectId, {
                        scrollPanel: false,
                        syncReactState: true,
                    });
                }
            });
            scheduleDebouncedFlatEnclosure();
            const queueHistory = () => recordHistory();
            if (typeof requestIdleCallback === "function") {
                requestIdleCallback(queueHistory, { timeout: 1200 });
            } else {
                setTimeout(queueHistory, 0);
            }
            scheduleLayoutPaint();
        } else if (hadEdit && interactionSegmentsRef.current) {
            interactionSegmentsRef.current = null;
            if (rectId) {
                selectFlatRect(rectId, {
                    scrollPanel: false,
                    syncReactState: true,
                });
            }
        }
        if (!hadEdit) {
            scheduleLayoutPaint();
        }
    }, [
        releaseFlatEditPointerCapture,
        scheduleLayoutPaint,
        syncFlatFaceInCacheAfterEdit,
        scheduleDebouncedFlatEnclosure,
        recordHistory,
        selectFlatRect,
    ]);

    const handleFlatResizePointerUp = useCallback(
        (e) => {
            if (e.button !== 0) return;
            if (!flatEditRef.current && !flatHandleRectId) return;
            finishFlatRectEdit();
        },
        [finishFlatRectEdit, flatHandleRectId]
    );

    // MUI Select/TextField swallow wheel events; capture on the sidebar so it scrolls over controls too.
    useEffect(() => {
        const el = sidebarRef.current;
        if (!el || preview3dOpen) return undefined;
        const onWheel = (e) => {
            if (e.target.closest("[role='listbox'], .MuiMenu-list")) return;
            const { deltaY } = e;
            if (!deltaY) return;
            const maxTop = el.scrollHeight - el.clientHeight;
            if (maxTop <= 0) return;
            const next = Math.max(0, Math.min(maxTop, el.scrollTop + deltaY));
            if (next === el.scrollTop) return;
            el.scrollTop = next;
            e.preventDefault();
        };
        el.addEventListener("wheel", onWheel, { passive: false, capture: true });
        return () => el.removeEventListener("wheel", onWheel, { capture: true });
    }, [preview3dOpen]);

    const selectBinMarkerById = useCallback(
        (markerId, options = {}) => {
            const { scrollPanel = true } = options;
            syncCanvasSelection({
                selectedMarkerId: markerId,
                selectedMarkerIds: markerId ? [markerId] : [],
                selectedFaceId: null,
                selectedSegmentId: null,
                selectedSegmentIds: [],
            });
            const scroll =
                scrollPanel && markerId && markerId !== selectedMarkerId;
            startTransition(() => {
                setSelectedMarkerId(markerId);
                setSelectedMarkerIds(markerId ? [markerId] : []);
                setGroupAnchorMarkerId(markerId);
                setGroupAnchorFlatRectId(null);
                setSelectedFaceId(null);
                setSelectedSegmentId(null);
                setSelectedSegmentIds([]);
            });
            if (scroll) {
                requestAnimationFrame(() =>
                    scrollAssignmentRowToCenter(`layout-assign-marker-${markerId}`)
                );
            }
        },
        [selectedMarkerId, scrollAssignmentRowToCenter, syncCanvasSelection]
    );

    const selectEnclosedFaceById = useCallback(
        (faceId, options = {}) => {
            const { scrollPanel = true, forceScroll = false } = options;
            if (!faceId) return;
            syncCanvasSelection({
                selectedFaceId: faceId,
                selectedMarkerId: null,
                selectedMarkerIds: [],
                selectedSegmentId: null,
                selectedSegmentIds: [],
            });
            const facesForScroll =
                enclosedFacesCacheRef.current.length > 0
                    ? enclosedFacesCacheRef.current
                    : enclosedFaces;
            const faceForScroll = facesForScroll.find((f) => f.id === faceId);
            const flatRectId = faceForScroll
                ? closedRectIdForFace(faceForScroll, layoutSegments)
                : faceId?.startsWith("flat:")
                  ? faceId.slice(5)
                  : null;
            const scroll =
                scrollPanel && (forceScroll || faceId !== selectedFaceId);
            startTransition(() => {
                setSelectedFaceId(faceId);
                setSelectedMarkerId(null);
                setSelectedMarkerIds([]);
                setGroupAnchorMarkerId(null);
                setGroupAnchorFlatRectId(flatRectId);
                setSelectedSegmentId(null);
                setSelectedSegmentIds([]);
            });
            if (scroll) {
                const rectId = flatRectId;
                const rowId = rectId
                    ? `layout-assign-face-${rectId}`
                    : `layout-assign-face-${faceId}`;
                requestAnimationFrame(() =>
                    scrollAssignmentRowToCenter(rowId)
                );
            }
        },
        [
            selectedFaceId,
            layoutSegments,
            enclosedFaces,
            scrollAssignmentRowToCenter,
            syncCanvasSelection,
        ]
    );

    /** Enclosed-area label or fill; in Wall/Arc skips attach snaps so lines can still start/end. */
    const trySelectEnclosedAreaAtPointer = useCallback(
        (canvasX, canvasY, imageX, imageY, e, scaleX, scaleY, options = {}) => {
            if (hasMultiBinGroupSelection(selectedMarkerId, selectedMarkerIds)) {
                return false;
            }
            const { allowWhenLayoutSnap = true } = options;
            const scaleMin = Math.min(scaleX, scaleY);
            if (
                !allowWhenLayoutSnap &&
                (mode === MODES.WALL || mode === MODES.ARC) &&
                snapImageToLayoutPoint(
                    imageX,
                    imageY,
                    markers,
                    layoutSegments,
                    scaleMin,
                    SNAP_TOL_CANVAS_PX
                )
            ) {
                return false;
            }

            let facesForHit =
                enclosedFacesCacheRef.current.length > 0
                    ? enclosedFacesCacheRef.current
                    : enclosedFaces;
            if (
                !facesForHit.length ||
                facesForHit.some((f) => !f.polygon?.length)
            ) {
                try {
                    facesForHit = computeEnclosedFaces(markers, layoutSegments);
                    enclosedFacesCacheRef.current = facesForHit;
                } catch {
                    return false;
                }
            }
            if (!facesForHit.length) return false;

            const canvas = canvasRef.current;
            const ctx = canvas?.getContext("2d");
            const tagOptions = {
                faceLabels,
                faceAssignments,
                siteBins,
                sameBinDataId,
            };

            if (ctx) {
                const tagHit = findFaceTagAtCanvasPoint(
                    canvasX,
                    canvasY,
                    ctx,
                    facesForHit,
                    scaleX,
                    scaleY,
                    tagOptions
                );
                if (tagHit) {
                    const flatRectId = closedRectIdForFace(tagHit, layoutSegments);
                    const flatEditOnFace =
                        (mode === MODES.FLAT || mode === MODES.PLACE) &&
                        flatRectId &&
                        tryBeginFlatRectEdit(
                            imageX,
                            imageY,
                            canvasX,
                            canvasY,
                            scaleX,
                            scaleY,
                            e
                        );
                    if (flatEditOnFace) {
                        return true;
                    }
                    selectEnclosedFaceById(tagHit.id, {
                        scrollPanel: true,
                        forceScroll: true,
                    });
                    return true;
                }
            }

            const faceHit = findFaceAtPoint(imageX, imageY, facesForHit);
            if (faceHit) {
                const flatRectId = closedRectIdForFace(faceHit, layoutSegments);
                const flatEditOnFace =
                    (mode === MODES.FLAT || mode === MODES.PLACE) &&
                    flatRectId &&
                    tryBeginFlatRectEdit(
                        imageX,
                        imageY,
                        canvasX,
                        canvasY,
                        scaleX,
                        scaleY,
                        e
                    );
                if (flatEditOnFace) {
                    return true;
                }
                selectEnclosedFaceById(faceHit.id, {
                    scrollPanel: true,
                    forceScroll: true,
                });
                return true;
            }
            return false;
        },
        [
            mode,
            markers,
            layoutSegments,
            enclosedFaces,
            faceLabels,
            faceAssignments,
            siteBins,
            selectedMarkerId,
            selectedMarkerIds,
            selectEnclosedFaceById,
            tryBeginFlatRectEdit,
        ]
    );

    /** Keep the selected bin or enclosed-area row centered in the assignment panel. */
    useEffect(() => {
        if (groupAnchorFlatRectId) {
            requestAnimationFrame(() =>
                scrollAssignmentRowToCenter(
                    `layout-assign-face-${groupAnchorFlatRectId}`
                )
            );
            return;
        }
        if (selectedFaceId) {
            const faces =
                enclosedFacesCacheRef.current.length > 0
                    ? enclosedFacesCacheRef.current
                    : enclosedFaces;
            const face = faces.find((f) => f.id === selectedFaceId);
            const rectId = face
                ? closedRectIdForFace(face, layoutSegments)
                : selectedFaceId.startsWith("flat:")
                  ? selectedFaceId.slice(5)
                  : null;
            const rowId = rectId
                ? `layout-assign-face-${rectId}`
                : `layout-assign-face-${selectedFaceId}`;
            requestAnimationFrame(() => scrollAssignmentRowToCenter(rowId));
            return;
        }
        const markerId =
            selectedMarkerId ??
            (selectedMarkerIds.length > 0
                ? groupAnchorMarkerId ?? selectedMarkerIds[0]
                : null);
        if (!markerId) return;
        scrollAssignmentRowToCenter(`layout-assign-marker-${markerId}`);
    }, [
        mode,
        selectedFaceId,
        groupAnchorFlatRectId,
        selectedMarkerId,
        selectedMarkerIds,
        groupAnchorMarkerId,
        layoutSegments,
        enclosedFaces,
        layoutAssignmentRows.length,
        scrollAssignmentRowToCenter,
    ]);

    const paintLayoutCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const data = layoutPaintDataRef.current;
        const sel = canvasSelectionRef.current;
        if (!canvas || !data.layoutSurfaceActive) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const cw = canvas.width;
        const ch = canvas.height;
        const scaleX = cw / Math.max(data.imageSize.width, 1);
        const scaleY = ch / Math.max(data.imageSize.height, 1);

        const bgKey = `${cw}x${ch}|${data.imageDataUrl ?? ""}|${data.showUploadedImage}|${data.showGraphGrid}|${data.blankCanvas}|${data.feetPerPixel}|${data.imageSize.width}`;
        let bgHit = layoutBgCacheRef.current?.key === bgKey;
        if (bgHit) {
            ctx.drawImage(layoutBgCacheRef.current.canvas, 0, 0);
        } else {
            ctx.clearRect(0, 0, cw, ch);
            const off = document.createElement("canvas");
            off.width = cw;
            off.height = ch;
            const octx = off.getContext("2d");
            if (octx) {
                const img = imageRef.current;
                if (data.imageDataUrl && data.showUploadedImage && img) {
                    octx.drawImage(img, 0, 0, cw, ch);
                } else {
                    octx.fillStyle = getThemeColor("#fafafa", "#1a1a1a");
                    octx.fillRect(0, 0, cw, ch);
                }
                if (data.showGraphGrid) {
                    const graphTheme =
                        (data.blankCanvas && !data.imageDataUrl) ||
                        (data.imageDataUrl && !data.showUploadedImage)
                            ? {
                                  minor: getThemeColor(
                                      "rgba(33,150,243,0.14)",
                                      "rgba(100,181,246,0.16)"
                                  ),
                                  major: getThemeColor(
                                      "rgba(21,101,192,0.22)",
                                      "rgba(66,165,245,0.26)"
                                  ),
                                  minorWidth: 0.75,
                                  majorWidth: 1,
                                  axis: getThemeColor(
                                      "rgba(13,71,161,0.42)",
                                      "rgba(100,181,246,0.38)"
                                  ),
                                  axisWidth: 1.5,
                              }
                            : {
                                  minor: getThemeColor(
                                      "rgba(33,150,243,0.5)",
                                      "rgba(100,181,246,0.55)"
                                  ),
                                  major: getThemeColor(
                                      "rgba(21,101,192,0.72)",
                                      "rgba(66,165,245,0.78)"
                                  ),
                                  minorWidth: 1.25,
                                  majorWidth: 2,
                                  axis: getThemeColor(
                                      "rgba(13,71,161,0.85)",
                                      "rgba(144,202,249,0.9)"
                                  ),
                                  axisWidth: 2.5,
                              };
                    drawFeetGraphGrid(
                        octx,
                        cw,
                        ch,
                        scaleX,
                        scaleY,
                        data.feetPerPixel,
                        graphTheme,
                        data.imageSize
                    );
                }
                layoutBgCacheRef.current = { key: bgKey, canvas: off };
                ctx.drawImage(off, 0, 0);
                bgHit = true;
            }
        }
        if (!bgHit) {
            ctx.clearRect(0, 0, cw, ch);
        }

        const markersForDraw =
            pendingPlacedMarkersRef.current ??
            interactionMarkersRef.current ??
            nudgeLiveMarkersRef.current ??
            data.markers;
        const segmentsForDraw =
            interactionSegmentsRef.current ??
            nudgeLiveSegmentsRef.current ??
            data.layoutSegments;
        // Full polygons live in enclosedFacesCacheRef; React state holds summaries only.
        let facesForDraw =
            enclosedFacesCacheRef.current.length > 0
                ? enclosedFacesCacheRef.current
                : data.enclosedFaces;
        const facesMissingPolygons =
            facesForDraw.length > 0 && !facesForDraw[0]?.polygon?.length;
        if (
            !data.layoutHeavyGeometry &&
            (facesForDraw.length === 0 || facesMissingPolygons) &&
            (markersForDraw.length > 0 || segmentsForDraw.length > 0) &&
            !layoutGeometryBusyRef.current
        ) {
            try {
                const full = computeEnclosedFaces(markersForDraw, segmentsForDraw);
                enclosedFacesCacheRef.current = full;
                facesForDraw = full;
            } catch (_) {
                /* keep last facesForDraw */
            }
        }

        const groupColors = ["#4fc3f7", "#aed581", "#ffb74d", "#ba68c8", "#f06292", "#90a4ae", "#dce775"];
        // "interacting" = actively dragging geometry or zooming — NOT plain selection.
        const interacting =
            interactionMarkersRef.current != null ||
            interactionSegmentsRef.current != null ||
            nudgeLiveMarkersRef.current != null ||
            nudgeLiveSegmentsRef.current != null ||
            canvasInteractingRef.current;
        const manyFaces = facesForDraw.length > LAYOUT_CANVAS_TAG_FACE_LIMIT;
        // Decimate polygons + skip unassigned fills only while actively interacting on big layouts.
        const liteDraw = interacting && (data.layoutHeavyGeometry || manyFaces);
        const showCanvasFaceTags = !interacting && !manyFaces;
        const skipFacePaint =
            interacting &&
            (data.layoutHeavyGeometry || facesForDraw.length > 12);
        const skipMarkerTags =
            interacting && (data.layoutHeavyGeometry || manyFaces);

        const selIds = sel.selectedMarkerIds;
        const selectedMarkerSet =
            selIds.length > 0 && selIds.length < 300 ? new Set(selIds) : null;

        markersForDraw.forEach((m, i) => {
            const bin =
                m.binDataId != null
                    ? data.siteBinByDataId.get(String(m.binDataId)) ?? null
                    : null;
            const x = m.x * scaleX;
            const y = m.y * scaleY;
            const clusterIdx = data.markerClusterAssignments[i] ?? 0;
            ctx.strokeStyle = groupColors[clusterIdx % groupColors.length];
            ctx.lineWidth = 2;

            const scaleMin = Math.min(scaleX, scaleY);
            const drawRadiusPx = getMarkerDrawRadiusCanvas(
                m,
                bin,
                data.feetPerPixel,
                scaleMin
            );

            ctx.beginPath();
            ctx.arc(x, y, drawRadiusPx, 0, Math.PI * 2);
            ctx.stroke();

            const isSel =
                m.id === sel.selectedMarkerId ||
                (selectedMarkerSet ? selectedMarkerSet.has(m.id) : selIds.includes(m.id));
            if (isSel) {
                ctx.save();
                ctx.strokeStyle = "#ffeb3b";
                ctx.lineWidth = 2;
                ctx.setLineDash([6, 4]);
                ctx.beginPath();
                ctx.arc(x, y, drawRadiusPx, 0, Math.PI * 2);
                ctx.stroke();
                ctx.setLineDash([]);
                if (m.id === sel.selectedMarkerId && sel.mode === MODES.PLACE) {
                    ctx.fillStyle = "#ffeb3b";
                    ctx.beginPath();
                    ctx.arc(x + drawRadiusPx, y, 5, 0, Math.PI * 2);
                    ctx.fill();
                }
                ctx.restore();
            }

            ctx.fillStyle = groupColors[clusterIdx % groupColors.length];
            ctx.beginPath();
            ctx.arc(x, y, 4, 0, Math.PI * 2);
            ctx.fill();

            if (!skipMarkerTags) {
                const slot = data.layoutAreaSlotByMarkerId.get(m.id);
                const tagLabel = getMarkerDisplayLabel(
                    m,
                    slot,
                    data.markerLabels,
                    data.siteBins,
                    markerAreaNamesRef.current
                );
                const tagIx = interacting ? m.x : (slot?.labelPoint?.x ?? m.x);
                const tagIy = interacting ? m.y : (slot?.labelPoint?.y ?? m.y);
                const tagTheme = isSel
                    ? {
                          tagBg: "rgba(255,235,59,0.95)",
                          tagBorder: "#f57f17",
                          tagText: "#1a1a1a",
                      }
                    : m.binDataId != null
                        ? {
                              tagBg: "rgba(200,230,201,0.95)",
                              tagBorder: "#388e3c",
                              tagText: "#1b5e20",
                          }
                        : {
                              tagBg: getThemeColor(
                                  "rgba(255,255,255,0.92)",
                                  "rgba(40,40,40,0.92)"
                              ),
                              tagBorder: getThemeColor(
                                  "rgba(0,0,0,0.35)",
                                  "rgba(244,204,142,0.6)"
                              ),
                              tagText: getThemeColor("#1a1a1a", "#f4cc8e"),
                          };
                drawAreaNameTag(ctx, tagIx * scaleX, tagIy * scaleY, tagLabel, tagTheme);
            }
        });

        const arcBulgeStep =
            sticeDrawRef.current?.kind === "arc" && !!sticeDrawRef.current?.to;

        let previewSeg = null;
        const sd = sticeDrawRef.current;
        const hover = arcBulgeStep ? sticeFreePointRef.current : sticeHoverSnapRef.current;
        if (sd?.from && hover) {
            if (sd.kind === "wall") {
                previewSeg = {
                    kind: "wall",
                    x1: sd.from.x,
                    y1: sd.from.y,
                    x2: hover.x,
                    y2: hover.y,
                };
            } else if (sd.kind === "arc") {
                if (sd.to) {
                    previewSeg = {
                        kind: "arc",
                        x1: sd.from.x,
                        y1: sd.from.y,
                        x2: sd.to.x,
                        y2: sd.to.y,
                        curveX: hover.x,
                        curveY: hover.y,
                        isInner: arcIsInnerFromBulge(
                            sd.from.x,
                            sd.from.y,
                            sd.to.x,
                            sd.to.y,
                            hover.x,
                            hover.y
                        ),
                    };
                } else {
                    previewSeg = {
                        kind: "wall",
                        x1: sd.from.x,
                        y1: sd.from.y,
                        x2: hover.x,
                        y2: hover.y,
                    };
                }
            }
        }
        if (!skipFacePaint && facesForDraw.length > 0) {
            drawEnclosedFaces(ctx, facesForDraw, scaleX, scaleY, {
                selectedFaceId: sel.selectedFaceId,
                faceAssignments: data.faceAssignments,
                faceLabels: data.faceLabels,
                siteBins: data.siteBins,
                sameBinDataId,
                showNameTags: showCanvasFaceTags,
                liteDraw,
                maxPolygonPts: liteDraw ? 28 : 48,
                labelTheme: {
                    tagBg: getThemeColor("rgba(255,255,255,0.92)", "rgba(40,40,40,0.92)"),
                    tagBorder: getThemeColor("rgba(0,0,0,0.35)", "rgba(244,204,142,0.6)"),
                    tagText: getThemeColor("#1a1a1a", "#f4cc8e"),
                },
            });
        }
        drawSticeSegments(ctx, [{ id: "_all", segments: segmentsForDraw }], markersForDraw, scaleX, scaleY, {
            activeSticeId: null,
            selectedSegmentId: sel.selectedSegmentId,
            selectedSegmentIds: sel.selectedSegmentIds,
            previewSegment: previewSeg,
            showEndpointHandles: false,
        });

        const flatRectIdForHandles = sel.flatHandleRectId || sel.flatSelectedRectId;
        if (
            (sel.mode === MODES.FLAT || sel.mode === MODES.PLACE) &&
            flatRectIdForHandles
        ) {
            const flatBounds = getFlatRectBounds(
                segmentsForDraw,
                flatRectIdForHandles
            );
            if (flatBounds) {
                const handleR = FLAT_RECT_CORNER_HIT_TOL_CANVAS / 2;
                const moveR = FLAT_RECT_MOVE_HANDLE_HIT_CANVAS / 2;
                const corners = [
                    [flatBounds.minX, flatBounds.minY],
                    [flatBounds.maxX, flatBounds.minY],
                    [flatBounds.maxX, flatBounds.maxY],
                    [flatBounds.minX, flatBounds.maxY],
                ];
                const movePt = flatRectMoveHandleImagePoint(flatBounds);
                const midX = movePt.x * scaleX;
                const midY = movePt.y * scaleY;
                ctx.save();
                ctx.fillStyle = getThemeColor("#0288d1", "#4fc3f7");
                ctx.strokeStyle = getThemeColor("#fff", "#1a1a1a");
                ctx.lineWidth = 1.5;
                corners.forEach(([ix, iy]) => {
                    const cx = ix * scaleX;
                    const cy = iy * scaleY;
                    ctx.beginPath();
                    ctx.rect(cx - handleR, cy - handleR, handleR * 2, handleR * 2);
                    ctx.fill();
                    ctx.stroke();
                });
                ctx.fillStyle = getThemeColor("#f57c00", "#ffb74d");
                ctx.beginPath();
                ctx.arc(midX, midY, moveR, 0, Math.PI * 2);
                ctx.fill();
                ctx.stroke();
                ctx.restore();
            }
        }

        const guide = moveGuidePointRef.current;
        if (guide) {
            drawAlignmentCrosshairs(
                ctx,
                guide.x,
                guide.y,
                scaleX,
                scaleY,
                canvas.width,
                canvas.height
            );
        }

        if (arcBulgeStep && sd?.from && sd?.to && hover) {
            const hx = hover.x * scaleX;
            const hy = hover.y * scaleY;
            const fx = sd.from.x * scaleX;
            const fy = sd.from.y * scaleY;
            const tx = sd.to.x * scaleX;
            const ty = sd.to.y * scaleY;
            const ext = Math.max(canvas.width, canvas.height) * 3;
            ctx.save();
            ctx.strokeStyle = getThemeColor("rgba(255,235,59,0.55)", "rgba(255,235,59,0.4)");
            ctx.lineWidth = 1;
            ctx.setLineDash([8, 6]);
            ctx.beginPath();
            ctx.moveTo(hx - ext, hy);
            ctx.lineTo(hx + ext, hy);
            ctx.moveTo(hx, hy - ext);
            ctx.lineTo(hx, hy + ext);
            ctx.stroke();
            ctx.setLineDash([4, 4]);
            ctx.strokeStyle = getThemeColor("rgba(255,152,0,0.45)", "rgba(255,183,77,0.35)");
            ctx.beginPath();
            ctx.moveTo(fx, fy);
            ctx.lineTo(tx, ty);
            ctx.stroke();
            ctx.restore();
        }

        const selRect = selectionRectLiveRef.current ?? sel.selectionRect;
        if (selRect) {
            const rx1 = selRect.x1 * scaleX;
            const ry1 = selRect.y1 * scaleY;
            const rw = (selRect.x2 - selRect.x1) * scaleX;
            const rh = (selRect.y2 - selRect.y1) * scaleY;
            ctx.strokeStyle = "#ffeb3b";
            ctx.lineWidth = 1;
            ctx.setLineDash([4, 4]);
            ctx.strokeRect(rx1, ry1, rw, rh);
            ctx.fillStyle = "rgba(255,235,59,0.12)";
            ctx.fillRect(rx1, ry1, rw, rh);
            ctx.setLineDash([]);
        }
    }, []);

    useLayoutEffect(() => {
        paintLayoutCanvasRef.current = paintLayoutCanvas;
    }, [paintLayoutCanvas]);

    useEffect(() => {
        layoutPaintDataRef.current = {
            markers,
            layoutSegments,
            enclosedFaces,
            faceAssignments,
            faceLabels,
            markerLabels,
            imageDataUrl,
            showUploadedImage,
            blankCanvas,
            showGraphGrid,
            feetPerPixel,
            imageSize,
            siteBins,
            siteBinByDataId,
            layoutAreaSlotByMarkerId,
            markerClusterAssignments,
            layoutHeavyGeometry,
            layoutSurfaceActive,
        };
        scheduleLayoutPaint();
    }, [
        markers,
        layoutSegments,
        enclosedFaces,
        faceAssignments,
        faceLabels,
        markerLabels,
        imageDataUrl,
        showUploadedImage,
        blankCanvas,
        showGraphGrid,
        feetPerPixel,
        imageSize,
        siteBins,
        siteBinByDataId,
        layoutAreaSlotByMarkerId,
        markerClusterAssignments,
        layoutHeavyGeometry,
        layoutSurfaceActive,
        scheduleLayoutPaint,
    ]);

    useEffect(() => {
        canvasSelectionRef.current = {
            selectedMarkerId,
            selectedMarkerIds,
            selectedFaceId,
            selectedSegmentId,
            selectedSegmentIds,
            mode,
            flatHandleRectId,
            flatSelectedRectId,
            selectionRect,
        };
        scheduleLayoutPaint();
    }, [
        selectedMarkerId,
        selectedMarkerIds,
        selectedFaceId,
        selectedSegmentId,
        selectedSegmentIds,
        mode,
        flatHandleRectId,
        flatSelectedRectId,
        selectionRect,
        scheduleLayoutPaint,
    ]);

    useEffect(() => {
        if (!layoutSurfaceActive) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const { width, height } = canvasSize;
        if (canvas.width !== width || canvas.height !== height) {
            resizeLayoutCanvasPreserve(canvas, width, height);
            layoutBgCacheRef.current = null;
        }
        if (!zoomPreviewActiveRef.current) {
            scheduleLayoutPaint();
        }
    }, [
        canvasSize.width,
        canvasSize.height,
        layoutSurfaceActive,
        scheduleLayoutPaint,
    ]);

    const handleFileChange = useCallback((e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setStatusMessage("Loading image…");
        const reader = new FileReader();
        reader.onload = async (ev) => {
            const dataUrl = ev.target.result;
            const img = new Image();
            img.onload = async () => {
                try {
                    const { displayImg, displayUrl, width, height } = await prepareLayoutImage(img, dataUrl);
                    const hadWork =
                        markers.length > 0 ||
                        layoutSegments.length > 0 ||
                        Object.keys(faceAssignments).length > 0 ||
                        Object.keys(faceLabels).length > 0 ||
                        Object.keys(markerLabels).length > 0;
                    const oldW = Math.max(
                        imageSize.width || BLANK_LAYOUT_SIZE.width,
                        1
                    );
                    const oldH = Math.max(
                        imageSize.height || BLANK_LAYOUT_SIZE.height,
                        1
                    );
                    const resizedLayout =
                        hadWork &&
                        (Math.abs(oldW - width) > 1 || Math.abs(oldH - height) > 1);
                    recordHistory();
                    if (resizedLayout) {
                        const scaled = scaleLayoutGeometryForImageResize(
                            markers,
                            layoutSegments,
                            width / oldW,
                            height / oldH
                        );
                        setMarkers(scaled.markers);
                        setLayoutSegments(scaled.segments);
                    }
                    imageRef.current = displayImg;
                    setBlankCanvas(false);
                    setShowUploadedImage(true);
                    setImageSize({ width, height });
                    setImageDataUrl(displayUrl);
                    setSelectedFaceId(null);
                    setFeetPerPixel(defaultLayoutFeetPerPixel(siteBins));
                    skipPruneUntilRef.current = Date.now() + 2500;
                    setEnclosureRequestId((id) => id + 1);
                    resetViewportToDefault();
                    const shrunk = width < img.naturalWidth || height < img.naturalHeight;
                    setStatusMessage(
                        hadWork
                            ? resizedLayout
                                ? shrunk
                                    ? `Image loaded (${width}×${height}, resized). Layout geometry scaled to the new photo.`
                                    : `Image loaded (${width}×${height}). Layout geometry scaled to the new photo.`
                                : shrunk
                                    ? `Image loaded (${width}×${height}, resized). Bins, walls, and areas kept.`
                                    : `Image loaded (${width}×${height}). Bins, walls, and areas kept.`
                            : shrunk
                                ? `Image loaded (${width}×${height}, resized for performance). Place bins or use Detect bins.`
                                : `Image loaded (${width}×${height}). Place bins or use Detect bins.`
                    );
                } catch (err) {
                    setStatusMessage(`Could not load image: ${err?.message ?? err}`);
                }
            };
            img.onerror = () => setStatusMessage("Could not read that image file.");
            img.src = dataUrl;
        };
        reader.onerror = () => setStatusMessage("Could not read that image file.");
        reader.readAsDataURL(file);
        e.target.value = "";
    }, [
        resetViewportToDefault,
        recordHistory,
        markers,
        layoutSegments,
        faceAssignments,
        faceLabels,
        markerLabels,
        imageSize.width,
        imageSize.height,
    ]);

    const handleRemoveImage = useCallback(() => {
        if (!imageDataUrl) return;
        recordHistory();
        setImageDataUrl(null);
        imageRef.current = null;
        setBlankCanvas(true);
        setShowUploadedImage(true);
        const input = document.getElementById("site-layout-image-input");
        if (input) input.value = "";
        setStatusMessage(
            "Image removed. Bins, walls, and arcs are unchanged on the blank canvas."
        );
    }, [imageDataUrl, recordHistory]);

    // Sync canvas pixel size to the canvas column (stable when mode toolbar overlays change).
    useEffect(() => {
        const updateSize = () => {
            const el = containerRef.current;
            if (!el || !layoutSurfaceActive) return;
            const containerWidth = el.clientWidth;
            const measuredH = el.clientHeight;
            const containerHeight =
                measuredH > 80 ? measuredH : Math.max(400, window.innerHeight - 280);
            const aspect = (imageSize.width || 1) / (imageSize.height || 1);
            let w = containerWidth;
            let h = containerWidth / aspect;
            if (h > containerHeight) {
                h = containerHeight;
                w = h * aspect;
            }
            const baseW = Math.floor(w);
            const baseH = Math.floor(h);
            const prevBase = prevBaseCanvasSizeRef.current;
            const viewport = viewportRef.current;
            if (
                viewport?.clientWidth > 0 &&
                prevBase.width > 0 &&
                prevBase.height > 0 &&
                (prevBase.width !== baseW || prevBase.height !== baseH)
            ) {
                const z = viewZoomRef.current;
                const oldW = prevBase.width * z;
                const oldH = prevBase.height * z;
                const newW = baseW * z;
                const newH = baseH * z;
                const pan = viewPanLiveRef.current;
                const u = oldW > 0 ? (viewport.clientWidth / 2 - pan.x) / oldW : 0.5;
                const v = oldH > 0 ? (viewport.clientHeight / 2 - pan.y) / oldH : 0.5;
                const nextPan = {
                    x: viewport.clientWidth / 2 - u * newW,
                    y: viewport.clientHeight / 2 - v * newH,
                };
                cancelViewPanStateSync();
                viewPanLiveRef.current = nextPan;
                setViewPan((prev) => {
                    if (prev.x === nextPan.x && prev.y === nextPan.y) return prev;
                    return nextPan;
                });
            }
            prevBaseCanvasSizeRef.current = { width: baseW, height: baseH };
            baseCanvasSizeRef.current = { width: baseW, height: baseH };
            setCanvasSize(scaledCanvasPixelSize(baseCanvasSizeRef.current, viewZoom));
            if (!viewportInitRef.current && baseW > 0) {
                viewportInitRef.current = true;
                requestAnimationFrame(() => fitGraphInViewport());
            }
        };
        updateSize();
        window.addEventListener("resize", updateSize);
        const el = containerRef.current;
        let ro;
        if (el && typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(() => updateSize());
            ro.observe(el);
        }
        return () => {
            window.removeEventListener("resize", updateSize);
            ro?.disconnect();
        };
    }, [imageSize, layoutSurfaceActive, fitGraphInViewport, viewZoom, cancelViewPanStateSync]);

    // Convert a mouse event into image-space coordinates.
    const eventToImagePos = useCallback((e) => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        const canvasX = e.clientX - rect.left;
        const canvasY = e.clientY - rect.top;
        const x = (canvasX / Math.max(rect.width, 1)) * imageSize.width;
        const y = (canvasY / Math.max(rect.height, 1)) * imageSize.height;
        return { x, y, canvasX, canvasY, rect };
    }, [imageSize]);

    const pointerHitSlopPx = (e) =>
        e?.pointerType === "touch" || e?.pointerType === "pen" ? 28 : 0;

    // Hit-test marker center (small target — use the ring to resize).
    // `minCanvasPx` enlarges the target for coarse pointers (finger / stylus).
    const findMarkerAtCanvasPos = useCallback((canvasX, canvasY, rectWidth, rectHeight, minCanvasPx = 0) => {
        if (!markers.length || !rectWidth || !rectHeight) return null;
        const scaleX = rectWidth / Math.max(imageSize.width, 1);
        const scaleY = rectHeight / Math.max(imageSize.height, 1);
        const scaleMin = Math.min(scaleX, scaleY) || 1;
        const imageX = canvasX / scaleX;
        const imageY = canvasY / scaleY;
        let best = null;
        for (const m of markers) {
            const d = Math.hypot(imageX - m.x, imageY - m.y);
            const hitRadius = Math.max(
                getMarkerRadiusImagePx(m) * 0.45,
                minCanvasPx / scaleMin,
            );
            if (d <= hitRadius && (!best || d < best.dist)) best = { marker: m, dist: d };
        }
        return best?.marker ?? null;
    }, [markers, imageSize]);

    const findMarkerResizeAtCanvasPos = useCallback((canvasX, canvasY, rectWidth, rectHeight) => {
        if (!selectedMarkerId || !rectWidth || !rectHeight) return false;
        const m = markers.find((mk) => mk.id === selectedMarkerId);
        if (!m) return false;
        const scaleX = rectWidth / Math.max(imageSize.width, 1);
        const scaleY = rectHeight / Math.max(imageSize.height, 1);
        const scaleMin = Math.min(scaleX, scaleY);
        const bin = siteBins.find(b => sameBinDataId(b.binDataId, m.binDataId));
        const drawR = getMarkerDrawRadiusCanvas(m, bin, feetPerPixel, scaleMin);
        const cx = m.x * scaleX;
        const cy = m.y * scaleY;
        const d = Math.hypot(canvasX - cx, canvasY - cy);
        return d >= drawR * 0.75 && d <= drawR * 1.25;
    }, [markers, selectedMarkerId, imageSize, siteBins, feetPerPixel]);

    const commitViewportZoomBitmap = useCallback(() => {
        if (zoomCommitTimerRef.current) {
            clearTimeout(zoomCommitTimerRef.current);
            zoomCommitTimerRef.current = null;
        }
        if (panActiveRef.current) {
            pendingZoomCommitAfterPanRef.current = true;
            return;
        }
        if (!layoutSurfaceActive) return;
        const z = viewZoomRef.current;
        const committed = committedViewZoomRef.current;
        if (Math.abs(z - committed) < 0.001) {
            applyCanvasWrapperTransform();
            return;
        }
        const base = baseCanvasSizeRef.current;
        const nextSize = scaledCanvasPixelSize(base, z);
        const pan = viewPanLiveRef.current;
        const canvas = canvasRef.current;
        if (canvas) {
            resizeLayoutCanvasPreserve(canvas, nextSize.width, nextSize.height);
        }
        zoomPreviewActiveRef.current = false;
        viewZoomRef.current = z;
        committedViewZoomRef.current = z;
        viewPanLiveRef.current = pan;
        applyCanvasWrapperTransform();
        layoutBgCacheRef.current = null;
        setViewZoom(z);
        setCanvasSize(nextSize);
        setViewPan({ x: pan.x, y: pan.y });
        if (!layoutGeometryBusyRef.current) {
            canvasInteractingRef.current = false;
        }
        scheduleLayoutPaint();
    }, [
        layoutSurfaceActive,
        applyCanvasWrapperTransform,
        scheduleLayoutPaint,
    ]);

    const scheduleViewportZoomCommit = useCallback(() => {
        if (panActiveRef.current) {
            pendingZoomCommitAfterPanRef.current = true;
            return;
        }
        if (zoomCommitTimerRef.current) clearTimeout(zoomCommitTimerRef.current);
        zoomCommitTimerRef.current = setTimeout(
            commitViewportZoomBitmap,
            VIEW_ZOOM_COMMIT_MS
        );
    }, [commitViewportZoomBitmap]);

    /** CSS-scale preview first; debounced bitmap resize avoids lag on wheel/button spam. */
    const applyViewportZoomTo = useCallback(
        (targetZoom, clientX = null, clientY = null) => {
            if (!layoutSurfaceActive) return;
            const z0 = viewZoomRef.current;
            const z1 = clampViewZoom(targetZoom);
            if (z1 === z0) return;

            cancelViewPanStateSync();
            canvasInteractingRef.current = true;
            zoomPreviewActiveRef.current = true;

            const viewport = viewportRef.current;
            if (!viewport) {
                viewZoomRef.current = z1;
                committedViewZoomRef.current = z1;
                setViewZoom(z1);
                return;
            }
            let vx;
            let vy;
            if (clientX != null && clientY != null) {
                const vRect = viewport.getBoundingClientRect();
                vx = clientX - vRect.left;
                vy = clientY - vRect.top;
            } else {
                vx = viewport.clientWidth / 2;
                vy = viewport.clientHeight / 2;
            }
            const pan = viewPanLiveRef.current;
            const base = baseCanvasSizeRef.current;
            const committed = committedViewZoomRef.current;
            const bitmap = scaledCanvasPixelSize(base, committed);
            const displayWBefore = bitmap.width * (z0 / committed);
            const displayHBefore = bitmap.height * (z0 / committed);
            const displayWAfter = bitmap.width * (z1 / committed);
            const displayHAfter = bitmap.height * (z1 / committed);
            const u = displayWBefore > 0 ? (vx - pan.x) / displayWBefore : 0;
            const v = displayHBefore > 0 ? (vy - pan.y) / displayHBefore : 0;
            viewZoomRef.current = z1;
            viewPanLiveRef.current = {
                x: vx - u * displayWAfter,
                y: vy - v * displayHAfter,
            };
            applyCanvasWrapperTransform();
            scheduleViewportZoomCommit();
        },
        [
            layoutSurfaceActive,
            applyCanvasWrapperTransform,
            scheduleViewportZoomCommit,
            cancelViewPanStateSync,
        ]
    );

    const flushQueuedZoomButtonSteps = useCallback(() => {
        zoomButtonRafRef.current = null;
        const steps = zoomButtonStepsRef.current;
        zoomButtonStepsRef.current = 0;
        if (!steps) return;
        applyViewportZoomTo(
            viewZoomRef.current + steps * VIEW_ZOOM_STEP,
            null,
            null
        );
    }, [applyViewportZoomTo]);

    const queueZoomButtonStep = useCallback(
        (stepSign) => {
            if (!layoutSurfaceActive) return;
            zoomButtonStepsRef.current += stepSign;
            if (zoomButtonRafRef.current != null) return;
            zoomButtonRafRef.current = requestAnimationFrame(flushQueuedZoomButtonSteps);
        },
        [layoutSurfaceActive, flushQueuedZoomButtonSteps]
    );

    /** stepSign: +1 zoom in, -1 zoom out. Buttons batch steps per animation frame. */
    const zoomViewportFromStep = useCallback(
        (stepSign, clientX = null, clientY = null, { fromButton = false } = {}) => {
            if (fromButton) {
                queueZoomButtonStep(stepSign);
                return;
            }
            const delta = stepSign > 0 ? VIEW_ZOOM_STEP : -VIEW_ZOOM_STEP;
            applyViewportZoomTo(viewZoomRef.current + delta, clientX, clientY);
        },
        [applyViewportZoomTo, queueZoomButtonStep]
    );

    const flushQueuedWheelZoom = useCallback(() => {
        wheelZoomRafRef.current = null;
        const steps = wheelZoomAccumRef.current;
        wheelZoomAccumRef.current = 0;
        if (!steps) return;
        const { x, y } = wheelZoomClientRef.current;
        applyViewportZoomTo(
            viewZoomRef.current + steps * VIEW_ZOOM_STEP,
            x,
            y
        );
    }, [applyViewportZoomTo]);

    const handleViewportWheel = useCallback((e) => {
        if (!layoutSurfaceActive) return;
        const resizeMarker =
            mode === MODES.PLACE && selectedMarkerId && (e.ctrlKey || e.metaKey);
        if (resizeMarker) {
            e.preventDefault();
            const m = markers.find((mk) => mk.id === selectedMarkerId);
            if (!m) return;
            const base = getMarkerRadiusImagePx(m);
            const delta = e.deltaY < 0 ? 2 : -2;
            const nextR = Math.max(6, base + delta);
            setMarkers((prev) =>
                prev.map((mk) => (mk.id === selectedMarkerId ? { ...mk, radiusPx: nextR } : mk))
            );
            return;
        }
        e.preventDefault();
        wheelZoomAccumRef.current += e.deltaY < 0 ? 1 : -1;
        wheelZoomClientRef.current = { x: e.clientX, y: e.clientY };
        if (wheelZoomRafRef.current != null) return;
        wheelZoomRafRef.current = requestAnimationFrame(flushQueuedWheelZoom);
    }, [
        layoutSurfaceActive,
        mode,
        selectedMarkerId,
        markers,
        flushQueuedWheelZoom,
    ]);

    const viewZoomAtMin = viewZoom <= MIN_VIEW_ZOOM;
    const viewZoomAtMax = viewZoom >= MAX_VIEW_ZOOM;

    useEffect(() => {
        const el = viewportRef.current;
        if (!el || !layoutSurfaceActive) return undefined;
        const onWheel = (e) => handleViewportWheel(e);
        el.addEventListener("wheel", onWheel, { passive: false });
        return () => el.removeEventListener("wheel", onWheel);
    }, [layoutSurfaceActive, handleViewportWheel]);

    const toggleObjectInGroupSelection = useCallback(
        ({ markerHit, segHit, flatRectHit }) => {
            if (flatRectHit) {
                const bounds = getFlatRectBounds(layoutSegments, flatRectHit);
                const wallIds = bounds?.segmentIds ?? [];
                const allIn =
                    wallIds.length > 0 &&
                    wallIds.every((id) => selectedSegmentIds.includes(id));
                setSelectedSegmentIds((prev) => {
                    if (allIn) {
                        return prev.filter((id) => !wallIds.includes(id));
                    }
                    return [...new Set([...prev, ...wallIds])];
                });
                setSelectedSegmentId(wallIds[0] ?? null);
                if (!allIn) setGroupAnchorFlatRectId(flatRectHit);
                return;
            }
            if (segHit) {
                const seg = layoutSegments.find((s) => s.id === segHit.id);
                if (seg?.closedRectId) {
                    const b = getFlatRectBounds(layoutSegments, seg.closedRectId);
                    const wallIds = b?.segmentIds ?? [];
                    const allIn =
                        wallIds.length > 0 &&
                        wallIds.every((id) => selectedSegmentIds.includes(id));
                    setSelectedSegmentIds((prev) => {
                        if (allIn) {
                            return prev.filter((id) => !wallIds.includes(id));
                        }
                        return [...new Set([...prev, ...wallIds])];
                    });
                    setSelectedSegmentId(segHit.id);
                    if (!allIn) setGroupAnchorFlatRectId(seg.closedRectId);
                } else {
                    setSelectedSegmentIds((prev) => {
                        const has = prev.includes(segHit.id);
                        return has
                            ? prev.filter((id) => id !== segHit.id)
                            : [...prev, segHit.id];
                    });
                    setSelectedSegmentId(segHit.id);
                }
                return;
            }
            if (markerHit) {
                setSelectedMarkerIds((prev) => {
                    const has = prev.includes(markerHit.id);
                    return has
                        ? prev.filter((id) => id !== markerHit.id)
                        : [...prev, markerHit.id];
                });
                setSelectedMarkerId(markerHit.id);
                setGroupAnchorMarkerId(markerHit.id);
                setGroupAnchorFlatRectId(null);
            }
        },
        [layoutSegments, selectedSegmentIds]
    );

    const handleGroupRightPointerDown = useCallback(
        (e, pos) => {
            const { x, y, canvasX, canvasY, rect } = pos;
            const scaleX = rect.width / Math.max(imageSize.width, 1);
            const scaleY = rect.height / Math.max(imageSize.height, 1);
            const flatRectHit = findFlatRectAtPoint(layoutSegments, x, y);
            const segHit = findSegmentAtCanvas(
                layoutSegments,
                markers,
                canvasX,
                canvasY,
                scaleX,
                scaleY,
                24
            );
            const markerHit = findMarkerAtCanvasPos(
                canvasX,
                canvasY,
                rect.width,
                rect.height
            );
            const hasObjectHit = !!(segHit || markerHit || flatRectHit);
            const ctrl = isMultiSelectKey(e);
            if (!hasObjectHit) {
                if (!ctrl && hasAnyGroupSelection()) {
                    groupRightPickRef.current = {
                        empty: true,
                        x,
                        y,
                        clientX: e.clientX,
                        clientY: e.clientY,
                    };
                    return;
                }
                groupRightPickRef.current = null;
                marqueeRef.current = {
                    x1: x,
                    y1: y,
                    x2: x,
                    y2: y,
                    additive: ctrl,
                };
                setSelectionRect({ x1: x, y1: y, x2: x, y2: y });
                return;
            }
            groupRightPickRef.current = {
                x,
                y,
                canvasX,
                canvasY,
                canvasW: rect.width,
                canvasH: rect.height,
                clientX: e.clientX,
                clientY: e.clientY,
                markerHit,
                segHit,
                flatRectHit,
                ctrl,
            };
        },
        [markers, layoutSegments, imageSize, hasAnyGroupSelection]
    );

    const tryBeginGroupDragOnPointerDown = useCallback(
        (e, pos) => {
            const { x, y, canvasX, canvasY, rect } = pos;
            const groupMarkerIds = resolveGroupMarkerIds(
                selectedMarkerId,
                selectedMarkerIds
            );
            const groupSegmentIds = resolveGroupSegmentIds(
                selectedSegmentId,
                selectedSegmentIds
            );
            if (groupMarkerIds.length === 0 && groupSegmentIds.length === 0) {
                return false;
            }

            const scaleX = rect.width / Math.max(imageSize.width, 1);
            const scaleY = rect.height / Math.max(imageSize.height, 1);
            /** Flat corners must not cancel multi-bin group drag (bins often sit on/near flats). */
            if (
                groupMarkerIds.length < 2 &&
                findFlatRectCornerHit(
                    layoutSegments,
                    x,
                    y,
                    scaleX,
                    scaleY
                )
            ) {
                return false;
            }
            const flatRectHit = findFlatRectAtPoint(layoutSegments, x, y);
            const flatOnlySelected =
                groupMarkerIds.length === 0 &&
                groupSegmentIds.length > 0 &&
                flatRectIdsFromSegmentIds(layoutSegments, groupSegmentIds)
                    .length === 1;
            if (flatRectHit && flatOnlySelected) {
                return false;
            }
            const segHit = findSegmentAtCanvas(
                layoutSegments,
                markers,
                canvasX,
                canvasY,
                scaleX,
                scaleY,
                14
            );
            const markerHit = findMarkerAtCanvasPos(
                canvasX,
                canvasY,
                rect.width,
                rect.height,
                pointerHitSlopPx(e)
            );

            if (
                !shouldStartGroupDrag({
                    x,
                    y,
                    markerHit,
                    segHit,
                    flatRectHit,
                    selectedMarkerIds: groupMarkerIds,
                    selectedSegmentIds: groupSegmentIds,
                    markers,
                    layoutSegments,
                })
            ) {
                return false;
            }

            recordHistory();
            const segmentOrigins = new Map();
            const dragSegmentIds = expandSegmentIdsToFullFlatRects(
                layoutSegments,
                groupSegmentIds
            );
            dragSegmentIds.forEach((id) => {
                const s = layoutSegments.find((seg) => seg.id === id);
                if (s) segmentOrigins.set(id, cloneSegmentForDrag(s));
            });
            layoutGeometryBusyRef.current = true;
            groupDragRef.current = {
                startX: x,
                startY: y,
                markerOrigins: new Map(
                    markers
                        .filter((m) => groupMarkerIds.includes(m.id))
                        .map((m) => [m.id, { x: m.x, y: m.y }])
                ),
                segmentOrigins,
            };
            return true;
        },
        [
            markers,
            layoutSegments,
            selectedMarkerId,
            selectedMarkerIds,
            selectedSegmentId,
            selectedSegmentIds,
            imageSize,
            recordHistory,
        ]
    );

    const handleGroupSelectionPointerDown = useCallback(
        (e, pos) => {
            const { x, y, canvasX, canvasY, rect } = pos;
            const scaleX = rect.width / Math.max(imageSize.width, 1);
            const scaleY = rect.height / Math.max(imageSize.height, 1);

            const segHit = findSegmentAtCanvas(
                layoutSegments,
                markers,
                canvasX,
                canvasY,
                scaleX,
                scaleY,
                14
            );
            const markerHit = findMarkerAtCanvasPos(
                canvasX,
                canvasY,
                rect.width,
                rect.height,
                pointerHitSlopPx(e)
            );

            const flatRectHit = findFlatRectAtPoint(layoutSegments, x, y);

            if (flatRectHit) {
                selectFlatRect(flatRectHit, { scrollPanel: true });
                return true;
            }

            if (segHit) {
                selectSegmentById(segHit.id, layoutSegments);
                return true;
            }

            if (markerHit && mode !== MODES.PLACE) {
                setSelectedMarkerIds([markerHit.id]);
                setSelectedMarkerId(markerHit.id);
                setGroupAnchorMarkerId(markerHit.id);
                setGroupAnchorFlatRectId(null);
                setSelectedSegmentIds([]);
                setSelectedSegmentId(null);
                setSelectedFaceId(null);
                return true;
            }

            if (!flatRectHit && !segHit && !markerHit) {
                if (hasAnyGroupSelection()) {
                    clearGroupSelection();
                }
                if (mode === MODES.WALL || mode === MODES.ARC) {
                    emptyDragRef.current = {
                        clientX: e.clientX,
                        clientY: e.clientY,
                        panX: viewPanLiveRef.current.x,
                        panY: viewPanLiveRef.current.y,
                    };
                    return true;
                }
            }

            return false;
        },
        [
            mode,
            markers,
            layoutSegments,
            imageSize,
            selectFlatRect,
            selectSegmentById,
            viewPan.x,
            viewPan.y,
            hasAnyGroupSelection,
            clearGroupSelection,
        ]
    );

    const handleViewportMouseDown = useCallback((e) => {
        if (!layoutSurfaceActive) return;
        const pan =
            e.button === 1 ||
            (e.button === 0 && (spaceHeldRef.current || e.altKey));
        if (!pan) return;
        panDragRef.current = {
            x: e.clientX,
            y: e.clientY,
            panX: viewPanLiveRef.current.x,
            panY: viewPanLiveRef.current.y,
        };
        beginPanGesture();
        e.preventDefault();
    }, [layoutSurfaceActive, beginPanGesture]);

    const handleCanvasMouseDown = useCallback((e) => {
        if (!layoutSurfaceActive) return;
        const hitSlop = pointerHitSlopPx(e);
        if (e.button === 2) {
            const pos = eventToImagePos(e);
            if (!pos) return;
            handleGroupRightPointerDown(e, pos);
            e.preventDefault();
            return;
        }
        if (
            e.button === 1 ||
            (e.button === 0 && (spaceHeldRef.current || e.altKey))
        ) {
            handleViewportMouseDown(e);
            return;
        }
        const pos = eventToImagePos(e);
        if (!pos) return;
        const { x, y, canvasX, canvasY, rect } = pos;

        const scaleX = rect.width / Math.max(imageSize.width, 1);
        const scaleY = rect.height / Math.max(imageSize.height, 1);

        if (isMultiSelectKey(e)) {
            const markerHit = findMarkerAtCanvasPos(
                canvasX,
                canvasY,
                rect.width,
                rect.height,
                hitSlop
            );
            if (markerHit) {
                toggleObjectInGroupSelection({ markerHit });
                e.preventDefault();
                return;
            }
            const flatRectHit = findFlatRectAtPoint(layoutSegments, x, y);
            if (flatRectHit) {
                toggleObjectInGroupSelection({ flatRectHit });
                e.preventDefault();
                return;
            }
        }

        const groupMarkerIds = resolveGroupMarkerIds(
            selectedMarkerId,
            selectedMarkerIds
        );

        /** Multi-bin group move wins over flat corner/body hits (PLACE mode used to steal drags). */
        if (groupMarkerIds.length >= 2) {
            if (tryBeginGroupDragOnPointerDown(e, pos)) {
                e.preventDefault();
                return;
            }
        } else if (
            mode === MODES.FLAT &&
            tryBeginFlatRectEdit(x, y, canvasX, canvasY, scaleX, scaleY, e)
        ) {
            e.preventDefault();
            return;
        }

        if (tryBeginGroupDragOnPointerDown(e, pos)) {
            e.preventDefault();
            return;
        }

        if (mode === MODES.FLAT) {
            if (
                trySelectEnclosedAreaAtPointer(
                    canvasX,
                    canvasY,
                    x,
                    y,
                    e,
                    scaleX,
                    scaleY
                )
            ) {
                e.preventDefault();
                return;
            }
            if (handleGroupSelectionPointerDown(e, pos)) {
                e.preventDefault();
                return;
            }
            emptyDragRef.current = {
                clientX: e.clientX,
                clientY: e.clientY,
                panX: viewPanLiveRef.current.x,
                panY: viewPanLiveRef.current.y,
                flatPlaceX: x,
                flatPlaceY: y,
            };
            e.preventDefault();
            return;
        }

        if (mode === MODES.PLACE) {
            const markerPick = findMarkerAtCanvasPos(
                canvasX,
                canvasY,
                rect.width,
                rect.height,
                hitSlop
            );
            if (markerPick) {
                if (
                    groupMarkerIds.length >= 2 &&
                    groupMarkerIds.includes(markerPick.id) &&
                    tryBeginGroupDragOnPointerDown(e, pos)
                ) {
                    e.preventDefault();
                    return;
                }
                selectBinMarkerById(markerPick.id);
                layoutGeometryBusyRef.current = true;
                dragRef.current = {
                    markerId: markerPick.id,
                    startX: x,
                    startY: y,
                    moved: false,
                };
                e.preventDefault();
                return;
            }
            if (
                !hasMultiBinGroupSelection(selectedMarkerId, selectedMarkerIds) &&
                trySelectEnclosedAreaAtPointer(
                    canvasX,
                    canvasY,
                    x,
                    y,
                    e,
                    scaleX,
                    scaleY
                )
            ) {
                e.preventDefault();
                return;
            }
        }

        if (mode === MODES.WALL || mode === MODES.ARC) {
            const scaleMin = Math.min(scaleX, scaleY);

            if (markers.length < 2) {
                setStatusMessage("Place at least two round bins before drawing walls or arcs.");
                return;
            }

            const sdEarly = sticeDrawRef.current;
            const snap = snapImageToLayoutPoint(
                x,
                y,
                markers,
                layoutSegments,
                scaleMin,
                SNAP_TOL_CANVAS_PX
            );

            const arcBulgeStep =
                mode === MODES.ARC && sdEarly?.kind === "arc" && sdEarly?.from && sdEarly?.to;
            const wallEndPt = snap || { x, y };

            if (mode === MODES.WALL) {
                const sd = sticeDrawRef.current;
                if (!sd || sd.kind !== "wall" || !sd.from) {
                    sticeDrawRef.current = { kind: "wall", from: wallEndPt };
                    setSticeHoverSnap(wallEndPt);
                    setStatusMessage(
                        "Wall: click end point (snaps to bins/walls when near)."
                    );
                    return;
                }
                if (!layoutPointsAllowWall(sd.from, wallEndPt)) {
                    setStatusMessage(
                        "Wall must span two different points (at least 2 px apart)."
                    );
                    return;
                }
                recordHistory();
                const seg = createSegmentBetweenLayoutPoints(
                    sd.from,
                    wallEndPt,
                    "wall",
                    null,
                    markers,
                    layoutSegments
                );
                if (!seg || !resolveSegmentGeometry(seg, markers, layoutSegments)) {
                    setStatusMessage("Could not create wall.");
                    return;
                }
                setLayoutSegments((prev) => [...prev, seg]);
                sticeDrawRef.current = null;
                setSticeHoverSnap(null);
                setEnclosureRequestId((id) => id + 1);
                setStatusMessage(
                    "Wall added. Enclosed areas appear when a region closes."
                );
                return;
            }

            if (!arcBulgeStep && !snap) {
                if (
                    trySelectEnclosedAreaAtPointer(
                        canvasX,
                        canvasY,
                        x,
                        y,
                        e,
                        scaleX,
                        scaleY,
                        { allowWhenLayoutSnap: false }
                    )
                ) {
                    sticeDrawRef.current = null;
                    setSticeHoverSnap(null);
                    setSticeFreePoint(null);
                    e.preventDefault();
                    return;
                }
            }

            if (!snap && !sdEarly?.from) {
                const hit = findSegmentAtCanvas(
                    layoutSegments,
                    markers,
                    canvasX,
                    canvasY,
                    scaleX,
                    scaleY,
                    14
                );
                if (hit) {
                    selectSegmentById(hit.id, layoutSegments);
                    return;
                }
            }

            if (arcBulgeStep) {
                if (!sdEarly.from || !sdEarly.to) return;
                recordHistory();
                const seg = createSegmentBetweenSnaps(
                    sdEarly.from,
                    sdEarly.to,
                    "arc",
                    { x, y }
                );
                if (!seg || !resolveSegmentGeometry(seg, markers, layoutSegments)) {
                    setStatusMessage("Arc must start and end on snap points.");
                    return;
                }
                setLayoutSegments((prev) => [...prev, seg]);
                sticeDrawRef.current = null;
                setSticeHoverSnap(null);
                setSticeFreePoint(null);
                setEnclosureRequestId((id) => id + 1);
                setStatusMessage(
                    "Arc added. A closed flat area appears when the arc and bin edge form a loop."
                );
                return;
            }

            if (!snap) {
                setStatusMessage(
                    "Click near a bin edge, wall/arc end, or wall/arc midpoint to start."
                );
                if (hasAnyGroupSelection()) {
                    clearGroupSelection();
                }
                emptyDragRef.current = {
                    clientX: e.clientX,
                    clientY: e.clientY,
                    panX: viewPanLiveRef.current.x,
                    panY: viewPanLiveRef.current.y,
                    placeX: null,
                    placeY: null,
                };
                return;
            }

            if (mode === MODES.ARC) {
                const sd = sticeDrawRef.current;
                if (!sd || sd.kind !== "arc" || !sd.from) {
                    sticeDrawRef.current = { kind: "arc", from: snap };
                    setStatusMessage("Arc: click end point near a bin edge or wall/arc end.");
                    return;
                }
                if (!sd.to) {
                    if (!snapsAllowWall(sd.from, snap)) {
                        setStatusMessage("Arc end must be a different snap point than the start.");
                        return;
                    }
                    sticeDrawRef.current = { kind: "arc", from: sd.from, to: snap };
                    setSticeHoverSnap(null);
                    setSticeFreePoint(null);
                    setStatusMessage("Arc: click on the image to set the curve (does not snap to bins).");
                    return;
                }
            }
        }

        if (handleGroupSelectionPointerDown(e, pos)) {
            e.preventDefault();
            return;
        }

        // PLACE: resize ring on selected marker, else drag center, else place new.
        if (
            mode === MODES.PLACE &&
            selectedMarkerId &&
            findMarkerResizeAtCanvasPos(canvasX, canvasY, rect.width, rect.height)
        ) {
            layoutGeometryBusyRef.current = true;
            resizeRef.current = { markerId: selectedMarkerId };
            return;
        }

        const hit = findMarkerAtCanvasPos(canvasX, canvasY, rect.width, rect.height, hitSlop);
        if (hit) {
            selectBinMarkerById(hit.id);
            layoutGeometryBusyRef.current = true;
            dragRef.current = { markerId: hit.id, startX: x, startY: y, moved: false };
            return;
        }
        if (hasAnyGroupSelection()) {
            clearGroupSelection();
        }
        const placeX = mode === MODES.PLACE ? x : null;
        const placeY = mode === MODES.PLACE ? y : null;
        emptyDragRef.current = {
            clientX: e.clientX,
            clientY: e.clientY,
            panX: viewPanLiveRef.current.x,
            panY: viewPanLiveRef.current.y,
            placeX,
            placeY,
        };
    }, [layoutSurfaceActive, mode, eventToImagePos, findMarkerAtCanvasPos, findMarkerResizeAtCanvasPos, handleViewportMouseDown, handleGroupRightPointerDown, tryBeginGroupDragOnPointerDown, handleGroupSelectionPointerDown, toggleObjectInGroupSelection, hasAnyGroupSelection, clearGroupSelection, selectedMarkerId, selectedMarkerIds, selectedSegmentIds, selectedFaceId, flatSelectedRectId, markers, layoutSegments, enclosedFaces, imageSize, viewPan.x, viewPan.y, recordHistory, selectSegmentById, selectBinMarkerById, trySelectEnclosedAreaAtPointer, placeFlatHouseAt, startFlatRectEdit, selectFlatRect, tryBeginFlatRectEdit, scheduleLayoutPaint]);

    const handleCanvasMouseMove = useCallback((e) => {
        if (!layoutSurfaceActive) return;

        if (groupRightPickRef.current && (e.buttons & 2) !== 0) {
            const p = groupRightPickRef.current;
            const dx = e.clientX - p.clientX;
            const dy = e.clientY - p.clientY;
            if (Math.hypot(dx, dy) > 4) {
                if (p.empty) {
                    marqueeRef.current = {
                        x1: p.x,
                        y1: p.y,
                        x2: p.x,
                        y2: p.y,
                        additive: isMultiSelectKey(e),
                    };
                    setSelectionRect({ x1: p.x, y1: p.y, x2: p.x, y2: p.y });
                    groupRightPickRef.current = null;
                }
            }
        }

        if (flatPendingDragRef.current && (e.buttons & 1)) {
            const p = flatPendingDragRef.current;
            const dx = e.clientX - p.clientX;
            const dy = e.clientY - p.clientY;
            if (Math.hypot(dx, dy) > 4) {
                const pos = eventToImagePos(e);
                const segsLive =
                    interactionSegmentsRef.current ?? layoutSegments;
                const stillOnFlat =
                    pos &&
                    findFlatRectAtPoint(segsLive, pos.x, pos.y) === p.rectId;
                flatPendingDragRef.current = null;
                if (stillOnFlat) {
                    emptyDragRef.current = null;
                    startFlatRectEdit(
                        p.rectId,
                        p.x,
                        p.y,
                        p.scaleX,
                        p.scaleY,
                        e,
                        p.corner,
                        p.moveOnly
                    );
                    return;
                }
            }
        }

        if (groupDragRef.current) {
            const pos = eventToImagePos(e);
            if (!pos) return;
            const { x, y } = pos;
            moveGuidePointRef.current = { x, y };
            const dx = x - groupDragRef.current.startX;
            const dy = y - groupDragRef.current.startY;
            const gdr = groupDragRef.current;
            interactionMarkersRef.current = markers.map((m) => {
                const o = gdr.markerOrigins.get(m.id);
                if (!o) return m;
                return { ...m, x: o.x + dx, y: o.y + dy };
            });
            if (gdr.segmentOrigins?.size) {
                const baseSegs =
                    layoutSegmentsRef.current ?? layoutSegments;
                interactionSegmentsRef.current = baseSegs.map((s) => {
                    const o = gdr.segmentOrigins.get(s.id);
                    if (!o) return s;
                    return applyDeltaToSegment(o, dx, dy);
                });
                layoutSegmentsRef.current = interactionSegmentsRef.current;
            }
            scheduleLayoutPaint();
            return;
        }

        if (flatEditRef.current) {
            if (e.buttons === 0) {
                finishFlatRectEdit();
                return;
            }
            const pos = eventToImagePos(e);
            if (!pos) return;
            const { x, y } = pos;
            const ed = flatEditRef.current;
            if (ed.type === "move") {
                const dx = x - ed.startX;
                const dy = y - ed.startY;
                if (Math.hypot(dx, dy) > 0.5) ed.moved = true;
                const baseSegs =
                    layoutSegmentsRef.current ?? layoutSegments;
                interactionSegmentsRef.current = baseSegs.map((s) => {
                    const o = ed.segmentOrigins.get(s.id);
                    return o ? applyDeltaToSegment(o, dx, dy) : s;
                });
            } else if (ed.type === "resize") {
                const nb = resizeFlatBoundsByCorner(ed.originBounds, ed.corner, x, y);
                if (!nb.tooSmall) {
                    ed.moved = true;
                    const baseSegs =
                        interactionSegmentsRef.current ?? layoutSegments;
                    const next = replaceFlatRectSegments(
                        baseSegs,
                        ed.rectId,
                        nb.minX,
                        nb.minY,
                        nb.maxX,
                        nb.maxY
                    );
                    layoutSegmentsRef.current = next;
                    interactionSegmentsRef.current = next;
                }
            }
            scheduleLayoutPaint();
            return;
        }

        if (marqueeRef.current) {
            const pos = eventToImagePos(e);
            if (!pos) return;
            const { x, y } = pos;
            marqueeRef.current.x2 = x;
            marqueeRef.current.y2 = y;
            selectionRectLiveRef.current = { ...marqueeRef.current };
            scheduleLayoutPaint();
            return;
        }

        if (panDragRef.current) {
            const dx = e.clientX - panDragRef.current.x;
            const dy = e.clientY - panDragRef.current.y;
            viewPanLiveRef.current = {
                x: panDragRef.current.panX + dx,
                y: panDragRef.current.panY + dy,
            };
            schedulePanTransform();
            return;
        }

        const pos = eventToImagePos(e);
        if (!pos) return;
        const { x, y, canvasX, canvasY, rect } = pos;

        if (emptyDragRef.current) {
            const dx = e.clientX - emptyDragRef.current.clientX;
            const dy = e.clientY - emptyDragRef.current.clientY;
            if (
                mode === MODES.PLACE &&
                emptyDragRef.current.placeX != null &&
                Math.hypot(dx, dy) <= 4
            ) {
                const scaleMin = Math.min(
                    rect.width / Math.max(imageSize.width, 1),
                    rect.height / Math.max(imageSize.height, 1)
                );
                const snapped = snapBinModeCenter(null, x, y, markers, scaleMin);
                emptyDragRef.current.placeX = snapped.x;
                emptyDragRef.current.placeY = snapped.y;
                return;
            }
            if (Math.hypot(dx, dy) > 4) {
                flatPendingDragRef.current = null;
                panDragRef.current = {
                    x: emptyDragRef.current.clientX,
                    y: emptyDragRef.current.clientY,
                    panX: emptyDragRef.current.panX,
                    panY: emptyDragRef.current.panY,
                };
                emptyDragRef.current = null;
                beginPanGesture();
                viewPanLiveRef.current = {
                    x: panDragRef.current.panX + dx,
                    y: panDragRef.current.panY + dy,
                };
                schedulePanTransform();
            }
            return;
        }

        if (resizeRef.current) {
            const id = resizeRef.current.markerId;
            const m = markers.find((mk) => mk.id === id);
            if (m) {
                const r = Math.max(6, Math.hypot(x - m.x, y - m.y));
                interactionMarkersRef.current = markers.map((mk) =>
                    mk.id === id ? { ...mk, radiusPx: r } : mk
                );
                scheduleLayoutPaint();
            }
            return;
        }
        if (dragRef.current) {
            const d = Math.hypot(x - dragRef.current.startX, y - dragRef.current.startY);
            if (d > 1) dragRef.current.moved = true;
            const id = dragRef.current.markerId;
            const scaleMin = Math.min(
                rect.width / Math.max(imageSize.width, 1),
                rect.height / Math.max(imageSize.height, 1)
            );
            const snapped = snapBinModeCenter(id, x, y, markers, scaleMin);
            moveGuidePointRef.current = { x: snapped.x, y: snapped.y };
            interactionMarkersRef.current = markers.map((m) =>
                m.id === id ? { ...m, x: snapped.x, y: snapped.y } : m
            );
            scheduleLayoutPaint();
            return;
        }
        const sd = sticeDrawRef.current;
        const arcBulgeStep = sd?.kind === "arc" && !!sd?.to;
        if ((mode === MODES.WALL || mode === MODES.ARC) && markers.length >= 2) {
            if (arcBulgeStep) {
                sticeFreePointRef.current = { x, y };
                sticeHoverSnapRef.current = null;
            } else {
                const scaleX = rect.width / Math.max(imageSize.width, 1);
                const scaleY = rect.height / Math.max(imageSize.height, 1);
                const scaleMin = Math.min(scaleX, scaleY);
                const snap = snapImageToLayoutPoint(
                    x,
                    y,
                    markers,
                    layoutSegments,
                    scaleMin,
                    SNAP_TOL_CANVAS_PX
                );
                const drawing = sticeDrawRef.current?.from;
                sticeHoverSnapRef.current = snap || (drawing ? { x, y } : null);
                sticeFreePointRef.current = null;
                const nextCursor = snap ? "crosshair" : "grab";
                if (wallArcCursorRef.current !== nextCursor) {
                    wallArcCursorRef.current = nextCursor;
                    setWallArcCursor(nextCursor);
                }
            }
            if (arcBulgeStep && wallArcCursorRef.current !== "grab") {
                wallArcCursorRef.current = "grab";
                setWallArcCursor("grab");
            }
            scheduleLayoutPaint();
            return;
        }
        if (sticeHoverSnapRef.current || sticeFreePointRef.current) {
            sticeHoverSnapRef.current = null;
            sticeFreePointRef.current = null;
            scheduleLayoutPaint();
        }

        // Hover cursor feedback: pointer when over a marker handle.
        const canvas = canvasRef.current;
        if (!canvas) return;
        let cursor = "crosshair";
        const scaleX = rect.width / Math.max(imageSize.width, 1);
        const scaleY = rect.height / Math.max(imageSize.height, 1);
        const hoverCorner =
            !hasMultiBinGroupSelection(selectedMarkerId, selectedMarkerIds) &&
            (mode === MODES.FLAT || mode === MODES.PLACE) &&
            (flatHandleRectId || flatSelectedRectId) &&
            findFlatRectCornerHit(
                layoutSegments,
                x,
                y,
                scaleX,
                scaleY,
                FLAT_RECT_CORNER_HIT_TOL_IMAGE,
                flatHandleRectId || flatSelectedRectId
            );
        if (hoverCorner) {
            cursor = "nwse-resize";
        } else if (
            mode === MODES.FLAT &&
            flatSelectedRectId &&
            findFlatRectAtPoint(layoutSegments, x, y)
        ) {
            cursor = "move";
        } else if (mode === MODES.PLACE && findMarkerResizeAtCanvasPos(canvasX, canvasY, rect.width, rect.height)) {
            cursor = "ew-resize";
        } else if (mode === MODES.PLACE && findMarkerAtCanvasPos(canvasX, canvasY, rect.width, rect.height)) {
            cursor = "move";
        } else if (
            selectedMarkerIds.length > 0 || selectedSegmentIds.length > 0
        ) {
            const gb = getGroupSelectionBounds(
                markers,
                layoutSegments,
                selectedMarkerIds,
                selectedSegmentIds
            );
            if (
                gb &&
                x >= gb.minX &&
                x <= gb.maxX &&
                y >= gb.minY &&
                y <= gb.maxY
            ) {
                cursor = "move";
            }
        }
        if (canvas.style.cursor !== cursor) canvas.style.cursor = cursor;
    }, [
        layoutSurfaceActive,
        mode,
        eventToImagePos,
        findMarkerAtCanvasPos,
        findMarkerResizeAtCanvasPos,
        markers,
        layoutSegments,
        scheduleLayoutPaint,
        applyCanvasWrapperTransform,
        beginPanGesture,
        schedulePanTransform,
        imageSize,
        finishFlatRectEdit,
        startFlatRectEdit,
        flatHandleRectId,
        flatSelectedRectId,
        selectedMarkerIds,
        selectedSegmentIds,
    ]);

    /** Canvas receives pointer capture during flat resize; viewport move alone misses events. */
    const handleLayoutSurfacePointerMove = useCallback(
        (e) => {
            handleCanvasMouseMove(e);
            e.stopPropagation();
        },
        [handleCanvasMouseMove]
    );

    const handleCanvasPointerDown = useCallback(
        (e) => {
            if (!e.isPrimary) return;
            layoutPointerActiveRef.current = true;
            try {
                e.currentTarget.setPointerCapture(e.pointerId);
            } catch {
                /* capture not available */
            }
            if (e.pointerType !== "mouse") e.preventDefault();
            handleCanvasMouseDown(e);
        },
        [handleCanvasMouseDown]
    );

    const finalizeMarqueeSelection = useCallback(() => {
        const r = marqueeRef.current;
        if (!r) return;
        const minX = Math.min(r.x1, r.x2);
        const maxX = Math.max(r.x1, r.x2);
        const minY = Math.min(r.y1, r.y2);
        const maxY = Math.max(r.y1, r.y2);
        if (Math.hypot(r.x2 - r.x1, r.y2 - r.y1) > 6) {
            const markerIds = markers
                .filter((m) => {
                    const b = getMarkerBounds(m);
                    return b.right >= minX && b.left <= maxX && b.bottom >= minY && b.top <= maxY;
                })
                .map((m) => m.id);
            const segIdsRaw = layoutSegments
                .filter((raw) => {
                    const g = resolveSegmentGeometry(raw, markers, layoutSegments);
                    if (!g) return false;
                    const sx = (g.x1 + g.x2) / 2;
                    const sy = (g.y1 + g.y2) / 2;
                    return sx >= minX && sx <= maxX && sy >= minY && sy <= maxY;
                })
                .map((s) => s.id);
            const segIds = expandSegmentIdsToFullFlatRects(layoutSegments, segIdsRaw);
            const flatIds = flatRectIdsFromSegmentIds(layoutSegments, segIds);
            if (r.additive) {
                setSelectedMarkerIds((prev) => [...new Set([...prev, ...markerIds])]);
                setSelectedSegmentIds((prev) => [...new Set([...prev, ...segIds])]);
            } else {
                setSelectedMarkerIds(markerIds);
                setSelectedSegmentIds(segIds);
                setGroupAnchorMarkerId(markerIds[0] ?? null);
                setGroupAnchorFlatRectId(flatIds[0] ?? null);
            }
            setSelectedMarkerId(markerIds[0] ?? null);
            setSelectedSegmentId(segIds[0] ?? null);
        }
        marqueeRef.current = null;
        selectionRectLiveRef.current = null;
        setSelectionRect(null);
        scheduleLayoutPaint();
    }, [markers, layoutSegments, scheduleLayoutPaint]);

    const deleteFlatRectFace = useCallback(
        (face) => {
            if (!face) return false;
            const rectId = closedRectIdForFace(face, layoutSegments);
            if (!rectId) return false;
            recordHistory();
            const segIds = layoutSegments
                .filter((s) => s.closedRectId === rectId)
                .map((s) => s.id);
            setLayoutSegments((prev) => filterSegmentsAfterDelete(prev, segIds));
            setFaceAssignments((prev) => {
                const next = { ...prev };
                delete next[face.id];
                return next;
            });
            setFaceLabels((prev) => {
                const next = { ...prev };
                delete next[face.id];
                return next;
            });
            setSelectedFaceId(null);
            setSelectedSegmentId(null);
            setSelectedSegmentIds([]);
            setEnclosureRequestId((id) => id + 1);
            setStatusMessage("Flat house deleted.");
            return true;
        },
        [layoutSegments, recordHistory]
    );

    const deleteObjectAtRightPick = useCallback(
        (pick) => {
            const { markerHit, segHit, flatRectHit, x, y, canvasX, canvasY, canvasW, canvasH } =
                pick;
            const scaleX = canvasW / Math.max(imageSize.width, 1);
            const scaleY = canvasH / Math.max(imageSize.height, 1);

            let segmentToDelete = segHit;
            if (!segmentToDelete) {
                segmentToDelete = findSegmentAtCanvas(
                    layoutSegments,
                    markers,
                    canvasX,
                    canvasY,
                    scaleX,
                    scaleY,
                    24
                );
            }
            if (!segmentToDelete) {
                const snapHit = findSegmentSnapAtCanvas(
                    markers,
                    layoutSegments,
                    canvasX,
                    canvasY,
                    scaleX,
                    scaleY,
                    SNAP_TOL_CANVAS_PX
                );
                if (
                    snapHit?.segmentId &&
                    (snapHit.kind === "segment-end" || snapHit.kind === "segment-mid")
                ) {
                    segmentToDelete = layoutSegments.find((s) => s.id === snapHit.segmentId) ?? null;
                }
            }

            if (segmentToDelete) {
                recordHistory();
                setLayoutSegments((prev) =>
                    filterSegmentsAfterDelete(prev, [segmentToDelete.id])
                );
                setSelectedSegmentId(null);
                setSelectedSegmentIds((prev) =>
                    prev.filter((id) => id !== segmentToDelete.id)
                );
                if (segmentToDelete.closedRectId) {
                    setFaceAssignments((prev) => {
                        const faces =
                            enclosedFacesCacheRef.current.length > 0
                                ? enclosedFacesCacheRef.current
                                : enclosedFaces;
                        const face = faces.find((f) =>
                            closedRectIdForFace(f, layoutSegments) ===
                            segmentToDelete.closedRectId
                        );
                        if (!face) return prev;
                        const next = { ...prev };
                        delete next[face.id];
                        return next;
                    });
                    setFaceLabels((prev) => {
                        const faces =
                            enclosedFacesCacheRef.current.length > 0
                                ? enclosedFacesCacheRef.current
                                : enclosedFaces;
                        const face = faces.find((f) =>
                            closedRectIdForFace(f, layoutSegments) ===
                            segmentToDelete.closedRectId
                        );
                        if (!face) return prev;
                        const next = { ...prev };
                        delete next[face.id];
                        return next;
                    });
                    setSelectedFaceId(null);
                    setEnclosureRequestId((id) => id + 1);
                    setStatusMessage("Flat house deleted.");
                } else {
                    setEnclosureRequestId((id) => id + 1);
                    setStatusMessage("Wall/arc deleted.");
                }
                return;
            }

            if (markerHit) {
                recordHistory();
                removeMarker(markerHit.id);
                setStatusMessage("Bin removed.");
                return;
            }

            if (flatRectHit) {
                const faces =
                    enclosedFacesCacheRef.current.length > 0
                        ? enclosedFacesCacheRef.current
                        : enclosedFaces;
                const face = faces.find(
                    (f) => closedRectIdForFace(f, layoutSegments) === flatRectHit
                );
                if (face && deleteFlatRectFace(face)) return;
            }

            const faces =
                enclosedFacesCacheRef.current.length > 0
                    ? enclosedFacesCacheRef.current
                    : enclosedFaces;
            const faceHit = findFaceAtPoint(x, y, faces);
            if (faceHit && deleteFlatRectFace(faceHit)) return;

            const snapHit = findSegmentSnapAtCanvas(
                markers,
                layoutSegments,
                canvasX,
                canvasY,
                scaleX,
                scaleY,
                SNAP_TOL_CANVAS_PX
            );
            if (snapHit?.kind === "segment-mid" && snapHit.segmentId) {
                recordHistory();
                setLayoutSegments((prev) =>
                    filterSegmentsAfterDelete(prev, [snapHit.segmentId])
                );
                setSelectedSegmentId(null);
                setSelectedSegmentIds((prev) =>
                    prev.filter((id) => id !== snapHit.segmentId)
                );
                setEnclosureRequestId((id) => id + 1);
                setStatusMessage("Wall/arc deleted.");
                return;
            }

            sticeDrawRef.current = null;
        },
        [
            markers,
            layoutSegments,
            enclosedFaces,
            imageSize,
            recordHistory,
            deleteFlatRectFace,
        ]
    );

    const handleCanvasMouseUp = useCallback(() => {
        moveGuidePointRef.current = null;
        const arrowNudgeActive =
            nudgeLiveMarkersRef.current != null ||
            nudgeArrowKeysHeldRef.current > 0;
        if (!arrowNudgeActive) {
            layoutGeometryBusyRef.current = false;
        }
        if (groupDragRef.current) {
            groupDragRef.current = null;
        }
        finishFlatRectEdit();
        if (dragRef.current?.moved && dragRef.current.markerId) {
            const dragId = dragRef.current.markerId;
            const canvas = canvasRef.current;
            const dragScaleMin = canvas
                ? Math.min(
                      canvas.width / Math.max(imageSize.width, 1),
                      canvas.height / Math.max(imageSize.height, 1)
                  )
                : 1;
            const live = interactionMarkersRef.current ?? markers;
            const m = live.find((mk) => mk.id === dragId);
            if (m) {
                const snapped = snapBinModeCenter(dragId, m.x, m.y, live, dragScaleMin);
                interactionMarkersRef.current = live.map((mk) =>
                    mk.id === dragId ? { ...mk, x: snapped.x, y: snapped.y } : mk
                );
            }
        }
        const segmentsDirty = interactionSegmentsRef.current != null;
        const geometryDirty =
            interactionMarkersRef.current != null || segmentsDirty;
        commitInteractionGeometry();
        if (segmentsDirty) {
            const segs = layoutSegmentsRef.current ?? layoutSegments;
            const rectIds = flatRectIdsFromSegmentIds(
                segs,
                expandSegmentIdsToFullFlatRects(segs, selectedSegmentIds)
            );
            rectIds.forEach((rid) =>
                syncFlatFaceInCacheAfterEdit(segs, rid)
            );
            scheduleDebouncedFlatEnclosure();
        }
        if (groupRightPickRef.current && !marqueeRef.current) {
            const pick = groupRightPickRef.current;
            if (pick.empty) {
                clearGroupSelection();
            } else if (pick.ctrl && pick.markerHit) {
                toggleObjectInGroupSelection({ markerHit: pick.markerHit });
            } else if (pick.ctrl && pick.flatRectHit) {
                toggleObjectInGroupSelection({ flatRectHit: pick.flatRectHit });
            } else {
                deleteObjectAtRightPick(pick);
            }
            groupRightPickRef.current = null;
        } else if (groupRightPickRef.current) {
            groupRightPickRef.current = null;
        }
        if (marqueeRef.current) {
            finalizeMarqueeSelection();
        }
        if (
            emptyDragRef.current?.flatPlaceX != null &&
            emptyDragRef.current?.flatPlaceY != null
        ) {
            placeFlatHouseAt(
                emptyDragRef.current.flatPlaceX,
                emptyDragRef.current.flatPlaceY
            );
        }
        if (emptyDragRef.current?.placeX != null && emptyDragRef.current?.placeY != null) {
            let { placeX, placeY } = emptyDragRef.current;
            const canvas = canvasRef.current;
            const scaleMin = canvas
                ? Math.min(
                      canvas.width / Math.max(imageSize.width, 1),
                      canvas.height / Math.max(imageSize.height, 1)
                  )
                : 1;
            const markersLive =
                pendingPlacedMarkersRef.current ?? markers;
            const snapped = snapBinModeCenter(
                null,
                placeX,
                placeY,
                markersLive,
                scaleMin
            );
            placeX = snapped.x;
            placeY = snapped.y;
            const fpp = resolveFeetPerPixel(markersLive, siteBins, feetPerPixel);
            const placeRadiusPx =
                fpp > 0 ? diameterFtToRadiusPx(FALLBACK_BIN_SIZE, fpp) : null;
            if (
                layoutPlacementBlockedForRoundBin({
                    cx: placeX,
                    cy: placeY,
                    radiusPx: placeRadiusPx,
                    markers: markersLive,
                    segments: layoutSegments,
                    faces:
                        enclosedFacesCacheRef.current.length > 0
                            ? enclosedFacesCacheRef.current
                            : enclosedFaces,
                })
            ) {
                setStatusMessage(
                    "Cannot place a bin here — it overlaps an existing bin, flat, wall, or area."
                );
                emptyDragRef.current = null;
                scheduleLayoutPaint();
                return;
            }
            const newId = `m_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
            const newMarker = {
                id: newId,
                x: placeX,
                y: placeY,
                binDataId: null,
                ...(placeRadiusPx != null ? { radiusPx: placeRadiusPx } : {}),
            };
            const pending = [...markersLive, newMarker];
            pendingPlacedMarkersRef.current = pending;
            const facesForLabels =
                enclosedFacesCacheRef.current.length > 0
                    ? enclosedFacesCacheRef.current
                    : enclosedFaces;
            const placeSlots = buildOrderedLayoutAreas(pending, facesForLabels);
            layoutAreaSlotsCacheRef.current = placeSlots;
            const mergedNames = mergeMarkerAreaNamesFromSlots(
                markerAreaNamesRef.current,
                placeSlots
            );
            markerAreaNamesRef.current = mergedNames;
            setMarkerAreaNames(mergedNames);
            paintLayoutCanvasRef.current?.();
            startTransition(() => {
                setMarkers((prev) => {
                    if (prev.some((m) => m.id === newId)) return prev;
                    return [...prev, newMarker];
                });
                setSelectedMarkerId(newId);
                setSelectedFaceId(null);
            });
            if (placeHistoryIdleRef.current != null) {
                if (typeof cancelIdleCallback === "function") {
                    cancelIdleCallback(placeHistoryIdleRef.current);
                }
            }
            const queuePlaceHistory = () => {
                placeHistoryIdleRef.current = null;
                recordHistory();
            };
            if (typeof requestIdleCallback === "function") {
                placeHistoryIdleRef.current = requestIdleCallback(
                    queuePlaceHistory,
                    { timeout: 1200 }
                );
            } else {
                setTimeout(queuePlaceHistory, 0);
            }
        }
        emptyDragRef.current = null;
        flatPendingDragRef.current = null;
        dragRef.current = null;
        resizeRef.current = null;
        const wasPanning = panDragRef.current != null;
        panDragRef.current = null;
        if (panMoveRafRef.current != null) {
            cancelAnimationFrame(panMoveRafRef.current);
            panMoveRafRef.current = null;
        }
        panActiveRef.current = false;
        applyPanGrabCursor(false);
        if (wasPanning) {
            applyCanvasWrapperTransform();
            scheduleViewPanStateSync();
        }
        const zoomNeedsCommit =
            pendingZoomCommitAfterPanRef.current ||
            Math.abs(
                viewZoomRef.current - committedViewZoomRef.current
            ) > 0.001;
        pendingZoomCommitAfterPanRef.current = false;
        if (zoomNeedsCommit) {
            scheduleViewportZoomCommit();
        } else if (wasPanning) {
            applyCanvasWrapperTransform();
        }
        if (!layoutGeometryBusyRef.current) {
            canvasInteractingRef.current = false;
        }
        if (geometryDirty) {
            setEnclosureRequestId((id) => id + 1);
            scheduleLayoutPaint();
        }
    }, [
        finalizeMarqueeSelection,
        toggleObjectInGroupSelection,
        deleteObjectAtRightPick,
        clearGroupSelection,
        recordHistory,
        markers,
        layoutSegments,
        imageSize,
        siteBins,
        feetPerPixel,
        placeFlatHouseAt,
        commitInteractionGeometry,
        commitViewportZoomBitmap,
        scheduleViewportZoomCommit,
        scheduleViewPanStateSync,
        applyPanGrabCursor,
        applyCanvasWrapperTransform,
        layoutHeavyGeometry,
        scheduleLayoutPaint,
        finishFlatRectEdit,
        syncFlatFaceInCacheAfterEdit,
        scheduleDebouncedFlatEnclosure,
        selectedSegmentIds,
    ]);

    const handleCanvasPointerUp = useCallback(
        (e) => {
            try {
                if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
                    e.currentTarget.releasePointerCapture(e.pointerId);
                }
            } catch {
                /* already released */
            }
            layoutPointerActiveRef.current = false;
            handleCanvasMouseUp();
        },
        [handleCanvasMouseUp]
    );

    const handleCanvasMouseDownGuarded = useCallback(
        (e) => {
            if (layoutPointerActiveRef.current) return;
            handleCanvasMouseDown(e);
        },
        [handleCanvasMouseDown]
    );

    const handleViewportMouseUpGuarded = useCallback(() => {
        if (layoutPointerActiveRef.current) return;
        handleCanvasMouseUp();
    }, [handleCanvasMouseUp]);

    const handleCanvasContextMenu = useCallback((e) => {
        e.preventDefault();
        if (!layoutSurfaceActive) return;
        if (mode === MODES.WALL || mode === MODES.ARC) {
            sticeDrawRef.current = null;
        }
    }, [layoutSurfaceActive, mode]);

    const updateMarker = (id, patch) => {
        setMarkers((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
    };
    const removeMarker = (id) => {
        recordHistory();
        setMarkerLabels((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
        setMarkerAreaNames((prev) => {
            const next = { ...prev };
            delete next[id];
            markerAreaNamesRef.current = next;
            return next;
        });
        setMarkers((prev) => prev.filter((m) => m.id !== id));
        setSelectedMarkerId((curr) => (curr === id ? null : curr));
        setSelectedMarkerIds((prev) => prev.filter((mid) => mid !== id));
        setLayoutSegments((prev) =>
            prev.filter(
                (seg) =>
                    seg.markerId1 !== id &&
                    seg.markerId2 !== id &&
                    seg.bulgeMarkerId !== id
            )
        );
        if (sticeDrawRef.current?.from?.markerId === id || sticeDrawRef.current?.to?.markerId === id) {
            sticeDrawRef.current = null;
        }
    };

    /** Synthetic bin record for 3D preview when a marker is not assigned yet. */
    const previewBinForMarker = useCallback(
        (marker, fpp) => {
            const footprintFt = getMarkerFootprintFt(marker, fpp);
            const size = footprintFt > 0 ? footprintFt : FALLBACK_BIN_SIZE;
            return {
                binDataId: marker.id,
                binId: marker.id,
                binName: `Bin ${marker.id}`,
                type: "round",
                height: 30,
                width: size,
                length: size,
                diameter: size,
            };
        },
        []
    );

    /** Editor snapshot for DB (geometry only — no image). */
    const buildEditorDraftPayload = useCallback(
        () =>
            siteLayoutSessionGeomPayload({
                blankCanvas: blankCanvas || !imageDataUrl,
                blankImageSize:
                    blankCanvas || !imageDataUrl
                        ? imageSize.width > 0 && imageSize.height > 0
                            ? imageSize
                            : { ...BLANK_LAYOUT_SIZE }
                        : null,
                markers,
                layoutSegments,
                faceAssignments,
                faceLabels,
                markerLabels,
                markerAreaNames,
                feetPerPixel,
                clusterEpsFt,
                mode,
            }),
        [
            blankCanvas,
            imageSize,
            imageDataUrl,
            markers,
            layoutSegments,
            faceAssignments,
            faceLabels,
            markerLabels,
            markerAreaNames,
            feetPerPixel,
            clusterEpsFt,
            mode,
        ]
    );

    const buildDraft = useCallback((feetPerPixelOverride, options = {}) => {
        const forPreview = options.forPreview === true;
        const forSave = options.forSave === true;
        const matchSite3dDimensions = options.matchSite3dDimensions === true;
        const useEditorFootprint =
            options.useEditorFootprint === true || forPreview;
        const strictLayoutFaces = options.strictLayoutFaces === true;
        const includeAllGeometry = forPreview || forSave;
        const facesSource = options.faces ?? enclosedFaces;
        const siteGuid = global.gw?.selectedSite?.siteGuid ?? activeSiteGuid;
        if (!siteGuid) return null;

        const snappedSegments = pruneUnsnappedSegments(layoutSegments, markers);

        const readyMarkers = includeAllGeometry
            ? markers
            : markers.filter(
                  (m) =>
                      m.binDataId != null ||
                      (markerLabels[m.id] && String(markerLabels[m.id]).trim())
              );
        const readyFaces = includeAllGeometry
            ? facesSource
            : facesSource.filter(
                  (f) =>
                      faceAssignments[f.id] != null ||
                      (faceLabels[f.id] && String(faceLabels[f.id]).trim())
              );
        if (readyMarkers.length === 0 && readyFaces.length === 0) return null;

        const fpp = feetPerPixelOverride > 0
            ? feetPerPixelOverride
            : resolveFeetPerPixel(markers, siteBins, feetPerPixel);

        const epsPx = clusterEpsFt > 0 && fpp > 0 ? clusterEpsFt / fpp : 80;

        const clusterInputs = [];
        const clusterMeta = [];
        readyMarkers.forEach((m) => {
            clusterInputs.push({ x: m.x, y: m.y });
            clusterMeta.push({ type: "marker", marker: m });
        });
        readyFaces.forEach((f) => {
            clusterInputs.push({ x: f.centroid.x, y: f.centroid.y });
            clusterMeta.push({ type: "face", face: f });
        });

        const assigned = clusterMarkers(clusterInputs, epsPx);

        const clusterMap = new Map();
        clusterMeta.forEach((meta, i) => {
            const c = assigned[i] ?? 0;
            if (!clusterMap.has(c)) clusterMap.set(c, { markers: [], faces: [] });
            const bucket = clusterMap.get(c);
            if (meta.type === "marker") bucket.markers.push(meta.marker);
            else bucket.faces.push(meta.face);
        });

        const groupsWithCenters = [];
        clusterMap.forEach((bucket, clusterIdx) => {
            const allPts = [
                ...bucket.markers.map((m) => ({ x: m.x, y: m.y })),
                ...bucket.faces.map((f) => f.centroid),
            ];
            if (allPts.length === 0) return;
            const cx = allPts.reduce((s, p) => s + p.x, 0) / allPts.length;
            const cy = allPts.reduce((s, p) => s + p.y, 0) / allPts.length;
            groupsWithCenters.push({ clusterIdx, centerX: cx, centerY: cy, ...bucket });
        });

        const siteCenterPx =
            computeLayoutSiteCenterPx(readyMarkers, readyFaces) ?? {
                x: 0,
                y: 0,
            };

        const groups = [];
        groupsWithCenters.forEach((g, gi) => {
            const groupX = round1((g.centerX - siteCenterPx.x) * fpp);
            const groupZ = round1((g.centerY - siteCenterPx.y) * fpp);

            const groupCenter = { centerX: g.centerX, centerY: g.centerY };
            let groupMinX = Infinity, groupMaxX = -Infinity, groupMinZ = Infinity, groupMaxZ = -Infinity, groupMaxH = 0;
            const bins = [];
            let order = 0;

            g.markers.forEach((m) => {
                const slot = layoutAreaSlotByMarkerId.get(m.id);
                const displayName = getMarkerDisplayLabel(
                    m,
                    slot,
                    markerLabels,
                    siteBins
                );
                let bin = siteBins.find((b) =>
                    sameBinDataId(b.binDataId, m.binDataId)
                );
                const saveBinId = resolveLayoutBinIdForSave(
                    m.binDataId,
                    slot?.areaIndex ?? order,
                    forSave
                );
                if (!bin && (includeAllGeometry || displayName)) {
                    const footprintFt = getMarkerFootprintFt(m, fpp);
                    const size = footprintFt > 0 ? footprintFt : FALLBACK_BIN_SIZE;
                    bin = {
                        binDataId: saveBinId,
                        binId: saveBinId,
                        binName: displayName,
                        type: "round",
                        height: 30,
                        width: size,
                        length: size,
                        diameter: size,
                    };
                }
                if (!bin) return;
                order += 1;
                const binForDraft = { ...bin, binName: displayName };
                const draft = buildBinDraft(
                    { ...m, groupOrder: order },
                    groupCenter,
                    fpp,
                    binForDraft,
                    forPreview,
                    matchSite3dDimensions && !useEditorFootprint,
                    useEditorFootprint
                );
                if (!draft) return;
                bins.push(draft);
                const halfW = draft.width / 2;
                const halfL = draft.length / 2;
                groupMinX = Math.min(groupMinX, draft.x - halfW);
                groupMaxX = Math.max(groupMaxX, draft.x + halfW);
                groupMinZ = Math.min(groupMinZ, draft.z - halfL);
                groupMaxZ = Math.max(groupMaxZ, draft.z + halfL);
                groupMaxH = Math.max(groupMaxH, draft.height);
            });

            g.faces.forEach((face) => {
                if (
                    !faceShouldPersistAsLayoutSticeBin(face, markers, fpp, {
                        forPreview,
                        forSave,
                    })
                ) {
                    return;
                }
                const binDataId = faceAssignments[face.id];
                const faceSlot = layoutFaceSlotById.get(face.id);
                const labelIndex =
                    faceSlot?.areaIndex ?? face.areaOrderIndex ?? order;
                let faceLabel = getFaceDisplayLabel(face, labelIndex, {
                    faceLabels,
                    faceAssignments,
                    siteBins,
                    sameBinDataId,
                });
                if (
                    faceAssignments[face.id] == null &&
                    !(faceLabels[face.id] && String(faceLabels[face.id]).trim()) &&
                    faceSlot?.letter
                ) {
                    faceLabel = faceSlot.letter;
                }
                let bin = siteBins.find((b) => sameBinDataId(b.binDataId, binDataId));
                const saveBinId = resolveLayoutBinIdForSave(
                    binDataId,
                    faceSlot?.areaIndex ?? labelIndex,
                    forSave
                );
                if (!bin && faceLabel) {
                    bin = {
                        binDataId: saveBinId,
                        binId: saveBinId,
                        binName: faceLabel,
                        type: "flat",
                        height: 20,
                        width: FALLBACK_BIN_SIZE,
                        length: FALLBACK_BIN_SIZE,
                    };
                }
                if (!bin) return;
                const faceSegs = snappedSegments.filter((s) =>
                    (face.segmentIds || []).includes(s.id)
                );
                const hasBinArcs = (face.binArcKeys || []).length > 0;
                if (
                    !faceSegs.length &&
                    !hasBinArcs &&
                    !faceHasSynthOutlineForDraft(face)
                ) {
                    return;
                }
                order += 1;
                const draft = buildSticeBinDraft(
                    {
                        segments: faceSegs,
                        segmentIds: face.segmentIds,
                        binArcKeys: face.binArcKeys,
                        groupOrder: order,
                    },
                    groupCenter,
                    fpp,
                    { ...bin, binName: faceLabel },
                    markers,
                    snappedSegments,
                    face,
                    // Skip "centroid inside catalog bin" filter for Preview and Save.
                    forPreview || forSave,
                    siteBins,
                    useEditorFootprint
                );
                if (!draft) return;
                bins.push(draft);
                const halfW = draft.width / 2;
                const halfL = draft.length / 2;
                groupMinX = Math.min(groupMinX, draft.x - halfW);
                groupMaxX = Math.max(groupMaxX, draft.x + halfW);
                groupMinZ = Math.min(groupMinZ, draft.z - halfL);
                groupMaxZ = Math.max(groupMaxZ, draft.z + halfL);
                groupMaxH = Math.max(groupMaxH, draft.height);
            });

            if (bins.length === 0) return;

            const groupWidth = isFinite(groupMaxX - groupMinX) ? round1(groupMaxX - groupMinX) : 0;
            const groupLength = isFinite(groupMaxZ - groupMinZ) ? round1(groupMaxZ - groupMinZ) : 0;

            groups.push({
                groupName: `Group ${gi + 1}`,
                width: groupWidth,
                length: groupLength,
                height: groupMaxH,
                rotation: 0,
                x: groupX,
                y: 0,
                z: groupZ,
                cx: null,
                cy: null,
                cz: null,
                bins,
            });
        });

        // Site bounds: enclosing box of all groups (with their bin extents).
        let bMinX = Infinity, bMaxX = -Infinity, bMinZ = Infinity, bMaxZ = -Infinity, bMaxH = 0;
        groups.forEach(g => {
            bMinX = Math.min(bMinX, g.x - g.width / 2);
            bMaxX = Math.max(bMaxX, g.x + g.width / 2);
            bMinZ = Math.min(bMinZ, g.z - g.length / 2);
            bMaxZ = Math.max(bMaxZ, g.z + g.length / 2);
            bMaxH = Math.max(bMaxH, g.height);
        });
        const bounds = isFinite(bMaxX - bMinX) ? {
            width: round1(bMaxX - bMinX),
            length: round1(bMaxZ - bMinZ),
            height: bMaxH,
            rotation: 0,
            x: 0, y: 0, z: 0,
            cx: null, cy: null, cz: null,
            lx: null, ly: null, lz: null,
        } : {
            width: 0, length: 0, height: 0, rotation: 0,
            x: 0, y: 0, z: 0,
            cx: null, cy: null, cz: null,
            lx: null, ly: null, lz: null,
        };

        if (groups.length === 0) return null;

        const out = { bounds, groups, feetPerPixel: fpp };
        return out;
    }, [
        markers,
        layoutSegments,
        enclosedFaces,
        faceAssignments,
        feetPerPixel,
        clusterEpsFt,
        siteBins,
        activeSiteGuid,
        previewBinForMarker,
        faceLabels,
        markerLabels,
        layoutAreaSlotByMarkerId,
        layoutFaceSlotById,
    ]);

    const applyDraftToMemory = (draft) => {
        const site = global.gw?.selectedSite;
        if (!site || !draft) return false;
        const proxy = buildLayoutTableProxyFromEditorDraft(draft, site.siteGuid);
        if (!proxy) return false;
        site.siteLayoutTableProxy = proxy;
        return applyLayoutTableProxyToSite(site, proxy);
    };

    const handlePreview = async () => {
        if (preview3dOpen) {
            setPreview3dOpen(false);
            setPreviewDraft(null);
            setPreviewEditorView(null);
            setStatusMessage("");
            return;
        }

        syncUnsnappedSegmentsBeforeCommit();
        const site = global.gw?.selectedSite;
        if (site?.siteGuid && !(site.layoutCables?.length > 0)) {
            try {
                await global.initializeCustomerSite(navigate);
            } catch (e) {
                console.warn("initializeCustomerSite before preview", e);
            }
        }
        const fpp = resolveFeetPerPixel(markers, siteBins, feetPerPixel);
        let facesForPreview = enclosedFaces;
        try {
            facesForPreview = computeEnclosedFaces(markers, layoutSegments);
            enclosedFacesCacheRef.current = facesForPreview;
            setEnclosedFaces(
                facesForPreview.map(enclosedFaceSummaryForState)
            );
        } catch (err) {
            console.error("computeEnclosedFaces failed", err);
        }
        const draft = buildDraft(fpp, {
            forPreview: true,
            useEditorFootprint: true,
            faces: facesForPreview,
        });
        const binCount = (draft?.groups || []).reduce((n, g) => n + (g.bins?.length || 0), 0);
        if (!draft || binCount === 0) {
            setStatusMessage("Place bins or draw walls/arcs that form areas before preview.");
            return;
        }
        const viewport = viewportRef.current;
        const base = baseCanvasSizeRef.current;
        const imgW = Math.max(imageSize.width, 1);
        const imgH = Math.max(imageSize.height, 1);
        let editorView = null;
        if (viewport?.clientWidth > 0 && base.width > 0 && base.height > 0) {
            const zx = (viewport.clientWidth * VIEW_FIT_PADDING) / base.width;
            const zy = (viewport.clientHeight * VIEW_FIT_PADDING) / base.height;
            const fitZoom = clampViewZoom(Math.min(zx, zy));
            const canvasW = Math.max(1, Math.floor(base.width * viewZoom));
            const canvasH = Math.max(1, Math.floor(base.height * viewZoom));
            const siteCenterImage =
                computeLayoutSiteCenterPx(markers, facesForPreview, {
                    width: imgW,
                    height: imgH,
                }) ?? { x: imgW / 2, y: imgH / 2 };
            editorView = {
                viewZoom,
                fitZoom,
                viewPan: { x: viewPan.x, y: viewPan.y },
                viewportWidth: viewport.clientWidth,
                viewportHeight: viewport.clientHeight,
                baseWidth: base.width,
                baseHeight: base.height,
                imageWidth: imgW,
                imageHeight: imgH,
                feetPerPixel: fpp,
                siteCenterImage,
                focusImageX:
                    ((viewport.clientWidth / 2 - viewPan.x) / canvasW) * imgW,
                focusImageY:
                    ((viewport.clientHeight / 2 - viewPan.y) / canvasH) * imgH,
            };
        }
        setPreviewEditorView(editorView);
        setPreviewDraft(draft);
        const siteGuid = global.gw?.selectedSite?.siteGuid ?? activeSiteGuid;
        if (siteGuid) {
            saveLayoutDraftForSite3d(siteGuid, draft);
            const previewProxy = buildLayoutTableProxyFromEditorDraft(
                draft,
                siteGuid
            );
            if (previewProxy) saveLayoutProxyForSite3d(siteGuid, previewProxy);
        }
        setPreview3dOpen(true);
        setStatusMessage("");
    };

    const handleOpenSite3d = async () => {
        if (!siteReady || busy || siteLoading) return;
        const siteGuid = global.gw?.selectedSite?.siteGuid ?? activeSiteGuid;
        if (!siteGuid) return;

        if (preview3dOpen) {
            setPreview3dOpen(false);
            setPreviewDraft(null);
            setPreviewEditorView(null);
        }

        saveSiteLayoutSession(siteGuid, getSiteLayoutSessionPayload());

        syncUnsnappedSegmentsBeforeCommit();
        const site = global.gw?.selectedSite;
        if (site?.siteGuid && !(site.layoutCables?.length > 0)) {
            try {
                await global.initializeCustomerSite(navigate);
            } catch (e) {
                console.warn("initializeCustomerSite before Site 3D", e);
            }
        }
        if (canPreviewLayout) {
            const fpp = resolveFeetPerPixel(markers, siteBins, feetPerPixel);
            let facesForSave = enclosedFaces;
            try {
                facesForSave = computeEnclosedFaces(markers, layoutSegments);
            } catch (err) {
                console.error("computeEnclosedFaces failed", err);
            }
            const draft = buildDraft(fpp, {
                forPreview: true,
                useEditorFootprint: true,
                faces: facesForSave,
            });
            const binCount = (draft?.groups || []).reduce(
                (n, g) => n + (g.bins?.length || 0),
                0
            );
            if (draft && binCount > 0) {
                saveLayoutDraftForSite3d(siteGuid, draft);
                const proxy = buildLayoutTableProxyFromEditorDraft(draft, siteGuid);
                if (proxy) saveLayoutProxyForSite3d(siteGuid, proxy);
            }
        }

        global.setSiteLayoutReturn?.(siteGuid);
        global.resetSite3dToSiteOverview?.();
        global.markSite3dPinned?.();
        setStatusMessage("");
        navigate("/site3d");
    };

    const importLayoutGeometryFromSite = useCallback(
        (geom, { recordUndo = true, statusMsg } = {}) => {
            if (!geom?.markers?.length && !geom?.layoutSegments?.length) {
                return false;
            }

            if (recordUndo) recordHistory();
            clearGroupSelection();
            sticeDrawRef.current = null;

            setBlankCanvas(true);
            setImageDataUrl(null);
            imageRef.current = null;

            const fitted = fitBlankLayoutCanvasSize(
                geom.markers,
                geom.layoutSegments,
                {
                    minWidth:
                        geom.blankImageSize?.width > 0
                            ? geom.blankImageSize.width
                            : BLANK_LAYOUT_SIZE.width,
                    minHeight:
                        geom.blankImageSize?.height > 0
                            ? geom.blankImageSize.height
                            : BLANK_LAYOUT_SIZE.height,
                }
            );

            setImageSize({ width: fitted.width, height: fitted.height });
            setMarkers(fitted.markers);
            setLayoutSegments(fitted.layoutSegments);
            setMarkerLabels(geom.markerLabels ?? {});
            setFeetPerPixel(geom.feetPerPixel);

            let faces = [];
            try {
                faces = computeEnclosedFaces(
                    fitted.markers,
                    fitted.layoutSegments
                );
                enclosedFacesCacheRef.current = faces;
                setEnclosedFaces(faces.map(enclosedFaceSummaryForState));
                const importSlots = buildOrderedLayoutAreas(
                    fitted.markers,
                    faces
                );
                const importAreaNames = mergeMarkerAreaNamesFromSlots(
                    geom.markerAreaNames ?? {},
                    importSlots
                );
                markerAreaNamesRef.current = importAreaNames;
                setMarkerAreaNames(importAreaNames);
            } catch (err) {
                console.error("computeEnclosedFaces after layout import", err);
                setEnclosureRequestId((id) => id + 1);
            }

            const { faceAssignments: loadedFaces, faceLabels: loadedFaceLabels } =
                faceAssignmentsFromLayoutTables(
                    faces,
                    fitted.layoutSegments,
                    geom.faceAssignments,
                    geom.faceLabels,
                    geom.flatAreaSeeds
                );
            setFaceAssignments(loadedFaces);
            setFaceLabels(loadedFaceLabels);
            clearPlaceModeSelection();
            skipPruneUntilRef.current = Date.now() + 2500;
            setEnclosureRequestId((id) => id + 1);

            let facesAfterImport = [];
            try {
                facesAfterImport = computeEnclosedFaces(
                    fitted.markers,
                    fitted.layoutSegments
                );
            } catch {
                /* pan fallback below */
            }
            const center = computeLayoutSiteCenterPx(
                fitted.markers,
                facesAfterImport,
                {
                    width: fitted.width,
                    height: fitted.height,
                }
            );
            if (center) {
                panViewportToImagePoint(center.x, center.y, viewZoom);
            } else if (geom.siteCenterPx) {
                panViewportToImagePoint(
                    geom.siteCenterPx.x,
                    geom.siteCenterPx.y,
                    viewZoom
                );
            } else if (fitted.siteCenterPx) {
                panViewportToImagePoint(
                    fitted.siteCenterPx.x,
                    fitted.siteCenterPx.y,
                    viewZoom
                );
            } else {
                resetViewportToDefault();
            }

            if (statusMsg) setStatusMessage(statusMsg);
            return true;
        },
        [
            recordHistory,
            clearGroupSelection,
            clearPlaceModeSelection,
            viewZoom,
            panViewportToImagePoint,
            resetViewportToDefault,
        ]
    );

    // After restore, open the canvas automatically (upload remains in the toolbar).
    useEffect(() => {
        if (!siteReady || !activeSiteGuid || !editorRestoreComplete) return;
        if (layoutSurfaceActive) return;
        if (autoBlankLayoutForGuidRef.current === activeSiteGuid) return;
        autoBlankLayoutForGuidRef.current = activeSiteGuid;
        startBlankLayout({ silent: true });
    }, [
        siteReady,
        activeSiteGuid,
        editorRestoreComplete,
        layoutSurfaceActive,
        startBlankLayout,
    ]);

    // No editor draft and no persisted layout tables → seed canvas from Site 3D default square grid.
    useEffect(() => {
        if (!siteReady || !activeSiteGuid || !editorRestoreComplete) return;
        if (autoGridAppliedForGuidRef.current === activeSiteGuid) return;
        if (markers.length > 0 || layoutSegments.length > 0 || imageDataUrl) {
            return;
        }

        const site = global.gw?.selectedSite;
        if (!site?.bins?.length || site.hasPersistedSiteLayout) return;

        const fpp = defaultLayoutFeetPerPixel(siteBins);
        const geom = buildEditorGeometryFromSite3d(site, {
            feetPerPixel: fpp,
            imageWidth: BLANK_LAYOUT_SIZE.width,
            imageHeight: BLANK_LAYOUT_SIZE.height,
        });
        if (!geom?.markers?.length && !geom?.layoutSegments?.length) return;

        autoGridAppliedForGuidRef.current = activeSiteGuid;
        const roundN = geom.markers.length;
        const flatPending = Object.keys(geom.faceAssignments ?? {}).length;
        importLayoutGeometryFromSite(geom, {
            recordUndo: false,
            statusMsg: `Loaded Site 3D default grid: ${roundN} round bin${roundN === 1 ? "" : "s"}${flatPending > 0 ? `, ${flatPending} flat area${flatPending === 1 ? "" : "s"}` : ""}.`,
        });
    }, [
        siteReady,
        activeSiteGuid,
        editorRestoreComplete,
        markers.length,
        layoutSegments.length,
        imageDataUrl,
        siteBins,
        importLayoutGeometryFromSite,
    ]);

    /** Import layoutGroups/layoutBins/layoutSides from Site 3D into the 2D canvas. */
    const handleLoadFromSite3d = useCallback(async () => {
        if (!siteReady || !activeSiteGuid) {
            setStatusMessage("Load a site first.");
            return;
        }
        const confirmed = await openConfirmDialog({
            title: "Load from Site 3D",
            message:
                "Replace the current layout on the canvas with data from this site's layout tables (Site 3D)?",
            confirmLabel: "Load",
        });
        if (!confirmed) return;

        setBusy(true);
        setStatusMessage("Loading layout from Site 3D…");
        try {
            let site = global.gw?.selectedSite;
            if (!site?.layoutBins?.length) {
                await global.initializeCustomerSite(navigate);
                site = global.gw?.selectedSite;
            }

            try {
                global.drawSite?.();
            } catch (drawErr) {
                console.warn("drawSite before layout load", drawErr);
            }

            const imgW = Math.max(imageSize.width, BLANK_LAYOUT_SIZE.width);
            const imgH = Math.max(imageSize.height, BLANK_LAYOUT_SIZE.height);
            const fpp = resolveFeetPerPixel(markers, siteBins, feetPerPixel);

            let geom = buildEditorGeometryFromSite3d(site, {
                feetPerPixel: fpp,
                imageWidth: imgW,
                imageHeight: imgH,
            });

            const sessionPayload = normalizeEditorDraftPayload(
                loadSiteLayoutSession(activeSiteGuid)
            );
            let editorPayload = sessionPayload;
            if (!editorPayload?.layoutSegments?.length) {
                try {
                    editorPayload = normalizeEditorDraftPayload(
                        await createApiEndpoint().fetchSiteLayoutEditorDraft(
                            "direct",
                            activeSiteGuid
                        )
                    );
                } catch {
                    editorPayload = null;
                }
            }

            const canRestoreEditor =
                editorPayload?.layoutSegments?.length > 0 &&
                editorPayload?.markers?.length > 0;
            if (canRestoreEditor) {
                recordHistory();
                clearGroupSelection();
                sticeDrawRef.current = null;
                if (
                    applyEditorSessionPayload(
                        editorPayload,
                        "Loaded saved editor layout (walls, arcs, and area assignments)."
                    )
                ) {
                    skipPruneUntilRef.current = Date.now() + 2500;
                    return;
                }
            }

            if (editorPayload?.layoutSegments?.length) {
                geom = mergeSavedEditorSegmentsIntoGeometry(geom, editorPayload);
            }

            if (!geom?.markers?.length && !geom?.layoutSegments?.length) {
                setStatusMessage(
                    site?.bins?.length > 0
                        ? "No bins could be placed on the canvas."
                        : "This site has no bins to load."
                );
                return;
            }

            const roundN = geom.markers.length;
            const flatPending = Object.keys(geom.faceAssignments ?? {}).length;
            const fromGrid = !site?.hasPersistedSiteLayout;
            importLayoutGeometryFromSite(geom, {
                statusMsg: fromGrid
                    ? `Loaded Site 3D default grid: ${roundN} round bin${roundN === 1 ? "" : "s"}${flatPending > 0 ? `, ${flatPending} flat area${flatPending === 1 ? "" : "s"}` : ""}.`
                    : `Loaded from Site 3D: ${roundN} round bin${roundN === 1 ? "" : "s"}, ${geom.layoutSegments.length} wall/arc segment${geom.layoutSegments.length === 1 ? "" : "s"}${flatPending > 0 ? `, ${flatPending} flat area${flatPending === 1 ? "" : "s"}` : ""}.`,
            });
        } catch (e) {
            console.error("handleLoadFromSite3d", e);
            setStatusMessage(`Load failed: ${e?.message ?? e}`);
        } finally {
            setBusy(false);
        }
    }, [
        siteReady,
        activeSiteGuid,
        navigate,
        imageSize.width,
        imageSize.height,
        markers,
        siteBins,
        feetPerPixel,
        importLayoutGeometryFromSite,
        applyEditorSessionPayload,
        recordHistory,
        clearGroupSelection,
        openConfirmDialog,
    ]);

    /** Save editor snapshot to CustomerSiteLayout.EditorDraftJson (no layout bin/group tables). */
    const handleSave = async () => {
        const siteGuid = global.gw?.selectedSite?.siteGuid;
        if (!siteGuid) {
            setStatusMessage("No site selected.");
            return;
        }
        if (!layoutSurfaceActive) {
            setStatusMessage("Layout canvas is not ready yet.");
            return;
        }
        flushViewportPanFromLive();
        syncUnsnappedSegmentsBeforeCommit();
        const fpp = resolveFeetPerPixel(markers, siteBins, feetPerPixel);
        let existingProxy =
            global.gw?.selectedSite?.persistedLayoutProxyFromEditor;
        if (!existingProxy?.layoutBins?.length) {
            try {
                const fromDb = parseSiteLayoutEditorDraftFromApi(
                    await createApiEndpoint().fetchSiteLayoutEditorDraft(
                        "direct",
                        siteGuid
                    )
                );
                existingProxy = extractLayoutProxyFromEditorDraft(fromDb);
            } catch {
                /* keep existing proxy if any */
            }
        }
        const editor = mergeLayoutProxyIntoEditorPayload(
            buildEditorDraftPayload(),
            existingProxy
        );
        const draft = {
            bounds: { ...EMPTY_LAYOUT_BOUNDS },
            groups: [],
            feetPerPixel: fpp > 0 ? fpp : feetPerPixel,
            editor,
        };
        setBusy(true);
        setStatusMessage("Saving editor draft…");
        try {
            const ok = await createApiEndpoint().saveCustomerSiteLayout("direct", siteGuid, draft);
            if (ok) {
                let editorPersisted = true;
                try {
                    const verify = normalizeEditorDraftPayload(
                        await createApiEndpoint().fetchSiteLayoutEditorDraft(
                            "direct",
                            siteGuid
                        )
                    );
                    const segCount = verify?.layoutSegments?.length ?? 0;
                    const markerCount = verify?.markers?.length ?? 0;
                    if (layoutSegments.length > 0) {
                        editorPersisted = segCount > 0;
                    } else if (markers.length > 0) {
                        editorPersisted = markerCount > 0;
                    }
                } catch {
                    editorPersisted = false;
                }
                saveSiteLayoutSession(siteGuid, getSiteLayoutSessionPayload());
                if (existingProxy?.layoutBins?.length && global.gw?.selectedSite) {
                    global.gw.selectedSite.persistedLayoutProxyFromEditor =
                        extractLayoutProxyFromEditorDraft(editor) || existingProxy;
                }
                if (!editorPersisted) {
                    setStatusMessage(
                        "Save failed: EditorDraftJson was not stored. Run AI/CustomerSiteLayout_EditorDraft.sql on Temputech, restart the API, then Save again."
                    );
                } else {
                    setStatusMessage(
                        "Editor draft saved to the site (all users). Use Apply to Site 3D to publish layout tables for Site 3D."
                    );
                }
            } else {
                setStatusMessage(
                    "Save failed. Run AI/CustomerSiteLayout_EditorDraft.sql on Temputech if this is the first save, then retry."
                );
            }
        } catch (e) {
            setStatusMessage(`Save failed: ${e?.message ?? e}`);
        } finally {
            setBusy(false);
        }
    };

    /** Persist assigned bins/groups/sides to CustomerSiteLayout tables. */
    const handleSaveLayoutTables = async () => {
        const confirmed = await openConfirmDialog({
            title: "Apply to Site 3D",
            message: "This will overwrite the current Site 3D view. Continue?",
            confirmLabel: "Yes",
            cancelLabel: "Cancel",
        });
        if (!confirmed) return;

        const siteGuid = global.gw?.selectedSite?.siteGuid;
        if (!siteGuid) {
            setStatusMessage("No site selected.");
            return;
        }
        if (!canSaveLayoutTables) {
            setStatusMessage("Place bins or draw walls/arcs before saving layout tables.");
            return;
        }
        flushViewportPanFromLive();
        syncUnsnappedSegmentsBeforeCommit();
        const fpp = resolveFeetPerPixel(markers, siteBins, feetPerPixel);
        let facesForSave = enclosedFaces;
        try {
            facesForSave = computeEnclosedFaces(markers, layoutSegments);
            enclosedFacesCacheRef.current = facesForSave;
            setEnclosedFaces(facesForSave.map(enclosedFaceSummaryForState));
        } catch (err) {
            console.error("computeEnclosedFaces failed", err);
        }
        const editor = buildEditorDraftPayload();
        let draft = buildDraft(fpp, {
            forSave: true,
            useEditorFootprint: true,
            faces: facesForSave,
            strictLayoutFaces: true,
        });
        if (!draft) {
            draft = {
                bounds: { ...EMPTY_LAYOUT_BOUNDS },
                groups: [],
                feetPerPixel: fpp > 0 ? fpp : feetPerPixel,
            };
        }
        draft.editor = editor;
        const tableBinCount = (draft.groups || []).reduce(
            (n, g) => n + (g.bins || []).length,
            0
        );
        if (tableBinCount === 0) {
            setStatusMessage(
                "No bins in the layout draft. Place round bins or flat areas, then save again."
            );
            return;
        }
        const siteCenterPx =
            computeLayoutSiteCenterPx(markers, facesForSave) ??
            (imageSize.width > 0 && imageSize.height > 0
                ? { x: imageSize.width / 2, y: imageSize.height / 2 }
                : { x: BLANK_LAYOUT_SIZE.width / 2, y: BLANK_LAYOUT_SIZE.height / 2 });
        augmentDraftWithRoundBinPerimeterSides(
            draft,
            markers,
            layoutSegments,
            siteCenterPx,
            fpp
        );
        prepareDraftForLayoutTableSave(draft);
        draft.sidesAreGroupAbsolute = true;
        const proxy = buildLayoutTableProxyFromEditorDraft(draft, siteGuid);
        if (!proxy) {
            setStatusMessage("Could not build layout rows from the editor draft.");
            return;
        }
        const layoutProxyPayload = {
            layout: proxy.layout,
            layoutGroups: proxy.layoutGroups,
            layoutBins: proxy.layoutBins,
            layoutSides: proxy.layoutSides,
        };
        const savePayload = {
            ...buildSavePayloadFromLayoutProxy(proxy, fpp),
            layoutProxyJson: layoutProxyPayload,
        };

        setBusy(true);
        setStatusMessage("Applying layout to Site 3D…");
        try {
            const ok = await createApiEndpoint().saveCustomerSiteLayout(
                "direct",
                siteGuid,
                savePayload
            );
            if (ok) {
                try {
                    await createApiEndpoint().saveCustomerSiteLayout(
                        "direct",
                        siteGuid,
                        {
                            bounds: { ...EMPTY_LAYOUT_BOUNDS },
                            groups: [],
                            feetPerPixel: fpp > 0 ? fpp : feetPerPixel,
                            layoutProxyJson: layoutProxyPayload,
                            editor: {
                                ...editor,
                                layoutProxy: layoutProxyPayload,
                            },
                        }
                    );
                } catch (editorSaveErr) {
                    console.warn("editor draft save with layoutProxy", editorSaveErr);
                }
                const siteAfter = global.gw?.selectedSite;
                if (siteAfter) {
                    saveLayoutDraftForSite3d(siteGuid, draft);
                    const snap = snapshotSiteLayout(siteAfter);
                    applyLayoutTableProxyToSite(siteAfter, proxy, {
                        skipCablePopulate: true,
                        skipDrawSite: true,
                    });
                    finalizeEditorLayoutForSite3dView(siteAfter, snap);
                    await clearSite3dSavedViewsForSite(siteGuid);
                    const savedProxy = {
                        layout: siteAfter.layout,
                        layoutGroups: siteAfter.layoutGroups,
                        layoutBins: siteAfter.layoutBins,
                        layoutSides: siteAfter.layoutSides,
                    };
                    saveLayoutProxyForSite3d(siteGuid, savedProxy, {
                        layoutCables: siteAfter.layoutCables,
                        layoutSensors: siteAfter.layoutSensors,
                    });
                    siteAfter.persistedLayoutProxyFromEditor = layoutProxyPayload;
                    siteAfter.hasCommittedSite3dLayoutProxy = true;
                    siteAfter.hasEditorLayoutDraft = true;
                }
                saveSiteLayoutSession(siteGuid, getSiteLayoutSessionPayload());
                setStatusMessage(
                    `Layout applied for all users (${tableBinCount} bin${tableBinCount === 1 ? "" : "s"}). Site 3D on any computer will load this geometry from the database — no Site Layout access required.`
                );
            } else {
                setStatusMessage(
                    "Layout table save failed. Confirm the API is running and you have access to this site."
                );
            }
        } catch (e) {
            setStatusMessage(`Layout table save failed: ${e?.message ?? e}`);
        } finally {
            setBusy(false);
        }
    };

    const handleRemoveMarker = (id) => removeMarker(id);

    const handleClearAll = async () => {
        const confirmed = await openConfirmDialog({
            title: "Clear layout",
            message:
                "Clear the uploaded image, blank canvas, bins, walls, arcs, and all assignments?",
            confirmLabel: "Clear all",
        });
        if (!confirmed) return;
        recordHistory();
        setImageDataUrl(null);
        setBlankCanvas(false);
        setShowUploadedImage(true);
        imageRef.current = null;
        setImageSize({ width: 0, height: 0 });
        resetViewportToDefault();
        setMarkers([]);
        setLayoutSegments([]);
        setFaceAssignments({});
        setFaceLabels({});
        setMarkerLabels({});
        setMarkerAreaNames({});
        markerAreaNamesRef.current = {};
        setSelectedMarkerId(null);
        setSelectedMarkerIds([]);
        setGroupAnchorMarkerId(null);
        setSelectedSegmentId(null);
        setSelectedSegmentIds([]);
        setSelectedFaceId(null);
        sticeDrawRef.current = null;
        setStatusMessage("Layout and image cleared.");
    };

    const groupAnchorMarker = useMemo(
        () => markers.find((m) => m.id === groupAnchorMarkerId) ?? null,
        [markers, groupAnchorMarkerId]
    );

    const handleChangeSite = useCallback(() => {
        if (preview3dOpen) {
            setPreview3dOpen(false);
            setPreviewDraft(null);
        }
        setSiteSetupOpen(true);
    }, [preview3dOpen]);

    const groupAnchorLabel = useMemo(() => {
        if (!groupAnchorMarker) return null;
        const slot = layoutAreaSlotByMarkerId.get(groupAnchorMarker.id);
        return (
            getMarkerDisplayLabel(
                groupAnchorMarker,
                slot,
                markerLabels,
                siteBins
            ) || "Reference bin"
        );
    }, [groupAnchorMarker, layoutAreaSlotByMarkerId, markerLabels, siteBins]);

    const layoutDiameterFpp = useMemo(
        () => resolveFeetPerPixel(markers, siteBins, feetPerPixel),
        [markers, siteBins, feetPerPixel]
    );

    useEffect(() => {
        setDiameterDrafts((prev) => {
            const next = { ...prev };
            markers.forEach((m) => {
                if (editingDiameterMarkerId === m.id) return;
                next[m.id] = markerDiameterFtString(m, layoutDiameterFpp, siteBins);
            });
            return next;
        });
    }, [markers, layoutDiameterFpp, siteBins, editingDiameterMarkerId]);

    useEffect(() => {
        setFaceDimensionDrafts((prev) => {
            const next = { ...prev };
            enclosedFaces.forEach((face) => {
                if (editingFaceDimensionsId === face.id) return;
                const dims = faceWidthLengthFtStrings(
                    face,
                    layoutSegments,
                    layoutDiameterFpp,
                    siteBins,
                    faceAssignments[face.id]
                );
                next[face.id] = { w: dims.w, l: dims.l };
            });
            return next;
        });
    }, [
        enclosedFaces,
        layoutSegments,
        layoutDiameterFpp,
        siteBins,
        faceAssignments,
        editingFaceDimensionsId,
    ]);

    const applyFaceDimensionsFt = useCallback(
        (faceId, widthRaw, lengthRaw) => {
            const widthFt = parseFloat(widthRaw);
            const lengthFt = parseFloat(lengthRaw);
            if (!Number.isFinite(widthFt) || !Number.isFinite(lengthFt)) return;
            if (widthFt <= 0 || lengthFt <= 0) return;
            if (layoutDiameterFpp <= 0) {
                setStatusMessage(
                    "Graph grid scale unavailable — cannot size flat houses in feet."
                );
                return;
            }
            const segments = layoutSegmentsRef.current?.length
                ? layoutSegmentsRef.current
                : layoutSegments;
            const faces =
                enclosedFacesCacheRef.current.length > 0
                    ? enclosedFacesCacheRef.current
                    : enclosedFaces;
            const face = faces.find((f) => f.id === faceId);
            if (!face) return;
            const bounds = getFaceBoundsImage(face, segments);
            const rectId =
                bounds?.rectId ||
                closedRectIdForFace(face, segments) ||
                (String(face.id).startsWith("flat:")
                    ? face.id.slice(5)
                    : null);
            if (!rectId) return;
            const oriented = getFlatRectOrientedGeometry(segments, rectId);
            if (!oriented) return;
            const cx = oriented.cx;
            const cy = oriented.cy;
            const angleRad = oriented.angleRad;
            const halfAxes = orientedHalfAxesFromBinFt(
                oriented,
                widthFt,
                lengthFt,
                layoutDiameterFpp
            );
            if (!halfAxes) return;
            const halfW = halfAxes.halfW;
            const halfL = halfAxes.halfL;
            if (
                halfW * 2 < MIN_FLAT_RECT_IMAGE_PX ||
                halfL * 2 < MIN_FLAT_RECT_IMAGE_PX
            ) {
                return;
            }
            const curW = round1(oriented.halfW * 2 * layoutDiameterFpp);
            const curL = round1(oriented.halfL * 2 * layoutDiameterFpp);
            const direct =
                Math.abs(curW - widthFt) + Math.abs(curL - lengthFt);
            const swapped =
                Math.abs(curL - widthFt) + Math.abs(curW - lengthFt);
            if (Math.min(direct, swapped) < 0.05) {
                return;
            }
            recordHistory();
            setLayoutSegments((prev) =>
                replaceFlatRectSegmentsOriented(
                    prev,
                    rectId,
                    cx,
                    cy,
                    halfW,
                    halfL,
                    angleRad
                )
            );
            setEnclosureRequestId((id) => id + 1);
        },
        [
            layoutDiameterFpp,
            layoutSegments,
            enclosedFaces,
            siteBins,
            faceAssignments,
            recordHistory,
        ]
    );

    const applyMarkerDiameterFt = useCallback(
        (markerId, ftRaw) => {
            const ft = parseFloat(ftRaw);
            if (!Number.isFinite(ft) || ft <= 0) return;
            const rPx = diameterFtToRadiusPx(ft, layoutDiameterFpp);
            if (rPx == null) {
                setStatusMessage(
                    "Graph grid scale unavailable — cannot size bins by diameter in feet."
                );
                return;
            }
            const m = markers.find((mk) => mk.id === markerId);
            if (!m) return;
            if (Math.abs(getMarkerFootprintFt(m, layoutDiameterFpp) - ft) < 0.05) return;
            recordHistory();
            setMarkers((prev) =>
                prev.map((mk) =>
                    mk.id === markerId ? { ...mk, radiusPx: rPx } : mk
                )
            );
        },
        [markers, layoutDiameterFpp, recordHistory]
    );

    const alignGroupLabels = {
        alignX: "Aligned selection to reference column (same X)",
        alignY: "Aligned selection to reference row (same Y)",
        sameSize: "Matched size to reference",
    };

    const groupDeleteDisabled =
        !selectedMarkerId &&
        !selectedSegmentId &&
        !selectedFaceId &&
        selectedMarkerIds.length === 0 &&
        selectedSegmentIds.length === 0;

    const handleAlignGroup = useCallback(
        (op) => {
            if (groupSelectedFlatRectIds.length >= 2) {
                const anchorRectId =
                    groupAnchorFlatRectId ?? groupSelectedFlatRectIds[0];
                if (!anchorRectId) return;
                recordHistory();
                setLayoutSegments((prev) =>
                    alignFlatsInGroup(
                        prev,
                        groupSelectedFlatRectIds,
                        anchorRectId,
                        op
                    )
                );
                setEnclosureRequestId((id) => id + 1);
                setStatusMessage(alignGroupLabels[op] ?? "Group updated.");
                return;
            }
            if (selectedMarkerIds.length < 2) {
                setStatusMessage(
                    "Select at least two bins or flat houses, then use align buttons."
                );
                return;
            }
            const anchorId = groupAnchorMarkerId ?? selectedMarkerIds[0];
            if (!anchorId) return;
            recordHistory();
            setMarkers((prev) => alignMarkersInGroup(prev, selectedMarkerIds, anchorId, op));
            setStatusMessage(alignGroupLabels[op] ?? "Group updated.");
        },
        [
            groupSelectedFlatRectIds,
            groupAnchorFlatRectId,
            selectedMarkerIds,
            groupAnchorMarkerId,
            recordHistory,
        ]
    );

    const handleRotateGroup = useCallback(
        (direction = 1) => {
            const markerIds =
                selectedMarkerIds.length > 0
                    ? selectedMarkerIds
                    : selectedMarkerId
                        ? [selectedMarkerId]
                        : [];
            const segmentIds =
                selectedSegmentIds.length > 0
                    ? selectedSegmentIds
                    : selectedSegmentId
                        ? [selectedSegmentId]
                        : [];
            if (!markerIds.length && !segmentIds.length) {
                setStatusMessage("Select bins, flat houses, or walls to rotate.");
                return;
            }
            const angleRad = direction < 0 ? -GROUP_ROTATE_STEP_RAD : GROUP_ROTATE_STEP_RAD;
            recordHistory();
            const rotated = rotateGroupSelection(
                markers,
                layoutSegments,
                markerIds,
                segmentIds,
                angleRad
            );
            setMarkers(rotated.markers);
            setLayoutSegments(rotated.segments);
            layoutSegmentsRef.current = rotated.segments;
            setEnclosureRequestId((id) => id + 1);
            setStatusMessage(
                direction < 0
                    ? `Rotated selection ${GROUP_ROTATE_STEP_DEG}° counter-clockwise.`
                    : `Rotated selection ${GROUP_ROTATE_STEP_DEG}° clockwise.`
            );
        },
        [
            markers,
            layoutSegments,
            selectedMarkerIds,
            selectedMarkerId,
            selectedSegmentIds,
            selectedSegmentId,
            recordHistory,
        ]
    );

    const updateFaceAssignment = (faceId, binDataId) => {
        setFaceAssignments((prev) => ({ ...prev, [faceId]: binDataId }));
        if (binDataId != null) {
            setFaceLabels((prev) => {
                if (!(faceId in prev)) return prev;
                const next = { ...prev };
                delete next[faceId];
                return next;
            });
        }
    };

    const updateFaceLabel = (faceId, label) => {
        setFaceLabels((prev) => {
            const raw = label ?? "";
            if (!raw.trim()) {
                const next = { ...prev };
                delete next[faceId];
                return next;
            }
            return { ...prev, [faceId]: raw };
        });
    };

    const updateMarkerLabel = (markerId, label) => {
        setMarkerLabels((prev) => {
            const raw = label ?? "";
            if (!raw.trim()) {
                const next = { ...prev };
                delete next[markerId];
                return next;
            }
            return { ...prev, [markerId]: raw };
        });
    };

    const syncUnsnappedSegmentsBeforeCommit = useCallback(() => {
        const pruned = pruneUnsnappedSegments(layoutSegments, markers);
        const removed = layoutSegments.length - pruned.length;
        if (removed > 0) {
            recordHistory();
            setLayoutSegments(pruned);
            setSelectedSegmentId(null);
            setSelectedSegmentIds([]);
            setStatusMessage(
                `Removed ${removed} unsnapped wall${removed === 1 ? "" : "s"} (orange dashed) before continuing.`
            );
        }
        return removed;
    }, [layoutSegments, markers, recordHistory]);

    const handleDeleteSelected = useCallback(() => {
        const markerIds =
            selectedMarkerIds.length > 0
                ? [...selectedMarkerIds]
                : selectedMarkerId
                    ? [selectedMarkerId]
                    : [];
        let segIds =
            selectedSegmentIds.length > 0
                ? [...selectedSegmentIds]
                : selectedSegmentId
                    ? [selectedSegmentId]
                    : [];
        if (!segIds.length && selectedFaceId) {
            const faces =
                enclosedFacesCacheRef.current.length > 0
                    ? enclosedFacesCacheRef.current
                    : enclosedFaces;
            const face = faces.find((f) => f.id === selectedFaceId);
            const rectId = closedRectIdForFace(face, layoutSegments);
            if (rectId) {
                segIds = layoutSegments
                    .filter((s) => s.closedRectId === rectId)
                    .map((s) => s.id);
            }
        }
        if (!markerIds.length && !segIds.length && !selectedFaceId) {
            setStatusMessage("Nothing selected to delete.");
            return;
        }
        recordHistory();
        setLayoutSegments((prev) => {
            let next = segIds.length ? filterSegmentsAfterDelete(prev, segIds) : prev;
            if (markerIds.length) {
                const gone = new Set(markerIds);
                next = next.filter(
                    (seg) =>
                        !gone.has(seg.markerId1) &&
                        !gone.has(seg.markerId2) &&
                        !gone.has(seg.bulgeMarkerId)
                );
            }
            return next;
        });
        if (segIds.length) {
            setSelectedSegmentId(null);
            setSelectedSegmentIds([]);
        }
        if (markerIds.length) {
            setMarkers((prev) => prev.filter((m) => !markerIds.includes(m.id)));
        }
        setSelectedMarkerIds([]);
        setGroupAnchorMarkerId(null);
        setSelectedMarkerId(null);
        if (selectedFaceId) {
            updateFaceAssignment(selectedFaceId, null);
        }
        setSelectedFaceId(null);
        setStatusMessage("Selection deleted.");
    }, [
        selectedMarkerId,
        selectedMarkerIds,
        selectedSegmentId,
        selectedSegmentIds,
        selectedFaceId,
        layoutSegments,
        enclosedFaces,
        recordHistory,
    ]);

    const applyNudgeDelta = useCallback(
        (dx, dy) => {
            const useLive = nudgeLiveMarkersRef.current != null;
            const ctx = keyboardNudgeCtxRef.current;
            const {
                mode: nudgeMode,
                flatSelectedRectId: nudgeFlatRectId,
                selectedMarkerId: nudgeSelectedMarkerId,
                selectedMarkerIds: nudgeSelectedMarkerIds,
                selectedSegmentId: nudgeSelectedSegmentId,
                selectedSegmentIds: nudgeSelectedSegmentIds,
            } = ctx;
            const liveMarkers = layoutLiveRef.current.markers ?? [];

            const nudgeSegments = (list, markerIds, segmentIds, markersForSeg) => {
                const markerIdSet = new Set(markerIds);
                return list.map((s) => {
                    if (
                        nudgeFlatRectId &&
                        s.closedRectId === nudgeFlatRectId
                    ) {
                        return applyDeltaToSegment(s, dx, dy);
                    }
                    if (segmentIds.length > 0) {
                        if (!segmentIds.includes(s.id)) return s;
                        return applyDeltaToSegment(s, dx, dy);
                    }
                    if (markerIds.length > 0) {
                        if (
                            !segmentTouchesMarkerIds(
                                s,
                                markerIdSet,
                                markersForSeg,
                                list
                            )
                        ) {
                            return s;
                        }
                        return applyDeltaToSegment(s, dx, dy);
                    }
                    return s;
                });
            };

            if (
                nudgeFlatRectId &&
                (nudgeMode === MODES.FLAT || nudgeMode === MODES.PLACE)
            ) {
                if (useLive && nudgeLiveSegmentsRef.current) {
                    nudgeLiveSegmentsRef.current = nudgeSegments(
                        nudgeLiveSegmentsRef.current,
                        [],
                        [],
                        nudgeLiveMarkersRef.current
                    );
                    scheduleLayoutPaint();
                    return;
                }
                setLayoutSegments((prev) => {
                    const next = nudgeSegments(prev, [], [], liveMarkers);
                    layoutSegmentsRef.current = next;
                    return next;
                });
                return;
            }

            const markerIds =
                nudgeSelectedMarkerIds.length > 0
                    ? nudgeSelectedMarkerIds
                    : nudgeSelectedMarkerId
                        ? [nudgeSelectedMarkerId]
                        : [];
            const segmentIds =
                nudgeSelectedSegmentIds.length > 0
                    ? nudgeSelectedSegmentIds
                    : nudgeSelectedSegmentId
                        ? [nudgeSelectedSegmentId]
                        : [];

            if (!markerIds.length && !segmentIds.length) {
                if (
                    (nudgeMode === MODES.FLAT || nudgeMode === MODES.PLACE) &&
                    nudgeFlatRectId
                ) {
                    /* flat-only nudge below */
                } else {
                    return;
                }
            }

            if (useLive) {
                if (markerIds.length) {
                    nudgeLiveMarkersRef.current = nudgeLiveMarkersRef.current.map(
                        (m) => {
                            if (!markerIds.includes(m.id)) return m;
                            return { ...m, x: m.x + dx, y: m.y + dy };
                        }
                    );
                }
                if (nudgeLiveSegmentsRef.current) {
                    nudgeLiveSegmentsRef.current = nudgeSegments(
                        nudgeLiveSegmentsRef.current,
                        markerIds,
                        segmentIds,
                        nudgeLiveMarkersRef.current
                    );
                }
                scheduleLayoutPaint();
                return;
            }

            if (markerIds.length) {
                setMarkers((prev) =>
                    prev.map((m) => {
                        if (!markerIds.includes(m.id)) return m;
                        return { ...m, x: m.x + dx, y: m.y + dy };
                    })
                );
            }
            setLayoutSegments((prev) => {
                const next = nudgeSegments(
                    prev,
                    markerIds,
                    segmentIds,
                    liveMarkers
                );
                if (next === prev) return prev;
                layoutSegmentsRef.current = next;
                return next;
            });
        },
        [scheduleLayoutPaint]
    );

    const flushPendingNudge = useCallback(() => {
        const { dx, dy } = nudgePendingRef.current;
        if (dx === 0 && dy === 0) return;
        nudgePendingRef.current = { dx: 0, dy: 0 };
        applyNudgeDelta(dx, dy);
    }, [applyNudgeDelta]);

    const endArrowNudgeSession = useCallback(() => {
        if (nudgeRafRef.current != null) {
            cancelAnimationFrame(nudgeRafRef.current);
            nudgeRafRef.current = null;
        }
        flushPendingNudge();
        if (nudgeLiveMarkersRef.current) {
            setMarkers(nudgeLiveMarkersRef.current);
            if (nudgeLiveSegmentsRef.current) {
                setLayoutSegments(nudgeLiveSegmentsRef.current);
                layoutSegmentsRef.current = nudgeLiveSegmentsRef.current;
            }
            nudgeLiveMarkersRef.current = null;
            nudgeLiveSegmentsRef.current = null;
        }
        layoutGeometryBusyRef.current = false;
        nudgeHistoryRecordedRef.current = false;
        setEnclosureRequestId((n) => n + 1);
    }, [flushPendingNudge]);

    const scheduleArrowNudge = useCallback(
        (dx, dy) => {
            if (!nudgeHistoryRecordedRef.current) {
                recordHistory();
                nudgeHistoryRecordedRef.current = true;
            }
            layoutGeometryBusyRef.current = true;
            if (!nudgeLiveMarkersRef.current) {
                const live = layoutLiveRef.current;
                nudgeLiveMarkersRef.current = (live.markers ?? []).map((m) => ({
                    ...m,
                }));
                nudgeLiveSegmentsRef.current = (live.layoutSegments ?? []).map(
                    (s) => cloneSegmentForHistory(s)
                );
            }
            nudgePendingRef.current = {
                dx: nudgePendingRef.current.dx + dx,
                dy: nudgePendingRef.current.dy + dy,
            };
            if (nudgeRafRef.current != null) return;
            nudgeRafRef.current = requestAnimationFrame(() => {
                nudgeRafRef.current = null;
                flushPendingNudge();
            });
        },
        [recordHistory, flushPendingNudge]
    );

    const isArrowNudgeKey = (key) =>
        key === "ArrowUp" ||
        key === "ArrowDown" ||
        key === "ArrowLeft" ||
        key === "ArrowRight";

    useEffect(() => {
        const onKeyDown = (e) => {
            if (
                /^(INPUT|TEXTAREA|SELECT)$/.test(e.target?.tagName) ||
                e.target?.closest?.(
                    "input, textarea, select, [contenteditable='true'], .MuiInputBase-input"
                )
            ) {
                return;
            }
            if (e.target?.closest?.(".MuiModal-root, .MuiPopover-root, .MuiMenu-root")) return;
            if (
                e.shiftKey &&
                (e.key === "ArrowLeft" || e.key === "ArrowRight")
            ) {
                const ctx = keyboardNudgeCtxRef.current;
                const markerIds =
                    ctx.selectedMarkerIds.length > 0
                        ? ctx.selectedMarkerIds
                        : ctx.selectedMarkerId
                            ? [ctx.selectedMarkerId]
                            : [];
                const segmentIds =
                    ctx.selectedSegmentIds.length > 0
                        ? ctx.selectedSegmentIds
                        : ctx.selectedSegmentId
                            ? [ctx.selectedSegmentId]
                            : [];
                if (!markerIds.length && !segmentIds.length) return;
                e.preventDefault();
                e.stopPropagation();
                if (e.repeat) return;
                cancelArrowNudgeSessionWithoutCommit();
                recordHistory();
                const angleRad =
                    e.key === "ArrowRight"
                        ? GROUP_ROTATE_FINE_RAD
                        : -GROUP_ROTATE_FINE_RAD;
                const rotated = rotateGroupSelection(
                    ctx.markers,
                    ctx.layoutSegments,
                    markerIds,
                    segmentIds,
                    angleRad
                );
                setMarkers(rotated.markers);
                setLayoutSegments(rotated.segments);
                layoutSegmentsRef.current = rotated.segments;
                setEnclosureRequestId((id) => id + 1);
                setStatusMessage(
                    `Rotated selection ${GROUP_ROTATE_FINE_DEG}° ${e.key === "ArrowRight" ? "clockwise" : "counter-clockwise"}.`
                );
                return;
            }
            if (isArrowNudgeKey(e.key)) {
                const ctx = keyboardNudgeCtxRef.current;
                const hasMarkers =
                    ctx.selectedMarkerIds.length > 0 || !!ctx.selectedMarkerId;
                const hasSegments =
                    ctx.selectedSegmentIds.length > 0 || !!ctx.selectedSegmentId;
                const hasFlat =
                    (ctx.mode === MODES.FLAT || ctx.mode === MODES.PLACE) &&
                    !!ctx.flatSelectedRectId;
                if (!hasMarkers && !hasSegments && !hasFlat) return;
                e.preventDefault();
                e.stopPropagation();
                if (!e.repeat) {
                    nudgeArrowKeysHeldRef.current += 1;
                }
                const step = e.shiftKey ? ARROW_NUDGE_FAST_PX : ARROW_NUDGE_PX;
                let dx = 0;
                let dy = 0;
                if (e.key === "ArrowLeft") dx = -step;
                if (e.key === "ArrowRight") dx = step;
                if (e.key === "ArrowUp") dy = -step;
                if (e.key === "ArrowDown") dy = step;
                scheduleArrowNudge(dx, dy);
                return;
            }
            if (e.key === "Delete" || e.key === "Backspace") {
                e.preventDefault();
                handleDeleteSelected();
            } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
                e.preventDefault();
                if (e.shiftKey) handleRedo();
                else handleUndo();
            } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y") {
                e.preventDefault();
                handleRedo();
            }
        };
        const onKeyUp = (e) => {
            if (!isArrowNudgeKey(e.key)) return;
            if (nudgeArrowKeysHeldRef.current > 0) {
                nudgeArrowKeysHeldRef.current -= 1;
            }
            if (nudgeArrowKeysHeldRef.current <= 0) {
                nudgeArrowKeysHeldRef.current = 0;
                endArrowNudgeSession();
            }
        };
        const onBlur = () => {
            if (nudgeArrowKeysHeldRef.current <= 0) return;
            nudgeArrowKeysHeldRef.current = 0;
            endArrowNudgeSession();
        };
        const onVisibilityChange = () => {
            if (document.visibilityState !== "hidden") return;
            onBlur();
        };
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("keyup", onKeyUp);
        window.addEventListener("blur", onBlur);
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("keyup", onKeyUp);
            window.removeEventListener("blur", onBlur);
            document.removeEventListener("visibilitychange", onVisibilityChange);
            if (
                nudgeLiveMarkersRef.current != null ||
                nudgeArrowKeysHeldRef.current > 0 ||
                nudgePendingRef.current.dx !== 0 ||
                nudgePendingRef.current.dy !== 0
            ) {
                endArrowNudgeSession();
            } else if (nudgeRafRef.current != null) {
                cancelAnimationFrame(nudgeRafRef.current);
                nudgeRafRef.current = null;
            }
        };
    }, [
        handleDeleteSelected,
        handleUndo,
        handleRedo,
        recordHistory,
        scheduleArrowNudge,
        endArrowNudgeSession,
        cancelArrowNudgeSessionWithoutCommit,
    ]);

    /**
     * Object detection: run Hough circles on the uploaded image, drop one marker per detected
     * silo, infer feet/pixel from the median detected radius vs the median real bin diameter.
     *
     * Existing assigned markers are preserved (we skip detected circles that overlap them).
     */
    const handleAutoDetect = useCallback(async () => {
        if (!imageDataUrl || !imageRef.current) {
            setStatusMessage("Upload an image first.");
            return;
        }
        if (siteBins.length === 0) {
            setStatusMessage("No bins loaded for this site.");
            return;
        }
        setDetectBusy(true);
        setStatusMessage("Detecting bins…");
        try {
            const img = imageRef.current;
            const longSide = Math.max(img.naturalWidth, img.naturalHeight);
            const fpp = feetPerPixel > 0 ? feetPerPixel : PIXEL_TO_FEET_DEFAULT;

            let radiusHints;
            if (fpp > 0) {
                const typicalFt = getMarkerFootprintFt({ radiusPx: DEFAULT_MARKER_RADIUS_PX }, fpp);
                const r = (ft) => Math.max(6, (ft / 2) / fpp);
                const minR = r(typicalFt * 0.5);
                const maxR = r(typicalFt * 2.5);
                radiusHints = {
                    minRadiusPx: minR,
                    maxRadiusPx: maxR,
                    minDistPx: minR * 1.5,
                };
            } else {
                radiusHints = {
                    minRadiusPx: Math.max(8, longSide * 0.008),
                    maxRadiusPx: Math.max(40, longSide * 0.18),
                    minDistPx: Math.max(20, longSide * 0.015),
                };
            }

            await new Promise((r) => setTimeout(r, 0));

            if (!window.__siteLayoutOpenCv?.Mat && !window.__siteLayoutOpenCvPromise) {
                loadOpenCV()
                    .then(() => setOpencvReady(true))
                    .catch(() => { /* canvas detector is sufficient */ });
            }

            let detected = detectCirclesCanvasFallback(img, radiusHints);
            let usedFallback = true;

            if (window.__siteLayoutOpenCv?.Mat && window.__siteLayoutOpenCv.HoughCircles) {
                try {
                    const cvDetected = await withTimeout(
                        detectCirclesInImage(img, radiusHints),
                        OPENCV_DETECT_TIMEOUT_MS,
                        "OpenCV detection timed out"
                    );
                    if (cvDetected.length > detected.length) {
                        detected = cvDetected;
                        usedFallback = false;
                    }
                } catch (opencvErr) {
                    console.warn("OpenCV detection skipped:", opencvErr);
                }
            }

            if (detected.length === 0) {
                setStatusMessage("No bins detected. Try a sharper top-down image or place markers manually.");
                return;
            }

            const detectedRadii = detected.map(c => c.r);
            const medianRPx = median(detectedRadii);

            const placementFaces =
                enclosedFacesCacheRef.current.length > 0
                    ? enclosedFacesCacheRef.current
                    : enclosedFaces;
            const newMarkers = [...markers];
            let added = 0;
            for (const c of detected) {
                if (
                    layoutPlacementBlockedForRoundBin({
                        cx: c.x,
                        cy: c.y,
                        radiusPx: c.r,
                        markers: newMarkers,
                        segments: layoutSegments,
                        faces: placementFaces,
                    })
                ) {
                    continue;
                }
                newMarkers.push({
                    id: `m_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
                    x: c.x,
                    y: c.y,
                    binDataId: null,
                    radiusPx: c.r,
                });
                added++;
            }

            setMarkers(newMarkers);

            const fallbackNote = usedFallback ? " (basic detector — adjust circles as needed)" : "";
            setStatusMessage(
                `Detected ${detected.length} bin${detected.length === 1 ? "" : "s"} (added ${added}).${fallbackNote} Resize circles and set diameter (ft) in the sidebar, then assign bins.`
            );
        } catch (e) {
            console.error("Bin detection error", e);
            setStatusMessage(`Detection failed: ${e?.message ?? e}. Place markers manually or try another image.`);
        } finally {
            setDetectBusy(false);
        }
    }, [imageDataUrl, siteBins, markers, feetPerPixel]);

    const layoutTheme = useMemo(() => {
        const accent = "#f4cc8e";
        const darkBg = "#2d2d2d";
        const menuPaperSx = {
            zIndex: 14000,
            backgroundColor: getThemeColor("#ffffff", darkBg),
            color: getThemeColor("#333333", accent),
            border: `1px solid ${getThemeColor("#cccccc", accent)}`,
            "& .MuiMenuItem-root": {
                color: getThemeColor("#333333", accent),
                backgroundColor: "transparent",
                "&.Mui-selected": {
                    backgroundColor: getThemeColor("#e8f0fe", "#3d3d3d"),
                },
                "&:hover": {
                    backgroundColor: getThemeColor("#f5f5f5", "#3d3d3d"),
                },
                "&.Mui-disabled": {
                    color: getThemeColor("#bbb", LAYOUT_DARK_TEXT_MUTED),
                    opacity: 1,
                },
            },
            "& .MuiList-root": {
                backgroundColor: getThemeColor("#ffffff", darkBg),
            },
        };
        const selectSx = {
            fontSize: 16,
            background: getThemeColor("#ffffff", darkBg),
            color: getThemeColor("#333333", accent),
            "& .MuiSelect-select": {
                color: getThemeColor("#333333", accent),
            },
            "&.Mui-disabled .MuiSelect-select": {
                color: getThemeColor("#333333", accent),
                WebkitTextFillColor: getThemeColor("#333333", accent),
            },
            "& .MuiOutlinedInput-input": {
                color: getThemeColor("#333333", accent),
            },
            "& .MuiOutlinedInput-root": {
                background: getThemeColor("#ffffff", darkBg),
                "& fieldset": {
                    borderColor: getThemeColor("#cccccc", accent),
                },
                "&:hover fieldset": {
                    borderColor: getThemeColor("#999999", accent),
                },
                "&.Mui-focused fieldset": {
                    borderColor: getThemeColor("#1976d2", accent),
                },
            },
            "& .MuiSelect-icon": {
                color: getThemeColor("#555555", accent),
            },
        };
        return {
            menuProps: withScrollHintMenuProps({
                disableScrollLock: true,
                disablePortal: false,
                PaperProps: { sx: menuPaperSx },
                MenuListProps: {
                    sx: { backgroundColor: getThemeColor("#ffffff", darkBg) },
                },
            }),
            selectSx,
            formControlSx: {
                "& .MuiInputLabel-root": {
                    color: getThemeColor("#000000", accent),
                },
                "& .MuiInputLabel-root.Mui-focused": {
                    color: getThemeColor("#000000", accent),
                },
                "& .MuiInputLabel-root.MuiInputLabel-shrink": {
                    transform: "translate(14px, -9px) scale(0.75)",
                },
            },
            inputLabelSx: {
                color: getThemeColor("#000000", accent),
                "&.Mui-focused": { color: getThemeColor("#000000", accent) },
            },
            tooltipSlotProps: {
                tooltip: {
                    sx: {
                        bgcolor: getThemeColor("#333", "#2d2d2d"),
                        color: getThemeColor("#fff", LAYOUT_DARK_TEXT),
                        fontSize: 12,
                        border: `1px solid ${getThemeColor("#ccc", "#555")}`,
                        maxWidth: 360,
                    },
                },
            },
            panelBorder: `1px solid ${getThemeColor("#ccc", "#444")}`,
            captionColor: getThemeColor("#555", LAYOUT_DARK_TEXT_MUTED),
            bodyColor: getThemeColor("#444", LAYOUT_DARK_TEXT),
            headingColor: getThemeColor("#000", LAYOUT_DARK_TEXT),
            btnAction: {
                color: getThemeColor("#333333", accent),
                backgroundColor: getThemeColor("transparent", darkBg),
                opacity: 1,
                border: `1px solid ${getThemeColor("#ccc", accent)}`,
                borderRadius: 1,
                "&.MuiButton-outlined": {
                    border: `1px solid ${getThemeColor("#ccc", accent)}`,
                },
                "&:hover": {
                    opacity: 1,
                    backgroundColor: getThemeColor("#f5f5f5", "#3d3d3d"),
                    border: `1px solid ${getThemeColor("#ccc", accent)}`,
                },
            },
            btnBase: { textTransform: "none", boxShadow: "none" },
            btnOutlined: {
                color: getThemeColor("#333333", accent),
                backgroundColor: getThemeColor("transparent", darkBg),
                opacity: 0.45,
                border: "none",
                "&.MuiButton-outlined": {
                    border: "none",
                },
                "&:hover": {
                    opacity: 0.65,
                    backgroundColor: getThemeColor("#f5f5f5", "#3d3d3d"),
                    border: "none",
                },
            },
            btnActive: {
                opacity: 1,
                color: getThemeColor("#333333", accent),
                backgroundColor: getThemeColor("transparent", darkBg),
                border: `1px solid ${getThemeColor("#ccc", accent)}`,
                borderRadius: 1,
                "&.MuiButton-outlined": {
                    border: `1px solid ${getThemeColor("#ccc", accent)}`,
                },
                "&:hover": {
                    opacity: 1,
                    backgroundColor: getThemeColor("#f5f5f5", "#3d3d3d"),
                    border: `1px solid ${getThemeColor("#ccc", accent)}`,
                },
            },
            btnContained: {
                backgroundColor: getThemeColor("#e0e0e0", "#3d3d3d"),
                color: getThemeColor("#333333", accent),
                "&:hover": {
                    backgroundColor: getThemeColor("#d0d0d0", "#4a4a4a"),
                },
            },
            btnText: {
                color: getThemeColor("#333333", accent),
                minWidth: "auto",
                "&:hover": {
                    backgroundColor: getThemeColor("rgba(0,0,0,0.04)", "rgba(244,204,142,0.08)"),
                },
            },
        };
    }, [forceUpdate]);

    const layoutBtnSx = useCallback(
        (active, variant = "outlined") => {
            const base = { ...layoutTheme.btnBase };
            if (variant === "text") return { ...base, ...layoutTheme.btnText, opacity: 1 };
            if (variant === "contained") return { ...base, ...layoutTheme.btnContained, opacity: 1 };
            if (variant === "action") return { ...base, ...layoutTheme.btnAction };
            if (active) return { ...base, ...layoutTheme.btnActive };
            return { ...base, ...layoutTheme.btnOutlined };
        },
        [layoutTheme]
    );

    /** Toolbar highlight (mode buttons, graph toggle) — matches undo/redo chrome. */
    const layoutToolbarHighlightSx = useCallback(
        (active) => ({
            ...layoutTheme.btnBase,
            border: `1px solid ${getThemeColor("#ccc", "#f4cc8e")}`,
            borderRadius: 1,
            color: getThemeColor("#333333", "#f4cc8e"),
            opacity: active ? 1 : 0.45,
            backgroundColor: getThemeColor("transparent", "#2d2d2d"),
            "&:hover": {
                opacity: 1,
                backgroundColor: getThemeColor("#f5f5f5", "#3d3d3d"),
            },
        }),
        [layoutTheme]
    );

    /** Graph toggle + group toolbar on the canvas — solid chrome, always readable on the grid. */
    const layoutCanvasOverlayIconSx = useCallback(
        () => ({
            ...layoutTheme.btnBase,
            border: `1px solid ${getThemeColor("#ccc", "#f4cc8e")}`,
            borderRadius: 1,
            color: getThemeColor("#333333", "#f4cc8e"),
            opacity: 1,
            backgroundColor: getThemeColor("#f5f5f5", "#2d2d2d"),
            boxShadow: getThemeColor(
                "0 1px 3px rgba(0,0,0,0.12)",
                "0 1px 4px rgba(0,0,0,0.45)"
            ),
            "&:hover": {
                backgroundColor: getThemeColor("#eeeeee", "#3d3d3d"),
            },
            "&.MuiIconButton-root.Mui-disabled": {
                opacity: 0.65,
                color: getThemeColor("#888888", "#c4a86a"),
                WebkitTextFillColor: getThemeColor("#888888", "#c4a86a"),
                backgroundColor: getThemeColor("#eeeeee", "#2d2d2d"),
                borderColor: getThemeColor("#cccccc", "#6b5a30"),
            },
            "&.Mui-disabled .MuiSvgIcon-root": {
                color: getThemeColor("#888888", "#c4a86a"),
            },
        }),
        [layoutTheme]
    );

    /** Zoom +/- on canvas — equal width/height (text glyphs, not square icons). */
    const layoutCanvasZoomButtonSx = useCallback(
        () => ({
            ...layoutCanvasOverlayIconSx(),
            boxSizing: "border-box",
            width: 32,
            height: 32,
            minWidth: 32,
            minHeight: 32,
            p: 0,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
        }),
        [layoutCanvasOverlayIconSx]
    );

    const binSelectValue = (id) => (id == null || id === "" ? "" : String(id));

    const isBinAssignedElsewhere = useCallback(
        (binDataId, { excludeMarkerId, excludeFaceId } = {}) => {
            if (binDataId == null) return false;
            if (
                markers.some(
                    (m) =>
                        m.id !== excludeMarkerId &&
                        sameBinDataId(m.binDataId, binDataId)
                )
            ) {
                return true;
            }
            return enclosedFaces.some(
                (f) =>
                    f.id !== excludeFaceId &&
                    sameBinDataId(faceAssignments[f.id], binDataId)
            );
        },
        [markers, enclosedFaces, faceAssignments]
    );

    const toolbarLockedByPreview = preview3dOpen;

    const confirmModal = (
        <ConfirmDialog
            open={!!confirmDialog}
            onClose={() => closeConfirmDialog(false)}
            titleId="site-layout-confirm-title"
            title={confirmDialog?.title ?? ""}
            message={confirmDialog?.message ?? null}
            modalProps={{
                disablePortal: true,
                container: () => pageContainerRef.current,
            }}
            actions={(
                <>
                    <MuiMaterials.Button
                        size="small"
                        variant="outlined"
                        sx={confirmDialogButtonSx()}
                        onClick={() => closeConfirmDialog(false)}
                    >
                        {confirmDialog?.cancelLabel ?? "Cancel"}
                    </MuiMaterials.Button>
                    <MuiMaterials.Button
                        size="small"
                        variant="outlined"
                        sx={confirmDialogButtonSx()}
                        onClick={() => closeConfirmDialog(true)}
                    >
                        {confirmDialog?.confirmLabel ?? "OK"}
                    </MuiMaterials.Button>
                </>
            )}
        />
    );

    if (siteSetupOpen) {
        return (
            <WindowChrome
                title="Layout"
                toolTitle="Layout"
                siteWindowId={SITE_WINDOW_IDS.layout}
                siteGuid={null}
                onClose={handleExitSiteLayout}
                closeAriaLabel="Close Site Layout"
            >
                <SiteSelectPhase
                    value={pickerSiteGuid}
                    onChange={setPickerSiteGuid}
                    onContinue={handleSiteSetupContinue}
                    onCancel={handleExitSiteLayout}
                    loading={sitesLoading || siteLoading}
                    filterSite={(s) => !s.isHms}
                />
            </WindowChrome>
        );
    }

    return (
        <div
            ref={pageContainerRef}
            style={{
                display: "flex",
                flexDirection: "column",
                color: getThemeColor("#000", LAYOUT_DARK_TEXT),
                position: "fixed",
                inset: 0,
                zIndex: 1000,
                overflow: "hidden",
                boxSizing: "border-box",
                background: getThemeColor("#e8e8e8", "#1a1a1a"),
            }}
        >
            {confirmModal}

                <ChromeHeaderBar
                    paper={getThemeColor("#ffffff", "#2d2d2d")}
                    border={getThemeColor("#e0e0e0", "#3d3d3d")}
                    textSecondary={getThemeColor("#666666", LAYOUT_DARK_TEXT_MUTED)}
                    onClose={onToolbarClick(handleExitSiteLayout)}
                    closeAriaLabel="Close Site Layout"
                    leading={
                        siteReady ? (
                            <SiteWindowHeaderLeading
                                toolTitle="Layout"
                                siteWindowId={SITE_WINDOW_IDS.layout}
                                siteGuid={activeSiteGuid || null}
                                textSecondary={getThemeColor("#666666", LAYOUT_DARK_TEXT_MUTED)}
                                border={getThemeColor("#e0e0e0", "#3d3d3d")}
                                showSiteSelect
                                siteSelectValue={activeSiteGuid || ""}
                                onSiteChange={(guid) => {
                                    if (!guid || toolbarLockedByPreview || siteLoading) return;
                                    selectSite(guid);
                                }}
                                filterSite={(s) => !s.isHms}
                                siteSelectDisabled={toolbarLockedByPreview || siteLoading}
                                includeTool={false}
                            />
                        ) : null
                    }
                    tool={
                        <SiteWindowToolNav
                            toolTitle="Layout"
                            siteWindowId={SITE_WINDOW_IDS.layout}
                            siteGuid={activeSiteGuid || null}
                            textSecondary={getThemeColor("#666666", LAYOUT_DARK_TEXT_MUTED)}
                        />
                    }
                />

            <input
                id="site-layout-image-input"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: "none" }}
            />
            <ChromeActionBar
                paper={getThemeColor("#ffffff", "#2d2d2d")}
                border={getThemeColor("#e0e0e0", "#3d3d3d")}
                sx={{ color: getThemeColor("#333333", LAYOUT_DARK_TEXT) }}
            >
                    {imageDataUrl ? (
                        <MuiMaterials.Tooltip
                            title="Remove the background image and keep bins, walls, and arcs on a blank canvas."
                            arrow
                            slotProps={layoutTheme.tooltipSlotProps}
                        >
                            <span>
                                <MuiMaterials.Button
                                    variant="contained"
                                    onClick={onToolbarClick(handleRemoveImage)}
                                    disabled={toolbarLockedByPreview || busy}
                                    sx={{ ...layoutBtnSx(false, "contained"), flexShrink: 0 }}
                                >
                                    Remove image
                                </MuiMaterials.Button>
                            </span>
                        </MuiMaterials.Tooltip>
                    ) : (
                        <label htmlFor="site-layout-image-input">
                            <MuiMaterials.Button
                                variant="contained"
                                component="span"
                                disabled={toolbarLockedByPreview}
                                onClick={onToolbarClick(() => {})}
                                sx={{ ...layoutBtnSx(false, "contained"), flexShrink: 0 }}
                            >
                                Upload
                            </MuiMaterials.Button>
                        </label>
                    )}
                    <MuiMaterials.Button
                        variant="outlined"
                        onClick={() => changeLayoutMode(MODES.PLACE)}
                        disabled={toolbarLockedByPreview || !layoutSurfaceActive}
                        sx={layoutBtnSx(mode === MODES.PLACE)}
                    >
                        Bin
                    </MuiMaterials.Button>
                    <MuiMaterials.Button
                        variant="outlined"
                        onClick={() => changeLayoutMode(MODES.WALL)}
                        disabled={
                            toolbarLockedByPreview ||
                            !layoutSurfaceActive ||
                            markers.length < 2
                        }
                        sx={layoutBtnSx(mode === MODES.WALL)}
                    >
                        Wall
                    </MuiMaterials.Button>
                    <MuiMaterials.Button
                        variant="outlined"
                        onClick={() => changeLayoutMode(MODES.ARC)}
                        disabled={
                            toolbarLockedByPreview ||
                            !layoutSurfaceActive ||
                            markers.length < 2
                        }
                        sx={layoutBtnSx(mode === MODES.ARC)}
                    >
                        Arc
                    </MuiMaterials.Button>
                    <MuiMaterials.Tooltip
                        title="Click to place a flat house (default 30×30 ft). Adjust W / L in the sidebar or drag corners."
                        arrow
                        slotProps={layoutTheme.tooltipSlotProps}
                    >
                        <span>
                            <MuiMaterials.Button
                                variant="outlined"
                                onClick={() => changeLayoutMode(MODES.FLAT)}
                                disabled={toolbarLockedByPreview || !layoutSurfaceActive}
                                sx={layoutBtnSx(mode === MODES.FLAT)}
                            >
                                Flat
                            </MuiMaterials.Button>
                        </span>
                    </MuiMaterials.Tooltip>
                    <MuiMaterials.Tooltip title="Undo" slotProps={layoutTheme.tooltipSlotProps}>
                        <span>
                            <MuiMaterials.IconButton
                                aria-label="Undo"
                                onClick={onToolbarClick(handleUndo)}
                                disabled={toolbarLockedByPreview || undoStackLen === 0}
                                sx={{
                                    ...layoutBtnSx(false, "action"),
                                    border: `1px solid ${getThemeColor("#ccc", "#f4cc8e")}`,
                                    borderRadius: 1,
                                    opacity: undoStackLen === 0 ? 0.35 : 1,
                                    color: getThemeColor("#333", LAYOUT_DARK_TEXT),
                                }}
                            >
                                <UndoIcon fontSize="small" />
                            </MuiMaterials.IconButton>
                        </span>
                    </MuiMaterials.Tooltip>
                    <MuiMaterials.Tooltip title="Redo" slotProps={layoutTheme.tooltipSlotProps}>
                        <span>
                            <MuiMaterials.IconButton
                                aria-label="Redo"
                                onClick={onToolbarClick(handleRedo)}
                                disabled={toolbarLockedByPreview || redoStackLen === 0}
                                sx={{
                                    ...layoutBtnSx(false, "action"),
                                    border: `1px solid ${getThemeColor("#ccc", "#f4cc8e")}`,
                                    borderRadius: 1,
                                    opacity: redoStackLen === 0 ? 0.35 : 1,
                                    color: getThemeColor("#333", LAYOUT_DARK_TEXT),
                                }}
                            >
                                <RedoIcon fontSize="small" />
                            </MuiMaterials.IconButton>
                        </span>
                    </MuiMaterials.Tooltip>
                    {imageDataUrl && (
                        <MuiMaterials.Tooltip
                            title="Find bin shapes (OpenCV Hough circles). First run loads WASM in the background after you upload; re-run to add missed bins."
                            arrow
                            slotProps={layoutTheme.tooltipSlotProps}
                        >
                            <span>
                                <MuiMaterials.Button
                                    variant="contained"
                                    onClick={onToolbarClick(handleAutoDetect)}
                                    disabled={
                                        toolbarLockedByPreview ||
                                        siteBins.length === 0 ||
                                        detectBusy
                                    }
                                    sx={layoutBtnSx(false, "contained")}
                                >
                                    {detectBusy ? "Detecting…" : "Detect bins"}
                                </MuiMaterials.Button>
                            </span>
                        </MuiMaterials.Tooltip>
                    )}
                    <MuiMaterials.Button
                        variant="contained"
                        onClick={onToolbarClick(handleClearAll)}
                        disabled={toolbarLockedByPreview || !layoutSurfaceActive}
                        sx={layoutBtnSx(false, "contained")}
                    >
                        Clear all
                    </MuiMaterials.Button>
                    <MuiMaterials.Tooltip
                        title="Load from current site 3D"
                        arrow
                        slotProps={layoutTheme.tooltipSlotProps}
                    >
                        <span>
                            <MuiMaterials.Button
                                variant="contained"
                                onClick={onToolbarClick(handleLoadFromSite3d)}
                                disabled={
                                    toolbarLockedByPreview ||
                                    !siteReady ||
                                    busy ||
                                    siteLoading ||
                                    !layoutSurfaceActive
                                }
                                sx={layoutBtnSx(false, "contained")}
                            >
                                {busy ? "Loading…" : "Load"}
                            </MuiMaterials.Button>
                        </span>
                    </MuiMaterials.Tooltip>
                    <MuiMaterials.Tooltip
                        title="Open production Site 3D for this site"
                        arrow
                        slotProps={layoutTheme.tooltipSlotProps}
                    >
                        <span>
                            <MuiMaterials.Button
                                variant="contained"
                                onClick={onToolbarClick(handleOpenSite3d)}
                                disabled={!siteReady || busy || siteLoading}
                                sx={layoutBtnSx(false, "contained")}
                            >
                                Site 3D
                            </MuiMaterials.Button>
                        </span>
                    </MuiMaterials.Tooltip>
                    <MuiMaterials.Tooltip
                        title={
                            preview3dOpen
                                ? "Close 3D preview"
                                : "Preview layout in Site 3D"
                        }
                        arrow
                        slotProps={layoutTheme.tooltipSlotProps}
                    >
                        <span>
                            <MuiMaterials.Button
                                variant="outlined"
                                onClick={onToolbarClick(handlePreview)}
                                disabled={
                                    !siteReady ||
                                    busy ||
                                    (!preview3dOpen && !canPreviewLayout)
                                }
                                sx={layoutBtnSx(preview3dOpen)}
                            >
                                Preview
                            </MuiMaterials.Button>
                        </span>
                    </MuiMaterials.Tooltip>
                    <MuiMaterials.Box data-chrome-spacer aria-hidden />
                    <MuiMaterials.Tooltip
                        title="Save markers, walls, arcs, and area assignments to EditorDraftJson"
                        arrow
                        slotProps={layoutTheme.tooltipSlotProps}
                    >
                        <span>
                            <MuiMaterials.Button
                                variant="outlined"
                                onClick={onToolbarClick(handleSave)}
                                disabled={toolbarLockedByPreview || !siteReady || busy}
                                sx={{ ...layoutBtnSx(false, "action"), flexShrink: 0 }}
                            >
                                {busy ? "Saving…" : "Save"}
                            </MuiMaterials.Button>
                        </span>
                    </MuiMaterials.Tooltip>
                    <MuiMaterials.Tooltip
                        title={
                            canSaveLayoutTables
                                ? "Apply layout to CustomerSiteLayout tables and Site 3D view"
                                : "Place round bins or draw walls/arcs first"
                        }
                        arrow
                        slotProps={layoutTheme.tooltipSlotProps}
                    >
                        <span>
                            <MuiMaterials.Button
                                variant="contained"
                                onClick={onToolbarClick(handleSaveLayoutTables)}
                                disabled={
                                    toolbarLockedByPreview ||
                                    !siteReady ||
                                    busy ||
                                    !canSaveLayoutTables
                                }
                                sx={{ ...layoutBtnSx(false, "contained"), flexShrink: 0 }}
                            >
                                {busy ? "Applying…" : "Apply"}
                            </MuiMaterials.Button>
                        </span>
                    </MuiMaterials.Tooltip>
            </ChromeActionBar>

            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    overflow: "hidden",
                    padding: 12,
                    boxSizing: "border-box",
                    opacity: siteReady && !siteSetupOpen ? 1 : 0.45,
                    pointerEvents: siteReady && !siteSetupOpen ? "auto" : "none",
                }}
            >

                <div
                    style={{
                        display: "flex",
                        flexDirection: narrowLayout ? "column" : "row",
                        gap: narrowLayout ? 8 : LAYOUT_CANVAS_SIDEBAR_GAP_PX,
                        alignItems: "stretch",
                        flex: 1,
                        minHeight: 0,
                        minWidth: 0,
                        overflow: "hidden",
                    }}
                >
                <div
                    ref={containerRef}
                    style={{
                        flex: 1,
                        minWidth: 0,
                        minHeight: narrowLayout ? 180 : 0,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        position: "relative",
                    }}
                >
                {layoutSurfaceActive ? (
                    <>
                    <div
                        ref={viewportRef}
                        data-layout-viewport
                        onPointerDown={handleViewportMouseDown}
                        onMouseDown={handleViewportMouseDown}
                        onPointerMove={handleCanvasMouseMove}
                        onMouseMove={handleCanvasMouseMove}
                        onPointerUp={handleViewportMouseUpGuarded}
                        onPointerCancel={handleViewportMouseUpGuarded}
                        onMouseUp={handleViewportMouseUpGuarded}
                        onMouseLeave={handleViewportMouseUpGuarded}
                        style={{
                            position: "relative",
                            flex: 1,
                            minHeight: 0,
                            width: "100%",
                            overflow: "hidden",
                            touchAction: "none",
                            border: `1px solid ${getThemeColor("#888", "#444")}`,
                            background: getThemeColor("#fafafa", "#1a1a1a"),
                            cursor: toolbarLockedByPreview
                                ? "default"
                                : spaceHeld
                                  ? "grab"
                                  : "default",
                            pointerEvents: toolbarLockedByPreview ? "none" : "auto",
                        }}
                    >
                        {showGroupOverlay && (
                            <MuiMaterials.Box
                                onMouseDown={(e) => e.stopPropagation()}
                                sx={{
                                    position: "absolute",
                                    top: 8,
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    zIndex: 4,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                <MuiMaterials.Tooltip title="Column (align X)" slotProps={layoutTheme.tooltipSlotProps}>
                                    <span>
                                        <MuiMaterials.IconButton
                                            aria-label="Column"
                                            size="small"
                                            disabled={
                                                toolbarLockedByPreview || !groupAlignEnabled
                                            }
                                            onClick={() => handleAlignGroup("alignX")}
                                            sx={layoutCanvasOverlayIconSx()}
                                        >
                                            <ViewColumnIcon fontSize="small" />
                                        </MuiMaterials.IconButton>
                                    </span>
                                </MuiMaterials.Tooltip>
                                <MuiMaterials.Tooltip title="Row (align Y)" slotProps={layoutTheme.tooltipSlotProps}>
                                    <span>
                                        <MuiMaterials.IconButton
                                            aria-label="Row"
                                            size="small"
                                            disabled={
                                                toolbarLockedByPreview || !groupAlignEnabled
                                            }
                                            onClick={() => handleAlignGroup("alignY")}
                                            sx={layoutCanvasOverlayIconSx()}
                                        >
                                            <ViewStreamIcon fontSize="small" />
                                        </MuiMaterials.IconButton>
                                    </span>
                                </MuiMaterials.Tooltip>
                                <MuiMaterials.Tooltip title="Same size" slotProps={layoutTheme.tooltipSlotProps}>
                                    <span>
                                        <MuiMaterials.IconButton
                                            aria-label="Same size"
                                            size="small"
                                            disabled={
                                                toolbarLockedByPreview || !groupAlignEnabled
                                            }
                                            onClick={() => handleAlignGroup("sameSize")}
                                            sx={layoutCanvasOverlayIconSx()}
                                        >
                                            <AspectRatioIcon fontSize="small" />
                                        </MuiMaterials.IconButton>
                                    </span>
                                </MuiMaterials.Tooltip>
                                <MuiMaterials.Tooltip
                                    title={`Rotate ${GROUP_ROTATE_STEP_DEG}° counter-clockwise`}
                                    slotProps={layoutTheme.tooltipSlotProps}
                                >
                                    <span>
                                        <MuiMaterials.IconButton
                                            aria-label={`Rotate ${GROUP_ROTATE_STEP_DEG} degrees counter-clockwise`}
                                            size="small"
                                            disabled={
                                                toolbarLockedByPreview || !groupRotateEnabled
                                            }
                                            onClick={() => handleRotateGroup(-1)}
                                            sx={layoutCanvasOverlayIconSx()}
                                        >
                                            <RotateLeftIcon fontSize="small" />
                                        </MuiMaterials.IconButton>
                                    </span>
                                </MuiMaterials.Tooltip>
                                <MuiMaterials.Tooltip
                                    title={`Rotate ${GROUP_ROTATE_STEP_DEG}° clockwise`}
                                    slotProps={layoutTheme.tooltipSlotProps}
                                >
                                    <span>
                                        <MuiMaterials.IconButton
                                            aria-label={`Rotate ${GROUP_ROTATE_STEP_DEG} degrees clockwise`}
                                            size="small"
                                            disabled={
                                                toolbarLockedByPreview || !groupRotateEnabled
                                            }
                                            onClick={() => handleRotateGroup(1)}
                                            sx={layoutCanvasOverlayIconSx()}
                                        >
                                            <RotateRightIcon fontSize="small" />
                                        </MuiMaterials.IconButton>
                                    </span>
                                </MuiMaterials.Tooltip>
                                <MuiMaterials.Tooltip title="Delete selected" slotProps={layoutTheme.tooltipSlotProps}>
                                    <span>
                                        <MuiMaterials.IconButton
                                            aria-label="Delete selected"
                                            size="small"
                                            disabled={
                                                toolbarLockedByPreview || groupDeleteDisabled
                                            }
                                            onClick={handleDeleteSelected}
                                            sx={layoutCanvasOverlayIconSx()}
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </MuiMaterials.IconButton>
                                    </span>
                                </MuiMaterials.Tooltip>
                            </MuiMaterials.Box>
                        )}
                        <MuiMaterials.Box
                            sx={{
                                position: "absolute",
                                top: 8,
                                left: 8,
                                zIndex: 4,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 0.5,
                            }}
                        >
                            <MuiMaterials.Tooltip
                                title="Site Layout help (mouse, keyboard, toolbar, panel)"
                                placement="right"
                                slotProps={layoutTheme.tooltipSlotProps}
                            >
                                <MuiMaterials.IconButton
                                    aria-label="Site Layout help"
                                    size="small"
                                    disabled={toolbarLockedByPreview}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenSiteLayoutHelp();
                                    }}
                                    sx={layoutCanvasOverlayIconSx()}
                                >
                                    <HelpOutlineIcon fontSize="small" />
                                </MuiMaterials.IconButton>
                            </MuiMaterials.Tooltip>
                        </MuiMaterials.Box>
                        <MuiMaterials.Box
                            sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                zIndex: 4,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 0.5,
                            }}
                        >
                            <MuiMaterials.Tooltip
                                title={`${showGraphGrid ? "Hide" : "Show"} ${GRAPH_GRID_FEET} ft graph paper`}
                                placement="left"
                                slotProps={layoutTheme.tooltipSlotProps}
                            >
                                <MuiMaterials.IconButton
                                    aria-label={showGraphGrid ? "Hide graph grid" : "Show graph grid"}
                                    size="small"
                                    disabled={toolbarLockedByPreview}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowGraphGrid((v) => !v);
                                    }}
                                    sx={{
                                        ...layoutCanvasOverlayIconSx(),
                                        ...(!showGraphGrid && {
                                            opacity: 0.55,
                                            borderColor: getThemeColor("#ccc", "#6b5a30"),
                                            color: getThemeColor("#888", "#a08050"),
                                        }),
                                    }}
                                >
                                    <GridOnIcon fontSize="small" />
                                </MuiMaterials.IconButton>
                            </MuiMaterials.Tooltip>
                            {imageDataUrl && (
                                <MuiMaterials.Tooltip
                                    title={
                                        showUploadedImage
                                            ? "Blank canvas (hide uploaded image)"
                                            : "Show uploaded image"
                                    }
                                    placement="left"
                                    slotProps={layoutTheme.tooltipSlotProps}
                                >
                                    <MuiMaterials.IconButton
                                        aria-label={
                                            showUploadedImage
                                                ? "Switch to blank canvas"
                                                : "Show uploaded image"
                                        }
                                        size="small"
                                        disabled={toolbarLockedByPreview}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowUploadedImage((v) => !v);
                                        }}
                                        sx={{
                                            ...layoutCanvasOverlayIconSx(),
                                            ...(!showUploadedImage && {
                                                opacity: 0.55,
                                                borderColor: getThemeColor("#ccc", "#6b5a30"),
                                                color: getThemeColor("#888", "#a08050"),
                                            }),
                                        }}
                                    >
                                        {showUploadedImage ? (
                                            <CropFreeIcon fontSize="small" />
                                        ) : (
                                            <ImageOutlinedIcon fontSize="small" />
                                        )}
                                    </MuiMaterials.IconButton>
                                </MuiMaterials.Tooltip>
                            )}
                            <MuiMaterials.Tooltip
                                title="Center layout on the graph (bins, walls, and enclosed areas)"
                                placement="left"
                                slotProps={layoutTheme.tooltipSlotProps}
                            >
                                <span>
                                    <MuiMaterials.IconButton
                                        aria-label="Center layout on graph"
                                        size="small"
                                        disabled={
                                            toolbarLockedByPreview ||
                                            !canPreviewLayout
                                        }
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            centerBinsOnGraph();
                                        }}
                                        sx={layoutCanvasOverlayIconSx()}
                                    >
                                        <CenterFocusStrongIcon fontSize="small" />
                                    </MuiMaterials.IconButton>
                                </span>
                            </MuiMaterials.Tooltip>
                        </MuiMaterials.Box>
                        <MuiMaterials.Box
                            sx={{
                                position: "absolute",
                                bottom: 8,
                                right: 8,
                                zIndex: 4,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 0.5,
                            }}
                        >
                            <MuiMaterials.Tooltip
                                title="Zoom in"
                                placement="left"
                                slotProps={layoutTheme.tooltipSlotProps}
                            >
                                <span>
                                    <MuiMaterials.IconButton
                                        aria-label="Zoom in"
                                        size="small"
                                        disabled={
                                            toolbarLockedByPreview || viewZoomAtMax
                                        }
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            zoomViewportFromStep(1, null, null, {
                                                fromButton: true,
                                            });
                                        }}
                                        sx={layoutCanvasZoomButtonSx()}
                                    >
                                        <MuiMaterials.Typography
                                            component="span"
                                            aria-hidden
                                            sx={{
                                                fontSize: "1.125rem",
                                                fontWeight: 700,
                                                lineHeight: 1,
                                                fontFamily: "inherit",
                                            }}
                                        >
                                            +
                                        </MuiMaterials.Typography>
                                    </MuiMaterials.IconButton>
                                </span>
                            </MuiMaterials.Tooltip>
                            <MuiMaterials.Tooltip
                                title="Zoom out"
                                placement="left"
                                slotProps={layoutTheme.tooltipSlotProps}
                            >
                                <span>
                                    <MuiMaterials.IconButton
                                        aria-label="Zoom out"
                                        size="small"
                                        disabled={
                                            toolbarLockedByPreview || viewZoomAtMin
                                        }
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            zoomViewportFromStep(-1, null, null, {
                                                fromButton: true,
                                            });
                                        }}
                                        sx={layoutCanvasZoomButtonSx()}
                                    >
                                        <MuiMaterials.Typography
                                            component="span"
                                            aria-hidden
                                            sx={{
                                                fontSize: "1.125rem",
                                                fontWeight: 700,
                                                lineHeight: 1,
                                                fontFamily: "inherit",
                                            }}
                                        >
                                            −
                                        </MuiMaterials.Typography>
                                    </MuiMaterials.IconButton>
                                </span>
                            </MuiMaterials.Tooltip>
                        </MuiMaterials.Box>
                        <div
                            ref={canvasPanWrapperRef}
                            style={{ display: "inline-block" }}
                        >
                            <canvas
                                ref={canvasRef}
                                onPointerDown={handleCanvasPointerDown}
                                onMouseDown={handleCanvasMouseDownGuarded}
                                onPointerMove={handleLayoutSurfacePointerMove}
                                onMouseMove={handleLayoutSurfacePointerMove}
                                onPointerUp={handleCanvasPointerUp}
                                onPointerCancel={handleCanvasPointerUp}
                                onMouseUp={handleFlatResizePointerUp}
                                onContextMenu={handleCanvasContextMenu}
                                style={{
                                    cursor: spaceHeld
                                        ? "grab"
                                        : mode === MODES.WALL || mode === MODES.ARC
                                          ? wallArcCursor
                                          : "crosshair",
                                    display: "block",
                                    userSelect: "none",
                                    touchAction: "none",
                                }}
                            />
                        </div>
                    </div>
                    </>
                ) : (
                    <div
                        style={{
                            flex: 1,
                            minHeight: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: `1px solid ${getThemeColor("#888", "#444")}`,
                            background: getThemeColor("#fafafa", "#1a1a1a"),
                            color: getThemeColor("#555", LAYOUT_DARK_TEXT_MUTED),
                        }}
                    >
                        <MuiMaterials.Typography sx={{ color: "inherit" }}>
                            {!siteReady
                                ? "Load a site first (site setup dialog)."
                                : "Loading layout…"}
                        </MuiMaterials.Typography>
                    </div>
                )}

                {preview3dOpen && previewDraft && (
                    <SiteLayoutPreview3d
                        open
                        draft={previewDraft}
                        editorView={previewEditorView}
                        onClose={() => {
                            setPreview3dOpen(false);
                            setPreviewDraft(null);
                            setPreviewEditorView(null);
                        }}
                    />
                )}

                </div>

            <MuiMaterials.Box
                ref={sidebarRef}
                sx={{
                    width: narrowLayout ? "100%" : LAYOUT_SIDEBAR_WIDTH_PX,
                    maxWidth: "100%",
                    flex: narrowLayout ? "0 0 auto" : "0 0 auto",
                    flexShrink: 0,
                    alignSelf: "stretch",
                    maxHeight: narrowLayout ? "38vh" : "none",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    paddingBottom: 3,
                    boxSizing: "border-box",
                    color: getThemeColor("#333333", LAYOUT_DARK_TEXT),
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                    pointerEvents: toolbarLockedByPreview ? "none" : "auto",
                    opacity: toolbarLockedByPreview ? 0.65 : 1,
                }}
            >
                <MuiMaterials.Stack spacing={1} sx={{ p: 1, border: layoutTheme.panelBorder, borderRadius: 1 }}>
                    {layoutAssignmentRows.map((row) => {
                        const isFace = row.kind === "face";
                        const face = row.face;
                        const m = row.marker;
                        const assignedId = isFace
                            ? faceAssignments[face.id]
                            : m.binDataId;
                        const hasBinAssignment = assignedId != null;
                        const flatRectId = isFace
                            ? closedRectIdForFace(face, layoutSegments)
                            : null;
                        const defaultAreaName = isFace
                            ? (flatRectId && faceAreaNamesByRectId[flatRectId]) ||
                              row.areaLabel ||
                              ""
                            : (markerAreaNames[m.id] ?? row.areaLabel ?? "");
                        const customNameValue = isFace
                            ? (faceLabels[face.id] ?? defaultAreaName)
                            : (markerLabels[m.id] ?? defaultAreaName);
                        const assignedBin = hasBinAssignment
                            ? siteBins.find((b) =>
                                  sameBinDataId(b.binDataId, assignedId)
                              )
                            : null;
                        const selectLabel = assignedBin?.binName
                            ? String(assignedBin.binName)
                            : defaultAreaName;
                        const isSelected = isFace
                            ? (() => {
                                  const flatRid = closedRectIdForFace(
                                      face,
                                      layoutSegments
                                  );
                                  return (
                                      face.id === selectedFaceId ||
                                      (flatRid &&
                                          (flatRid === groupAnchorFlatRectId ||
                                              selectedFaceId ===
                                                  `flat:${flatRid}`))
                                  );
                              })()
                            : m.id === selectedMarkerId;
                        const isGroupSelected = !isFace && selectedMarkerIds.includes(m.id);

                        const assignSelectLabelId = `${row.labelId}-assign-label`;
                        const assignSelectId = `${row.labelId}-assign-select`;

                        return (
                            <MuiMaterials.Stack
                                key={row.key}
                                id={row.labelId}
                                direction="row"
                                spacing={1}
                                alignItems="flex-start"
                                onClick={(e) => {
                                    if (
                                        e.target instanceof Element &&
                                        e.target.closest(
                                            ".MuiTextField-root, .MuiSelect-root, .MuiButton-root, .MuiIconButton-root"
                                        )
                                    ) {
                                        return;
                                    }
                                    if (isFace) {
                                        const rectId = closedRectIdForFace(
                                            face,
                                            layoutSegments
                                        );
                                        if (rectId) {
                                            selectFlatRect(rectId);
                                        } else {
                                            selectEnclosedFaceById(face.id);
                                        }
                                    } else if (isMultiSelectKey(e)) {
                                        setSelectedMarkerIds((prev) => {
                                            const has = prev.includes(m.id);
                                            return has
                                                ? prev.filter((id) => id !== m.id)
                                                : [...prev, m.id];
                                        });
                                        setSelectedMarkerId(m.id);
                                        setGroupAnchorMarkerId(m.id);
                                        setGroupAnchorFlatRectId(null);
                                        setSelectedSegmentIds([]);
                                        setSelectedSegmentId(null);
                                        setSelectedFaceId(null);
                                    } else {
                                        selectBinMarkerById(m.id);
                                    }
                                }}
                                sx={{
                                    p: 0.5,
                                    borderRadius: 1,
                                    cursor: "pointer",
                                    backgroundColor: isSelected || isGroupSelected
                                        ? getThemeColor("rgba(255,235,59,0.15)", "rgba(255,235,59,0.12)")
                                        : assignedId
                                            ? getThemeColor("rgba(76,175,80,0.1)", "rgba(76,175,80,0.15)")
                                            : "transparent",
                                    outline: isSelected || isGroupSelected
                                        ? `1px solid ${getThemeColor("#fbc02d", "#ffeb3b")}`
                                        : "none",
                                }}
                            >
                                <MuiMaterials.Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
                                    {!hasBinAssignment && (
                                        <MuiMaterials.TextField
                                            size="small"
                                            fullWidth
                                            label="Name"
                                            value={customNameValue}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onClick={(e) => e.stopPropagation()}
                                            onFocus={(e) => e.stopPropagation()}
                                            onKeyDown={(e) => e.stopPropagation()}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                const trimmed = v.trim();
                                                const isDefault =
                                                    trimmed === defaultAreaName ||
                                                    trimmed === "";
                                                if (isFace) {
                                                    updateFaceLabel(
                                                        face.id,
                                                        isDefault ? "" : v
                                                    );
                                                } else {
                                                    updateMarkerLabel(
                                                        m.id,
                                                        isDefault ? "" : v
                                                    );
                                                }
                                            }}
                                            sx={layoutTheme.formControlSx}
                                            inputProps={{ sx: layoutTheme.inputLabelSx }}
                                        />
                                    )}
                                    <MuiMaterials.Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="flex-start"
                                        sx={{ width: "100%" }}
                                    >
                                        {isFace && (
                                            <>
                                                <MuiMaterials.TextField
                                                    size="small"
                                                    type="number"
                                                    label="W (ft)"
                                                    value={
                                                        faceDimensionDrafts[face.id]?.w ??
                                                        faceWidthLengthFtStrings(
                                                            face,
                                                            layoutSegments,
                                                            layoutDiameterFpp,
                                                            siteBins,
                                                            assignedId
                                                        ).w
                                                    }
                                                    disabled={layoutDiameterFpp <= 0}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onFocus={() =>
                                                        setEditingFaceDimensionsId(face.id)
                                                    }
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        const v = e.target.value;
                                                        setFaceDimensionDrafts((d) => ({
                                                            ...d,
                                                            [face.id]: {
                                                                w: v,
                                                                l:
                                                                    d[face.id]?.l ??
                                                                    faceWidthLengthFtStrings(
                                                                        face,
                                                                        layoutSegments,
                                                                        layoutDiameterFpp,
                                                                        siteBins,
                                                                        assignedId
                                                                    ).l,
                                                            },
                                                        }));
                                                    }}
                                                    onBlur={() => {
                                                        const draft =
                                                            faceDimensionDrafts[face.id];
                                                        const base = faceWidthLengthFtStrings(
                                                            face,
                                                            layoutSegments,
                                                            layoutDiameterFpp,
                                                            siteBins,
                                                            assignedId
                                                        );
                                                        applyFaceDimensionsFt(
                                                            face.id,
                                                            draft?.w ?? base.w,
                                                            draft?.l ?? base.l
                                                        );
                                                        setEditingFaceDimensionsId(null);
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            const draft =
                                                                faceDimensionDrafts[face.id];
                                                            const base =
                                                                faceWidthLengthFtStrings(
                                                                    face,
                                                                    layoutSegments,
                                                                    layoutDiameterFpp,
                                                                    siteBins,
                                                                    assignedId
                                                                );
                                                            applyFaceDimensionsFt(
                                                                face.id,
                                                                draft?.w ?? base.w,
                                                                draft?.l ?? base.l
                                                            );
                                                            setEditingFaceDimensionsId(null);
                                                            e.currentTarget.blur();
                                                        }
                                                    }}
                                                    inputProps={{ min: 1, step: 0.5 }}
                                                    sx={{
                                                        ...layoutTheme.formControlSx,
                                                        width: 72,
                                                        flexShrink: 0,
                                                    }}
                                                    InputLabelProps={{
                                                        sx: layoutTheme.inputLabelSx,
                                                    }}
                                                />
                                                <MuiMaterials.TextField
                                                    size="small"
                                                    type="number"
                                                    label="L (ft)"
                                                    value={
                                                        faceDimensionDrafts[face.id]?.l ??
                                                        faceWidthLengthFtStrings(
                                                            face,
                                                            layoutSegments,
                                                            layoutDiameterFpp,
                                                            siteBins,
                                                            assignedId
                                                        ).l
                                                    }
                                                    disabled={layoutDiameterFpp <= 0}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onFocus={() =>
                                                        setEditingFaceDimensionsId(face.id)
                                                    }
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        const v = e.target.value;
                                                        setFaceDimensionDrafts((d) => ({
                                                            ...d,
                                                            [face.id]: {
                                                                l: v,
                                                                w:
                                                                    d[face.id]?.w ??
                                                                    faceWidthLengthFtStrings(
                                                                        face,
                                                                        layoutSegments,
                                                                        layoutDiameterFpp,
                                                                        siteBins,
                                                                        assignedId
                                                                    ).w,
                                                            },
                                                        }));
                                                    }}
                                                    onBlur={() => {
                                                        const draft =
                                                            faceDimensionDrafts[face.id];
                                                        const base = faceWidthLengthFtStrings(
                                                            face,
                                                            layoutSegments,
                                                            layoutDiameterFpp,
                                                            siteBins,
                                                            assignedId
                                                        );
                                                        applyFaceDimensionsFt(
                                                            face.id,
                                                            draft?.w ?? base.w,
                                                            draft?.l ?? base.l
                                                        );
                                                        setEditingFaceDimensionsId(null);
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            const draft =
                                                                faceDimensionDrafts[face.id];
                                                            const base =
                                                                faceWidthLengthFtStrings(
                                                                    face,
                                                                    layoutSegments,
                                                                    layoutDiameterFpp,
                                                                    siteBins,
                                                                    assignedId
                                                                );
                                                            applyFaceDimensionsFt(
                                                                face.id,
                                                                draft?.w ?? base.w,
                                                                draft?.l ?? base.l
                                                            );
                                                            setEditingFaceDimensionsId(null);
                                                            e.currentTarget.blur();
                                                        }
                                                    }}
                                                    inputProps={{ min: 1, step: 0.5 }}
                                                    sx={{
                                                        ...layoutTheme.formControlSx,
                                                        width: 72,
                                                        flexShrink: 0,
                                                    }}
                                                    InputLabelProps={{
                                                        sx: layoutTheme.inputLabelSx,
                                                    }}
                                                />
                                            </>
                                        )}
                                        {!isFace && (
                                            <MuiMaterials.TextField
                                                size="small"
                                                type="number"
                                                label="Dia (ft)"
                                                value={
                                                    diameterDrafts[m.id] ??
                                                    markerDiameterFtString(
                                                        m,
                                                        layoutDiameterFpp,
                                                        siteBins
                                                    )
                                                }
                                                disabled={layoutDiameterFpp <= 0}
                                                onClick={(e) => e.stopPropagation()}
                                                onFocus={() =>
                                                    setEditingDiameterMarkerId(m.id)
                                                }
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    const v = e.target.value;
                                                    setDiameterDrafts((d) => ({
                                                        ...d,
                                                        [m.id]: v,
                                                    }));
                                                }}
                                                onBlur={() => {
                                                    applyMarkerDiameterFt(
                                                        m.id,
                                                        diameterDrafts[m.id] ??
                                                            markerDiameterFtString(
                                                                m,
                                                                layoutDiameterFpp,
                                                                siteBins
                                                            )
                                                    );
                                                    setEditingDiameterMarkerId(null);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        applyMarkerDiameterFt(
                                                            m.id,
                                                            diameterDrafts[m.id] ??
                                                                markerDiameterFtString(
                                                                    m,
                                                                    layoutDiameterFpp,
                                                                    siteBins
                                                                )
                                                        );
                                                        setEditingDiameterMarkerId(
                                                            null
                                                        );
                                                        e.currentTarget.blur();
                                                    }
                                                }}
                                                inputProps={{ min: 1, step: 0.5 }}
                                                sx={{
                                                    ...layoutTheme.formControlSx,
                                                    width: 88,
                                                    flexShrink: 0,
                                                }}
                                                InputLabelProps={{
                                                    sx: layoutTheme.inputLabelSx,
                                                }}
                                            />
                                        )}
                                        <MuiMaterials.FormControl
                                            size="small"
                                            sx={{
                                                ...layoutTheme.formControlSx,
                                                flex: 1,
                                                minWidth: 0,
                                            }}
                                        >
                                        <MuiMaterials.InputLabel
                                            id={assignSelectLabelId}
                                            shrink
                                            sx={layoutTheme.inputLabelSx}
                                        >
                                            {selectLabel}
                                        </MuiMaterials.InputLabel>
                                        <MuiMaterials.Select
                                            id={assignSelectId}
                                            labelId={assignSelectLabelId}
                                            label={selectLabel}
                                            value={binSelectValue(assignedId)}
                                            displayEmpty
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                const binId =
                                                    v === "" ? null : parseInt(v, 10);
                                                if (isFace) {
                                                    updateFaceAssignment(face.id, binId);
                                                    if (binId != null) {
                                                        updateFaceLabel(face.id, "");
                                                    }
                                                } else {
                                                    const fpp = resolveFeetPerPixel(
                                                        markers,
                                                        siteBins,
                                                        feetPerPixel
                                                    );
                                                    const patch = { binDataId: binId };
                                                    if (binId != null && fpp > 0) {
                                                        const cur = markers.find(
                                                            (mk) => mk.id === m.id
                                                        );
                                                        const bin = siteBins.find((b) =>
                                                            sameBinDataId(
                                                                b.binDataId,
                                                                binId
                                                            )
                                                        );
                                                        const dFt =
                                                            getBinRecordDiameterFt(bin);
                                                        if (
                                                            dFt > 0 &&
                                                            !(cur?.radiusPx > 4)
                                                        ) {
                                                            const rPx = diameterFtToRadiusPx(
                                                                dFt,
                                                                fpp
                                                            );
                                                            if (rPx != null) {
                                                                patch.radiusPx = rPx;
                                                            }
                                                        }
                                                    }
                                                    updateMarker(m.id, patch);
                                                    if (binId != null) {
                                                        updateMarkerLabel(m.id, "");
                                                    }
                                                }
                                            }}
                                            onOpen={() => {
                                                if (isFace) {
                                                    selectEnclosedFaceById(face.id, {
                                                        scrollPanel: false,
                                                    });
                                                } else {
                                                    selectBinMarkerById(m.id, {
                                                        scrollPanel: false,
                                                    });
                                                }
                                            }}
                                            sx={layoutTheme.selectSx}
                                            MenuProps={layoutTheme.menuProps}
                                        >
                                            <MuiMaterials.MenuItem value="">
                                                <em>Not in bin list</em>
                                            </MuiMaterials.MenuItem>
                                            {siteBins.map((b) => (
                                                <MuiMaterials.MenuItem
                                                    key={`${row.key}_bin_${b.binDataId}`}
                                                    value={String(b.binDataId)}
                                                    disabled={isBinAssignedElsewhere(
                                                        b.binDataId,
                                                        {
                                                            excludeFaceId: isFace
                                                                ? face.id
                                                                : null,
                                                            excludeMarkerId: isFace
                                                                ? null
                                                                : m.id,
                                                        }
                                                    )}
                                                >
                                                    {b.binName}
                                                </MuiMaterials.MenuItem>
                                            ))}
                                        </MuiMaterials.Select>
                                    </MuiMaterials.FormControl>
                                    </MuiMaterials.Stack>
                                </MuiMaterials.Stack>
                                {!isFace && (
                                    <MuiMaterials.Button
                                        size="small"
                                        variant="text"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveMarker(m.id);
                                        }}
                                        sx={{ minWidth: 32, mt: 0.5, ...layoutBtnSx(false, "text") }}
                                    >
                                        ×
                                    </MuiMaterials.Button>
                                )}
                            </MuiMaterials.Stack>
                        );
                    })}
                </MuiMaterials.Stack>

            </MuiMaterials.Box>
                </div>
            </div>
        </div>
    );
};

/** Numeric-vs-string-safe BinId equality (matches BinStice.js sameBinDataId). */
function sameBinDataId(a, b) {
    if (a === undefined || a === null || b === undefined || b === null) return false;
    const na = parseInt(String(a), 10);
    const nb = parseInt(String(b), 10);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na === nb;
    return String(a) === String(b);
}

/** Administrators only — non-admins are sent back to Sites. */
const SiteLayoutGate = () => {
    const navigate = useNavigate();
    const isAdmin = global.isAdminUser(global.gw.email);

    useEffect(() => {
        if (!isAdmin) {
            navigate("/sites", { replace: true });
        }
    }, [isAdmin, navigate]);

    if (!isAdmin) return null;
    return <SiteLayout />;
};

export default SiteLayoutGate;
